import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AppLayout } from '../components/AppLayout';
import { SECTION_DEFS, roleDefaultAccess } from '../data/sectionAccess';
import { resolveTabVisible, tabBaseDefault, CROQUIS_TABS } from '../utils/tabAccess';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageHeader } from '../components/ds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Calculator, 
  TrendingUp, 
  Wallet,
  Clock,
  BarChart3,
  Loader2,
  LayoutGrid,
  Settings,
  Save,
  RotateCcw,
  RefreshCw,
  Pencil,
  X,
  Check,
  Upload,
  Download,
  FileSpreadsheet,
  History,
  Trash2,
  LogIn,
  Shield,
  UserCog,
  UserPlus,
  Mail,
  Send,
  AlertCircle,
  CheckCircle,
  XCircle,
  RotateCw,
  Building2,
  Plus,
  Key,
  Lock,
  Eye,
  FileEdit,
  ChartBar,
  FileText,
  FileSignature,
  HardHat,
  Tag,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Calendar,
  Search,
  ShieldCheck,
  CalendarDays,
  Sparkles, BellOff
} from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { SettingsDialog as PlanningSettingsDialog } from './PlanningChantiersPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Login Day Group Component (expandable row per date)
import { LoginDayGroup, DiagnosticPanel, EmailPausePanel, PlanningChantiersAdminTab } from '../components/admin/AdminPanels';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function AdminPage() {
  const { api, isAdmin, user, token } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [clusterHistory, setClusterHistory] = useState([]);
  const [laborRates, setLaborRates] = useState([]);
  const [editingLaborRate, setEditingLaborRate] = useState(null);
  const [savingLaborRate, setSavingLaborRate] = useState(false);
  const [caPerEmployee, setCaPerEmployee] = useState(120000);
  const [caPerEmployeeInput, setCaPerEmployeeInput] = useState('120000');
  const [savingCaPerEmployee, setSavingCaPerEmployee] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCluster, setEditingCluster] = useState(null);
  const [savingCluster, setSavingCluster] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [loadingLoginHistory, setLoadingLoginHistory] = useState(false);
  const [selectedUserHistory, setSelectedUserHistory] = useState(null);
  const [loginHistorySearch, setLoginHistorySearch] = useState('');
  const [connSearch, setConnSearch] = useState('');
  const [connSort, setConnSort] = useState('last_login');
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [newRecipient, setNewRecipient] = useState({ email: '', name: '', role: 'admin' });
  // HG Recipients
  const [hgRecipients, setHgRecipients] = useState([]);
  const [loadingHgRecipients, setLoadingHgRecipients] = useState(false);
  const [newHgRecipient, setNewHgRecipient] = useState({ email: '', name: '' });
  // Contract Recipients
  const [contractRecipients, setContractRecipients] = useState([]);
  const [loadingContractRecipients, setLoadingContractRecipients] = useState(false);
  const [newContractRecipient, setNewContractRecipient] = useState({ email: '', name: '' });
  const [chantierRecipients, setChantierRecipients] = useState([]);
  const [loadingChantierRecipients, setLoadingChantierRecipients] = useState(false);
  const [newChantierRecipient, setNewChantierRecipient] = useState({ email: '', name: '' });
  const [chantierIncludedAdmins, setChantierIncludedAdmins] = useState([]);
  const [togglingAdminId, setTogglingAdminId] = useState(null);
  const [bulkStatus, setBulkStatus] = useState('all');
  const [bulkExporting, setBulkExporting] = useState(null);
  const [caCornerRoles, setCaCornerRoles] = useState({ admin: true, super_admin: true, commercial: true });
  const [savingCaCornerAccess, setSavingCaCornerAccess] = useState(false);
  const [caCornerUsers, setCaCornerUsers] = useState([]);
  const [caCornerUserAccess, setCaCornerUserAccess] = useState({});
  const [caCornerUserSearch, setCaCornerUserSearch] = useState('');
  const [loadingCaCornerUsers, setLoadingCaCornerUsers] = useState(false);
  const [caCornerAlertConfig, setCaCornerAlertConfig] = useState({ recipients: [], threshold_pct: -15, min_months: 3, quality_threshold_pct: 90, renewal_reminder_months: 6, eligible_users: [] });
  const [savingCaCornerAlert, setSavingCaCornerAlert] = useState(false);
  const [caCornerAlertSearch, setCaCornerAlertSearch] = useState('');
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [roleAccessOpen, setRoleAccessOpen] = useState(false);
  const [roleAccessConfig, setRoleAccessConfig] = useState({});
  const [roleAccessSelected, setRoleAccessSelected] = useState('commercial');
  const [savingRoleAccess, setSavingRoleAccess] = useState(false);
  // Accès fin aux sous-onglets Admin (par rôle + surcharge par utilisateur)
  const [ataConfig, setAtaConfig] = useState({ role_config: {}, user_config: {} });
  const [ataSelectedRole, setAtaSelectedRole] = useState('admin');
  const [ataSelectedUserId, setAtaSelectedUserId] = useState('');
  const [savingAta, setSavingAta] = useState(false);
  const [adminTab, setAdminTab] = useState('users');
  const [adminCat, setAdminCat] = useState('users_access');
  const [editProfileUser, setEditProfileUser] = useState(null);
  const [editProfileForm, setEditProfileForm] = useState({ name: '', email: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [recalculatingAll, setRecalculatingAll] = useState(false);
  const [approvingUser, setApprovingUser] = useState(null);
  // Email resend states
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [resendingBulk, setResendingBulk] = useState(false);
  // Permissions states
  const [permissions, setPermissions] = useState({});
  const [permissionLabels, setPermissionLabels] = useState({});
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedPermRole, setSelectedPermRole] = useState('commercial');
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState({});
  const fileInputRef = React.useRef(null);
  
  // Email templates states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // User visibility configuration states
  const [configuringVisibility, setConfiguringVisibility] = useState(null); // User being configured
  const [allUsersList, setAllUsersList] = useState([]); // List of all users for selection
  const [visibilityScope, setVisibilityScope] = useState('own');
  const [selectedVisibleUsers, setSelectedVisibleUsers] = useState([]);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [loadingVisibility, setLoadingVisibility] = useState(false);
  
  // Organisations states
  const [organisations, setOrganisations] = useState([]);
  const [slaSettings, setSlaSettings] = useState({ sla_delay_hours: 72, relance1_hours: 72, relance2_hours: 120 });
  const [slaSaving, setSlaSaving] = useState(false);

  // Agenceurs config states
  const [agenceursConfig, setAgenceursConfig] = useState([]);
  const [loadingAgenceurs, setLoadingAgenceurs] = useState(false);
  const [newAgenceur, setNewAgenceur] = useState({ name: '', email: '' });
  const [editingAgenceur, setEditingAgenceur] = useState(null);
  const [savingAgenceur, setSavingAgenceur] = useState(false);
  const [deletingAgenceurId, setDeletingAgenceurId] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [newOrgForm, setNewOrgForm] = useState({ nom: '', description: '', contact_email: '', contact_phone: '' });
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(null);
  const [assigningOrgUser, setAssigningOrgUser] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  // Create user modal states
  const [createUserModal, setCreateUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'prestataire', organisation_id: '' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Checklist items management states
  const [checklistItems, setChecklistItems] = useState([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState({ label: '', options: 'Oui,Non,Partiellement', comment_on: 'Non,Partiellement', category: 'Pose & Installation' });
  const [editingChecklistItem, setEditingChecklistItem] = useState(null);
  const [savingChecklistItem, setSavingChecklistItem] = useState(false);
  const [checklistCategories, setChecklistCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');

  // Upload categories for Annonce ALL
  const [uploadCategories, setUploadCategories] = useState([]);
  const [loadingUploadCats, setLoadingUploadCats] = useState(false);
  const [newUploadCatLabel, setNewUploadCatLabel] = useState('');
  const [editingUploadCat, setEditingUploadCat] = useState(null);

  // Email routing config
  const [emailRouting, setEmailRouting] = useState([]);
  const [loadingRouting, setLoadingRouting] = useState(false);
  const [routingHistory, setRoutingHistory] = useState([]);
  const [showRoutingHistory, setShowRoutingHistory] = useState(false);
  const [loadingRoutingHistory, setLoadingRoutingHistory] = useState(false);

  // Prerequis access control
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [loadingPrerequisAccess, setLoadingPrerequisAccess] = useState(false);
  const [savingPrerequisAccess, setSavingPrerequisAccess] = useState(false);
  const [prerequisAccessSearch, setPrerequisAccessSearch] = useState('');

  // Capex Sales access control (blocage d'exceptions)
  const [csAllowedUserIds, setCsAllowedUserIds] = useState([]);
  const [csAlwaysAllowedIds, setCsAlwaysAllowedIds] = useState([]);
  const [loadingCsAccess, setLoadingCsAccess] = useState(false);
  const [savingCsAccess, setSavingCsAccess] = useState(false);
  const [csAccessSearch, setCsAccessSearch] = useState('');

  // Capex Sales — import opportunités (back-office)
  const [csOppFile, setCsOppFile] = useState(null);
  const [csOppUploading, setCsOppUploading] = useState(false);
  const [csOppResult, setCsOppResult] = useState(null);
  const [csImportLog, setCsImportLog] = useState([]);
  const [csContactFile, setCsContactFile] = useState(null);
  const [csContactUploading, setCsContactUploading] = useState(false);
  const [csContactResult, setCsContactResult] = useState(null);
  const [csDedupRunning, setCsDedupRunning] = useState(false);
  const [csDedupResult, setCsDedupResult] = useState(null);
  const [csCrFile, setCsCrFile] = useState(null);
  const [csCrUploading, setCsCrUploading] = useState(false);
  const [csCrResult, setCsCrResult] = useState(null);

  // Corner access control
  const [cornerAllowedIds, setCornerAllowedIds] = useState([]);
  const [loadingCornerAccess, setLoadingCornerAccess] = useState(false);
  const [savingCornerAccess, setSavingCornerAccess] = useState(false);
  const [cornerAccessSearch, setCornerAccessSearch] = useState('');

  // Prerequis recipients
  const [prerequisRecipients, setPrerequisRecipients] = useState([]);
  const [loadingPrerequisRecipients, setLoadingPrerequisRecipients] = useState(false);
  const [newPrerequisRecipient, setNewPrerequisRecipient] = useState({ email: '', name: '' });
  const [prereqUserOptions, setPrereqUserOptions] = useState([]);

  // PV relance config
  const [pvRelanceCfg, setPvRelanceCfg] = useState({ enabled: true, weekday: 0, hour: 9, recipients: [] });
  const [loadingPvCfg, setLoadingPvCfg] = useState(false);
  const [savingPvCfg, setSavingPvCfg] = useState(false);
  const [newPvRecipient, setNewPvRecipient] = useState('');

  // Direction report config
  const [dirCfg, setDirCfg] = useState({ enabled: true, day_of_month: 1, hour: 8, recipients: [] });
  const [loadingDirCfg, setLoadingDirCfg] = useState(false);
  const [savingDirCfg, setSavingDirCfg] = useState(false);
  const [bizReminderCfg, setBizReminderCfg] = useState({ enabled: true, day: 0, hour: 9 });
  const [savingBizReminderCfg, setSavingBizReminderCfg] = useState(false);
  const [newDirRecipient, setNewDirRecipient] = useState('');

  // Primes commercial emails
  const [primesCommerciaux, setPrimesCommerciaux] = useState([]);
  const [primesEmailsMap, setPrimesEmailsMap] = useState({});
  const [loadingPrimesEmails, setLoadingPrimesEmails] = useState(false);
  const [savingPrimesEmails, setSavingPrimesEmails] = useState(false);

  // Prerequis checklist items
  const [prereqCheckItems, setPrereqCheckItems] = useState([]);
  const [loadingPrereqCheck, setLoadingPrereqCheck] = useState(false);
  const [prereqCheckCategories, setPrereqCheckCategories] = useState([]);
  const [newPrereqCheckItem, setNewPrereqCheckItem] = useState({ label: '', response_type: 'radio', options: 'Conforme,Non conforme,À vérifier', comment_on: 'Non conforme,À vérifier', category: 'Autre', required: false });
  const [editingPrereqCheckItem, setEditingPrereqCheckItem] = useState(null);
  const [savingPrereqCheckItem, setSavingPrereqCheckItem] = useState(false);
  const [prereqCheckFilterCat, setPrereqCheckFilterCat] = useState('');

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!isAdmin) return;

    const fetchData = async () => {
      try {
        const [statsRes, usersRes, clustersRes, laborRatesRes, laborConfigRes] = await Promise.all([
          api().get('/admin/stats'),
          api().get('/admin/users/activity'),  // Use new endpoint with activity
          api().get('/admin/clusters'),
          api().get('/admin/labor-rates'),
          api().get('/config/labor'),
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
        setClusters(clustersRes.data);
        setLaborRates(laborRatesRes.data);
        if (laborConfigRes.data?.ca_per_employee) {
          setCaPerEmployee(laborConfigRes.data.ca_per_employee);
          setCaPerEmployeeInput(String(laborConfigRes.data.ca_per_employee));
        }
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        toast.error(t('error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Charge la config d'accès fin aux sous-onglets Admin (pour appliquer la visibilité au rôle admin)
    api().get('/admin-tab-access')
      .then(res => setAtaConfig({ role_config: res.data?.role_config || {}, user_config: res.data?.user_config || {} }))
      .catch(() => {});
  }, [api, t, isAdmin]);

  // Fetch organisations
  const fetchOrganisations = async () => {
    setLoadingOrgs(true);
    try {
      const res = await api().get('/organisations');
      setOrganisations(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingOrgs(false); }
  };

  // SLA Agenceur settings
  const fetchSlaSettings = async () => {
    try {
      const res = await api().get('/dossiers-agenceur/sla-settings');
      setSlaSettings(res.data);
    } catch (e) { console.error(e); }
  };
  const handleSaveSla = async () => {
    setSlaSaving(true);
    try {
      await api().put('/dossiers-agenceur/sla-settings', slaSettings);
      toast.success('Paramètres SLA mis à jour');
    } catch (e) { toast.error('Erreur'); }
    finally { setSlaSaving(false); }
  };

  // Agenceurs config CRUD
  const handleCreateUser = async () => {
    if (!newUserForm.name.trim() || !newUserForm.email.trim() || !newUserForm.password.trim()) {
      toast.error('Nom, email et mot de passe sont obligatoires');
      return;
    }
    setCreatingUser(true);
    try {
      const payload = { ...newUserForm };
      if (!payload.organisation_id) delete payload.organisation_id;
      await api().post('/admin/create-user', payload);
      toast.success(`Utilisateur ${newUserForm.name} créé avec succès`);
      setCreateUserModal(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'prestataire', organisation_id: '' });
      // Refresh users list
      const usersRes = await api().get('/admin/users/activity');
      setUsers(usersRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setCreatingUser(false); }
  };

  const fetchAgenceursConfig = async () => {
    setLoadingAgenceurs(true);
    try {
      const res = await api().get('/agenceurs-config');
      setAgenceursConfig(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingAgenceurs(false); }
  };

  const handleAddAgenceur = async () => {
    if (!newAgenceur.name.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSavingAgenceur(true);
    try {
      await api().post('/agenceurs-config', newAgenceur);
      toast.success('Prestataire ajouté');
      setNewAgenceur({ name: '', email: '' });
      fetchAgenceursConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingAgenceur(false); }
  };

  const handleUpdateAgenceur = async () => {
    if (!editingAgenceur) return;
    setSavingAgenceur(true);
    try {
      await api().put(`/agenceurs-config/${editingAgenceur.id}`, { name: editingAgenceur.name, email: editingAgenceur.email });
      toast.success('Prestataire mis à jour');
      setEditingAgenceur(null);
      fetchAgenceursConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingAgenceur(false); }
  };

  const handleDeleteAgenceur = async (agenceurId) => {
    setDeletingAgenceurId(agenceurId);
    try {
      await api().delete(`/agenceurs-config/${agenceurId}`);
      toast.success('Prestataire supprimé');
      fetchAgenceursConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setDeletingAgenceurId(null); }
  };

  // ---- Checklist Items CRUD ----
  const fetchChecklistItems = async () => {
    setLoadingChecklist(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        api().get('/chantier-checklist-items'),
        api().get('/chantier-checklist-categories'),
      ]);
      setChecklistItems(itemsRes.data);
      setChecklistCategories(catsRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingChecklist(false); }
  };

  const fetchUploadCategories = async () => {
    setLoadingUploadCats(true);
    try {
      const res = await api().get('/announcement-upload-categories');
      setUploadCategories(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingUploadCats(false); }
  };

  const handleAddUploadCat = async () => {
    if (!newUploadCatLabel.trim()) { toast.error('Le libellé est obligatoire'); return; }
    try {
      await api().post('/announcement-upload-categories', { label: newUploadCatLabel.trim() });
      toast.success('Catégorie ajoutée');
      setNewUploadCatLabel('');
      fetchUploadCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateUploadCat = async (cat) => {
    try {
      await api().put(`/announcement-upload-categories/${cat.id}`, { label: cat.label, active: cat.active });
      toast.success('Catégorie mise à jour');
      setEditingUploadCat(null);
      fetchUploadCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteUploadCat = async (catId) => {
    try {
      await api().delete(`/announcement-upload-categories/${catId}`);
      toast.success('Catégorie supprimée');
      fetchUploadCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Prerequis checklist CRUD
  const fetchPrereqCheckItems = async () => {
    setLoadingPrereqCheck(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        api().get('/prerequis-checklist-items'),
        api().get('/prerequis-checklist-categories'),
      ]);
      setPrereqCheckItems(itemsRes.data);
      setPrereqCheckCategories(catsRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingPrereqCheck(false); }
  };

  const handleAddPrereqCheckItem = async () => {
    if (!newPrereqCheckItem.label.trim()) { toast.error('Le libellé est obligatoire'); return; }
    setSavingPrereqCheckItem(true);
    try {
      const payload = {
        label: newPrereqCheckItem.label,
        response_type: newPrereqCheckItem.response_type,
        options: newPrereqCheckItem.response_type === 'text' || newPrereqCheckItem.response_type === 'number'
          ? [] : newPrereqCheckItem.options.split(',').map(s => s.trim()).filter(Boolean),
        comment_on: newPrereqCheckItem.comment_on.split(',').map(s => s.trim()).filter(Boolean),
        category: newPrereqCheckItem.category || 'Autre',
        required: newPrereqCheckItem.required,
      };
      await api().post('/prerequis-checklist-items', payload);
      toast.success('Point ajouté');
      setNewPrereqCheckItem({ label: '', response_type: 'radio', options: 'Conforme,Non conforme,À vérifier', comment_on: 'Non conforme,À vérifier', category: newPrereqCheckItem.category, required: false });
      fetchPrereqCheckItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingPrereqCheckItem(false); }
  };

  const handleUpdatePrereqCheckItem = async () => {
    if (!editingPrereqCheckItem) return;
    setSavingPrereqCheckItem(true);
    try {
      const payload = {
        label: editingPrereqCheckItem.label,
        response_type: editingPrereqCheckItem.response_type,
        options: editingPrereqCheckItem.response_type === 'text' || editingPrereqCheckItem.response_type === 'number'
          ? [] : (typeof editingPrereqCheckItem.options === 'string'
            ? editingPrereqCheckItem.options.split(',').map(s => s.trim()).filter(Boolean)
            : editingPrereqCheckItem.options),
        comment_on: typeof editingPrereqCheckItem.comment_on === 'string'
          ? editingPrereqCheckItem.comment_on.split(',').map(s => s.trim()).filter(Boolean)
          : editingPrereqCheckItem.comment_on,
        active: editingPrereqCheckItem.active,
        category: editingPrereqCheckItem.category || 'Autre',
        required: editingPrereqCheckItem.required,
      };
      await api().put(`/prerequis-checklist-items/${editingPrereqCheckItem.id}`, payload);
      toast.success('Point mis à jour');
      setEditingPrereqCheckItem(null);
      fetchPrereqCheckItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingPrereqCheckItem(false); }
  };

  const handleDeletePrereqCheckItem = async (itemId) => {
    try {
      await api().delete(`/prerequis-checklist-items/${itemId}`);
      toast.success('Point supprimé');
      fetchPrereqCheckItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleTogglePrereqCheckItem = async (item) => {
    try {
      await api().put(`/prerequis-checklist-items/${item.id}`, { active: !item.active });
      fetchPrereqCheckItems();
    } catch (e) { toast.error('Erreur'); }
  };

  // Prerequis recipients CRUD
  const fetchPrerequisRecipients = async () => {
    if (loadingPrerequisRecipients) return;
    setLoadingPrerequisRecipients(true);
    try {
      const [res, ul] = await Promise.all([api().get('/admin/prerequis-recipients'), api().get('/users/list').catch(() => ({ data: [] }))]);
      setPrerequisRecipients(res.data);
      setPrereqUserOptions(ul.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingPrerequisRecipients(false); }
  };

  const handleAddPrerequisRecipient = async () => {
    if (!newPrerequisRecipient.email) { toast.error('Email requis'); return; }
    try {
      const res = await api().post('/admin/prerequis-recipients', newPrerequisRecipient);
      setPrerequisRecipients([...prerequisRecipients, res.data.recipient]);
      setNewPrerequisRecipient({ email: '', name: '' });
      toast.success('Destinataire prérequis ajouté');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDeletePrerequisRecipient = async (recipientId) => {
    try {
      await api().delete(`/admin/prerequis-recipients/${recipientId}`);
      setPrerequisRecipients(prerequisRecipients.filter(r => r.id !== recipientId));
      toast.success('Destinataire prérequis supprimé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // PV relance config
  const fetchPvRelanceConfig = async () => {
    if (loadingPvCfg) return;
    setLoadingPvCfg(true);
    try {
      const res = await api().get('/admin/pv-relance-config');
      setPvRelanceCfg(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingPvCfg(false); }
  };

  const savePvRelanceConfig = async (overrides = {}) => {
    setSavingPvCfg(true);
    try {
      const payload = { ...pvRelanceCfg, ...overrides };
      const res = await api().put('/admin/pv-relance-config', payload);
      setPvRelanceCfg(res.data);
      toast.success(language === 'fr' ? 'Configuration enregistrée' : 'Configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingPvCfg(false); }
  };

  const addPvRecipient = () => {
    const email = newPvRecipient.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    if ((pvRelanceCfg.recipients || []).includes(email)) { toast.error('Déjà ajouté'); return; }
    savePvRelanceConfig({ recipients: [...(pvRelanceCfg.recipients || []), email] });
    setNewPvRecipient('');
  };

  const removePvRecipient = (email) => {
    savePvRelanceConfig({ recipients: (pvRelanceCfg.recipients || []).filter(e => e !== email) });
  };

  const runPvRelanceNow = async () => {
    try {
      const res = await api().post('/admin/jobs/pv-relance/run');
      toast.success(language === 'fr' ? `${res.data?.reminders_sent ?? 0} email(s) envoyé(s)` : `${res.data?.reminders_sent ?? 0} email(s) sent`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Direction report config
  const fetchDirReportConfig = async () => {
    if (loadingDirCfg) return;
    setLoadingDirCfg(true);
    try {
      const res = await api().get('/admin/direction-report-config');
      setDirCfg(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingDirCfg(false); }
  };

  const saveDirReportConfig = async (overrides = {}) => {
    setSavingDirCfg(true);
    try {
      const res = await api().put('/admin/direction-report-config', { ...dirCfg, ...overrides });
      setDirCfg(res.data);
      toast.success(language === 'fr' ? 'Configuration enregistrée' : 'Configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingDirCfg(false); }
  };

  const addDirRecipient = () => {
    const email = newDirRecipient.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Email invalide'); return; }
    if ((dirCfg.recipients || []).includes(email)) { toast.error('Déjà ajouté'); return; }
    saveDirReportConfig({ recipients: [...(dirCfg.recipients || []), email] });
    setNewDirRecipient('');
  };

  const removeDirRecipient = (email) => {
    saveDirReportConfig({ recipients: (dirCfg.recipients || []).filter(e => e !== email) });
  };

  const runDirReportNow = async () => {
    try {
      const res = await api().post('/admin/jobs/monthly-report/run?force=true');
      toast.success(language === 'fr' ? `Rapport envoyé (${res.data?.recipients_count ?? 0} destinataire·s)` : `Report sent (${res.data?.recipients_count ?? 0} recipients)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Business Commercial reminder config
  const fetchBizReminderConfig = async () => {
    try {
      const res = await api().get('/business-reports/reminder-config');
      setBizReminderCfg(res.data);
    } catch (e) { console.error(e); }
  };

  const saveBizReminderConfig = async (overrides = {}) => {
    setSavingBizReminderCfg(true);
    try {
      const res = await api().put('/business-reports/reminder-config', { ...bizReminderCfg, ...overrides });
      setBizReminderCfg(res.data);
      toast.success(language === 'fr' ? 'Configuration enregistrée' : 'Configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingBizReminderCfg(false); }
  };

  const runBizReminderNow = async () => {
    try {
      const res = await api().post('/business-reports/admin/run-reminders');
      toast.success(language === 'fr' ? `${res.data?.sent ?? 0} relance(s) envoyée(s)` : `${res.data?.sent ?? 0} reminder(s) sent`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Primes commercial emails
  const fetchPrimesEmails = async () => {
    setLoadingPrimesEmails(true);
    try {
      const [cRes, eRes] = await Promise.all([
        api().get('/primes/commerciaux'),
        api().get('/primes/emails-commerciaux'),
      ]);
      setPrimesCommerciaux(cRes.data || []);
      setPrimesEmailsMap(eRes.data || {});
    } catch (e) { console.error(e); toast.error('Erreur chargement emails primes'); }
    finally { setLoadingPrimesEmails(false); }
  };

  const handleSavePrimesEmails = async () => {
    setSavingPrimesEmails(true);
    try {
      await api().put('/primes/emails-commerciaux', { mapping: primesEmailsMap });
      toast.success('Emails des primes enregistrés');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingPrimesEmails(false); }
  };


  // Corner access CRUD
  const fetchCornerAccess = async () => {
    setLoadingCornerAccess(true);
    try {
      const res = await api().get('/dossiers-agenceur-access');
      setCornerAllowedIds(res.data.allowed_user_ids || []);
    } catch (e) { console.error(e); }
    finally { setLoadingCornerAccess(false); }
  };

  const handleToggleCornerAccess = (userId) => {
    setCornerAllowedIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSaveCornerAccess = async () => {
    setSavingCornerAccess(true);
    try {
      await api().put('/dossiers-agenceur-access', { allowed_user_ids: cornerAllowedIds });
      toast.success('Accès Commande Corner mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingCornerAccess(false); }
  };

  // Prerequis access CRUD
  const fetchPrerequisAccess = async () => {
    setLoadingPrerequisAccess(true);
    try {
      const res = await api().get('/prerequis-techniques-access');
      setBlockedUserIds(res.data.blocked_user_ids || []);
    } catch (e) { console.error(e); }
    finally { setLoadingPrerequisAccess(false); }
  };

  const handleToggleBlockUser = (userId) => {
    setBlockedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSavePrerequisAccess = async () => {
    setSavingPrerequisAccess(true);
    try {
      await api().put('/prerequis-techniques-access', { blocked_user_ids: blockedUserIds });
      toast.success('Configuration d\'accès mise à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSavingPrerequisAccess(false); }
  };

  // Capex Sales access CRUD (autorisation explicite / liste blanche)
  const fetchCsAccess = async () => {
    setLoadingCsAccess(true);
    try {
      const res = await api().get('/capex-sales/access-config');
      setCsAllowedUserIds(res.data.allowed_user_ids || []);
      setCsAlwaysAllowedIds(res.data.always_allowed_ids || []);
    } catch (e) { console.error(e); }
    finally { setLoadingCsAccess(false); }
  };

  const handleToggleCsAllowUser = (userId) => {
    if (csAlwaysAllowedIds.includes(userId)) return; // admins toujours autorisés
    setCsAllowedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSaveCsAccess = async () => {
    setSavingCsAccess(true);
    try {
      await api().put('/capex-sales/access-config', { allowed_user_ids: csAllowedUserIds });
      toast.success('Accès Capex Sales mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSavingCsAccess(false); }
  };

  const handleImportCsOpps = async (preview = false) => {
    if (!csOppFile) { toast.error('Sélectionnez un fichier .xlsx'); return; }
    setCsOppUploading(true); if (!preview) { /* keep preview visible until done */ }
    try {
      const fd = new FormData();
      fd.append('file', csOppFile);
      const res = await api().post(`/capex-sales/import-opportunities${preview ? '?preview=true' : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsOppResult(res.data);
      if (!preview) { toast.success(`${res.data.imported} opportunité(s) importée(s)`); fetchCsImportLog(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    } finally { setCsOppUploading(false); }
  };

  const fetchCsImportLog = async () => {
    try { const res = await api().get('/capex-sales/import-log'); setCsImportLog(res.data.items || []); }
    catch (e) { /* noop */ }
  };

  const handleImportCsContacts = async (preview = false) => {
    if (!csContactFile) { toast.error('Sélectionnez un fichier .xlsx'); return; }
    setCsContactUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', csContactFile);
      const res = await api().post(`/capex-sales/import-contacts${preview ? '?preview=true' : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsContactResult(res.data);
      if (!preview) { toast.success(`${res.data.imported} contact(s) importé(s)`); fetchCsImportLog(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    } finally { setCsContactUploading(false); }
  };

  const handleImportCsCr = async (preview = false) => {
    if (!csCrFile) { toast.error('Sélectionnez un fichier .xlsx'); return; }
    setCsCrUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', csCrFile);
      const res = await api().post(`/capex-sales/import-comptes-rendus${preview ? '?preview=true' : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsCrResult(res.data);
      if (!preview) { toast.success(`${res.data.imported} compte(s) rendu(s) importé(s)`); fetchCsImportLog(); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    } finally { setCsCrUploading(false); }
  };

  const handleRollbackImport = async (log) => {
    if (!window.confirm("Annuler cet import ? Les données correspondantes seront supprimées (ou restaurées pour un dédoublonnage).")) return;
    try {
      const res = await api().post('/capex-sales/import-rollback', { log_id: log.id });
      toast.success(res.restored !== undefined ? `${res.data.restored} compte(s) restauré(s)` : `${res.data.deleted} ${res.data.type} supprimé(s)`);
      fetchCsImportLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'annulation");
    }
  };

  const handleDedupAccounts = async () => {
    if (!window.confirm('Lancer le dédoublonnage des comptes homonymes ? Les fiches prospects en double seront fusionnées (sauvegarde automatique). Les clients sont conservés.')) return;
    setCsDedupRunning(true); setCsDedupResult(null);
    try {
      const res = await api().post('/capex-sales/dedup-accounts', {});
      setCsDedupResult(res.data);
      toast.success(res.data.message || `${res.data.deleted} doublon(s) supprimé(s)`);
      fetchCsImportLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du dédoublonnage");
    } finally { setCsDedupRunning(false); }
  };

  const fetchEmailRouting = async () => {
    setLoadingRouting(true);
    try {
      const res = await api().get('/email-routing-config');
      setEmailRouting(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingRouting(false); }
  };

  const toggleEmailRouting = async (emailType, userId, enabled) => {
    try {
      await api().post('/email-routing-config', { email_type: emailType, user_id: userId, enabled });
      setEmailRouting(prev => prev.map(r => r.email_type === emailType ? {
        ...r,
        recipients: r.recipients.map(rec => rec.user_id === userId ? { ...rec, enabled } : rec)
      } : r));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const fetchRoutingHistory = async () => {
    setLoadingRoutingHistory(true);
    try {
      const res = await api().get('/email-routing-history');
      setRoutingHistory(res.data || []);
      setShowRoutingHistory(true);
    } catch (e) { console.error(e); }
    finally { setLoadingRoutingHistory(false); }
  };



  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.label.trim()) { toast.error('Le libellé est obligatoire'); return; }
    setSavingChecklistItem(true);
    try {
      const payload = {
        label: newChecklistItem.label,
        options: newChecklistItem.options.split(',').map(s => s.trim()).filter(Boolean),
        comment_on: newChecklistItem.comment_on.split(',').map(s => s.trim()).filter(Boolean),
        category: newChecklistItem.category || 'Pose & Installation',
      };
      await api().post('/chantier-checklist-items', payload);
      toast.success('Point ajouté');
      setNewChecklistItem({ label: '', options: 'Oui,Non,Partiellement', comment_on: 'Non,Partiellement', category: newChecklistItem.category });
      fetchChecklistItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingChecklistItem(false); }
  };

  const handleUpdateChecklistItem = async () => {
    if (!editingChecklistItem) return;
    setSavingChecklistItem(true);
    try {
      const payload = {
        label: editingChecklistItem.label,
        options: typeof editingChecklistItem.options === 'string'
          ? editingChecklistItem.options.split(',').map(s => s.trim()).filter(Boolean)
          : editingChecklistItem.options,
        comment_on: typeof editingChecklistItem.comment_on === 'string'
          ? editingChecklistItem.comment_on.split(',').map(s => s.trim()).filter(Boolean)
          : editingChecklistItem.comment_on,
        active: editingChecklistItem.active,
        category: editingChecklistItem.category || 'Pose & Installation',
      };
      await api().put(`/chantier-checklist-items/${editingChecklistItem.id}`, payload);
      toast.success('Point mis à jour');
      setEditingChecklistItem(null);
      fetchChecklistItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingChecklistItem(false); }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    try {
      await api().delete(`/chantier-checklist-items/${itemId}`);
      toast.success('Point supprimé');
      fetchChecklistItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleChecklistItem = async (item) => {
    try {
      await api().put(`/chantier-checklist-items/${item.id}`, { active: !item.active });
      fetchChecklistItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleMoveChecklistItem = async (item, direction) => {
    const sorted = [...checklistItems].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(i => i.id === item.id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === sorted.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const items = [
      { id: sorted[idx].id, order: sorted[swapIdx].order },
      { id: sorted[swapIdx].id, order: sorted[idx].order },
    ];
    try {
      await api().put('/chantier-checklist-items/reorder', { items });
      fetchChecklistItems();
    } catch (e) { toast.error('Erreur de réorganisation'); }
  };

  const handleCreateOrg = async () => {
    if (!newOrgForm.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setCreatingOrg(true);
    try {
      await api().post('/organisations', newOrgForm);
      toast.success('Organisation créée');
      setNewOrgForm({ nom: '', description: '', contact_email: '', contact_phone: '' });
      fetchOrganisations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreatingOrg(false); }
  };

  const handleDeleteOrg = async (orgId) => {
    try {
      await api().delete(`/organisations/${orgId}`);
      toast.success('Organisation supprimée');
      setDeletingOrg(null);
      fetchOrganisations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateOrg = async () => {
    if (!editingOrg) return;
    try {
      await api().put(`/organisations/${editingOrg.id}`, editingOrg);
      toast.success('Organisation mise à jour');
      setEditingOrg(null);
      fetchOrganisations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleAssignOrg = async (userId, orgId) => {
    try {
      if (orgId) {
        await api().put(`/organisations/${orgId}/assign-user/${userId}`);
        toast.success('Utilisateur affecté à l\'organisation');
      } else {
        await api().put(`/organisations/remove-user/${userId}`);
        toast.success('Utilisateur retiré de l\'organisation');
      }
      // Refresh users
      const usersRes = await api().get('/admin/users/activity');
      setUsers(usersRes.data);
      fetchOrganisations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setAssigningOrgUser(null); }
  };

  const handleRecalculateAll = async () => {
    if (!window.confirm('Recalculer TOUTES les simulations avec les données cluster actuelles ? Cette opération peut prendre quelques minutes.')) return;
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const res = await api().post('/admin/recalculate-all-simulations');
      setRecalcResult(res.data);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du recalcul');
    } finally {
      setRecalculating(false);
    }
  };

  // Fetch login history
  const fetchLoginHistory = async (forUser = null) => {
    if (loadingLoginHistory) return;
    setLoadingLoginHistory(true);
    try {
      const url = forUser ? `/admin/login-history?limit=200&user_id=${forUser.id}` : '/admin/login-history?limit=200';
      const response = await api().get(url);
      setLoginHistory(response.data);
      setSelectedUserHistory(forUser);
      setShowLoginHistory(true);
    } catch (error) {
      console.error('Failed to fetch login history:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement de l\'historique' : 'Failed to load history');
    } finally {
      setLoadingLoginHistory(false);
    }
  };

  // Fetch email history
  const fetchEmailHistory = async () => {
    if (loadingEmailHistory) return;
    setLoadingEmailHistory(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        api().get('/admin/email-history?limit=100'),
        api().get('/admin/email-stats')
      ]);
      setEmailHistory(historyRes.data);
      setEmailStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch email history:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement de l\'historique emails' : 'Failed to load email history');
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  // Fetch email recipients
  const fetchEmailRecipients = async () => {
    if (loadingRecipients) return;
    setLoadingRecipients(true);
    try {
      const response = await api().get('/admin/email-recipients');
      setEmailRecipients(response.data);
    } catch (error) {
      console.error('Failed to fetch email recipients:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des destinataires' : 'Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Add email recipient
  const handleAddRecipient = async () => {
    if (!newRecipient.email || !newRecipient.name) {
      toast.error(language === 'fr' ? 'Email et nom requis' : 'Email and name required');
      return;
    }
    try {
      const response = await api().post('/admin/email-recipients', newRecipient);
      setEmailRecipients([...emailRecipients, response.data]);
      setNewRecipient({ email: '', name: '', role: 'admin' });
      toast.success(language === 'fr' ? 'Destinataire ajouté' : 'Recipient added');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // Delete email recipient
  const handleDeleteRecipient = async (recipientId) => {
    try {
      await api().delete(`/admin/email-recipients/${recipientId}`);
      setEmailRecipients(emailRecipients.filter(r => r.id !== recipientId));
      toast.success(language === 'fr' ? 'Destinataire supprimé' : 'Recipient removed');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // ============ HG Recipients Management ============
  
  // Fetch HG recipients
  const fetchHgRecipients = async () => {
    if (loadingHgRecipients) return;
    setLoadingHgRecipients(true);
    try {
      const response = await api().get('/admin/hg-recipients');
      setHgRecipients(response.data);
    } catch (error) {
      console.error('Failed to fetch HG recipients:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des destinataires HG' : 'Failed to load HG recipients');
    } finally {
      setLoadingHgRecipients(false);
    }
  };

  // Add HG recipient
  const handleAddHgRecipient = async () => {
    if (!newHgRecipient.email) {
      toast.error(language === 'fr' ? 'Email requis' : 'Email required');
      return;
    }
    try {
      const response = await api().post('/admin/hg-recipients', newHgRecipient);
      setHgRecipients([...hgRecipients, response.data.recipient]);
      setNewHgRecipient({ email: '', name: '' });
      toast.success(language === 'fr' ? 'Destinataire HG ajouté' : 'HG recipient added');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // Delete HG recipient
  const handleDeleteHgRecipient = async (recipientId) => {
    try {
      await api().delete(`/admin/hg-recipients/${recipientId}`);
      setHgRecipients(hgRecipients.filter(r => r.id !== recipientId));
      toast.success(language === 'fr' ? 'Destinataire HG supprimé' : 'HG recipient removed');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // ============ Chantier (Pilotage / PV) Recipients Management ============
  const fetchChantierRecipients = async () => {
    if (loadingChantierRecipients) return;
    setLoadingChantierRecipients(true);
    try {
      const [recipientsRes, adminsRes] = await Promise.all([
        api().get('/chantier-recipients'),
        api().get('/chantier-recipients/included-admins'),
      ]);
      setChantierRecipients(recipientsRes.data);
      setChantierIncludedAdmins(adminsRes.data);
    } catch (error) {
      console.error('Failed to fetch chantier recipients:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des destinataires chantier' : 'Failed to load chantier recipients');
    } finally {
      setLoadingChantierRecipients(false);
    }
  };

  const handleToggleIncludedAdmin = async (userId, enabled) => {
    setTogglingAdminId(userId);
    try {
      await api().put(`/chantier-recipients/included-admins/${userId}`, { enabled });
      setChantierIncludedAdmins(prev => prev.map(a => a.user_id === userId ? { ...a, enabled } : a));
      toast.success(enabled
        ? (language === 'fr' ? 'Administrateur ajouté aux rapports chantier' : 'Admin added to chantier reports')
        : (language === 'fr' ? 'Administrateur retiré des rapports chantier' : 'Admin removed from chantier reports'));
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    } finally {
      setTogglingAdminId(null);
    }
  };

  const handleAddChantierRecipient = async () => {
    if (!newChantierRecipient.email) {
      toast.error(language === 'fr' ? 'Email requis' : 'Email required');
      return;
    }
    try {
      const response = await api().post('/chantier-recipients', newChantierRecipient);
      setChantierRecipients([...chantierRecipients, response.data]);
      setNewChantierRecipient({ email: '', name: '' });
      toast.success(language === 'fr' ? 'Destinataire chantier ajouté' : 'Chantier recipient added');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  const handleDeleteChantierRecipient = async (recipientId) => {
    try {
      await api().delete(`/chantier-recipients/${recipientId}`);
      setChantierRecipients(chantierRecipients.filter(r => r.id !== recipientId));
      toast.success(language === 'fr' ? 'Destinataire chantier supprimé' : 'Chantier recipient removed');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  const fetchCaCornerConfig = async () => {
    setLoadingCaCornerUsers(true);
    try {
      const res = await api().get('/ca-corner/users-access');
      const list = res.data?.users || [];
      setCaCornerUsers(list);
      const map = {};
      list.forEach(u => { map[u.id] = u.locked ? true : !!u.allowed; });
      setCaCornerUserAccess(map);
    } catch (e) { console.error('CA corner users-access load failed', e); }
    finally { setLoadingCaCornerUsers(false); }
    try {
      const ar = await api().get('/ca-corner/alert-config');
      setCaCornerAlertConfig({
        recipients: ar.data?.recipients || [],
        threshold_pct: ar.data?.threshold_pct ?? -15,
        min_months: ar.data?.min_months ?? 3,
        quality_threshold_pct: ar.data?.quality_threshold_pct ?? 90,
        renewal_reminder_months: ar.data?.renewal_reminder_months ?? 6,
        eligible_users: ar.data?.eligible_users || [],
      });
    } catch (e) { console.error('CA corner alert-config load failed', e); }
  };

  const toggleAlertRecipient = (uid) => {
    setCaCornerAlertConfig(prev => {
      const has = prev.recipients.includes(uid);
      return { ...prev, recipients: has ? prev.recipients.filter(x => x !== uid) : [...prev.recipients, uid] };
    });
  };

  const handleSaveCaCornerAlertConfig = async () => {
    setSavingCaCornerAlert(true);
    try {
      await api().put('/ca-corner/alert-config', {
        recipients: caCornerAlertConfig.recipients,
        threshold_pct: Number(caCornerAlertConfig.threshold_pct),
        min_months: Number(caCornerAlertConfig.min_months),
        quality_threshold_pct: Number(caCornerAlertConfig.quality_threshold_pct),
        renewal_reminder_months: Number(caCornerAlertConfig.renewal_reminder_months),
      });
      toast.success(language === 'fr' ? 'Alerte CA Corner enregistrée' : 'CA Corner alert saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally { setSavingCaCornerAlert(false); }
  };

  const handleSaveCaCornerAccess = async () => {
    setSavingCaCornerAccess(true);
    try {
      const payload = {};
      caCornerUsers.forEach(u => { if (!u.locked && caCornerUserAccess[u.id]) payload[u.id] = true; });
      await api().put('/ca-corner/users-access', { users: payload });
      toast.success(language === 'fr' ? 'Accès CA Corner enregistré' : 'CA Corner access saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally { setSavingCaCornerAccess(false); }
  };

  // ============ Bulk Export (mass extraction) ============
  const handleBulkExport = async (kind) => {
    setBulkExporting(kind);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bulk-export/${kind}?status=${bulkStatus}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      if (blob.size === 0) {
        toast.error(language === 'fr' ? 'Aucune donnée à exporter' : 'No data to export');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ext = kind === 'excel-summary' ? 'xlsx' : 'zip';
      const prefix = kind === 'excel-summary' ? 'Simulations_CAPEX' : (kind === 'excel-zip' ? 'Etudes_Excel' : 'Etudes_PDF');
      a.href = url;
      a.download = `${prefix}_${bulkStatus}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(language === 'fr' ? 'Extraction téléchargée' : 'Export downloaded');
    } catch (error) {
      console.error('Bulk export failed:', error);
      toast.error(language === 'fr' ? "Erreur lors de l'extraction" : 'Export error');
    } finally {
      setBulkExporting(null);
    }
  };

  // ============ Contract Recipients Management ============
  
  // Fetch Contract recipients
  const fetchContractRecipients = async () => {
    if (loadingContractRecipients) return;
    setLoadingContractRecipients(true);
    try {
      const response = await api().get('/admin/contract-recipients');
      setContractRecipients(response.data);
    } catch (error) {
      console.error('Failed to fetch contract recipients:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des destinataires contrat' : 'Failed to load contract recipients');
    } finally {
      setLoadingContractRecipients(false);
    }
  };

  // Add Contract recipient
  const handleAddContractRecipient = async () => {
    if (!newContractRecipient.email) {
      toast.error(language === 'fr' ? 'Email requis' : 'Email required');
      return;
    }
    try {
      const response = await api().post('/admin/contract-recipients', newContractRecipient);
      setContractRecipients([...contractRecipients, response.data.recipient]);
      setNewContractRecipient({ email: '', name: '' });
      toast.success(language === 'fr' ? 'Destinataire contrat ajouté' : 'Contract recipient added');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // Delete Contract recipient
  const handleDeleteContractRecipient = async (recipientId) => {
    try {
      await api().delete(`/admin/contract-recipients/${recipientId}`);
      setContractRecipients(contractRecipients.filter(r => r.id !== recipientId));
      toast.success(language === 'fr' ? 'Destinataire contrat supprimé' : 'Contract recipient removed');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // ============ Permissions Management ============
  
  // Fetch all permissions
  const fetchPermissions = async () => {
    if (loadingPermissions) return;
    setLoadingPermissions(true);
    try {
      const response = await api().get('/admin/permissions');
      setPermissions(response.data.permissions);
      setPermissionLabels(response.data.labels);
      setAvailableRoles(response.data.roles);
      // Initialize edited permissions with current values
      setEditedPermissions(response.data.permissions);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des permissions' : 'Failed to load permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Update permission for a role
  const handlePermissionChange = (role, permission, value) => {
    setEditedPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [permission]: value
      }
    }));
  };

  // Save permissions for a role
  const handleSavePermissions = async (role) => {
    setSavingPermissions(true);
    try {
      await api().put(`/admin/permissions/${role}`, { permissions: editedPermissions[role] });
      setPermissions(prev => ({
        ...prev,
        [role]: editedPermissions[role]
      }));
      toast.success(language === 'fr' ? `Permissions sauvegardées pour ${role}` : `Permissions saved for ${role}`);
    } catch (error) {
      console.error('Failed to save permissions:', error);
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    } finally {
      setSavingPermissions(false);
    }
  };

  // Reset permissions for a role
  const handleResetPermissions = async (role) => {
    if (!window.confirm(language === 'fr' 
      ? `Réinitialiser les permissions pour ${role} aux valeurs par défaut ?` 
      : `Reset permissions for ${role} to defaults?`)) {
      return;
    }
    
    try {
      const response = await api().post(`/admin/permissions/reset/${role}`);
      setEditedPermissions(prev => ({
        ...prev,
        [role]: response.data.permissions
      }));
      setPermissions(prev => ({
        ...prev,
        [role]: response.data.permissions
      }));
      toast.success(language === 'fr' ? 'Permissions réinitialisées' : 'Permissions reset');
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error');
      toast.error(msg);
    }
  };

  // Update user role
  // --- Accès par RÔLE (défaut appliqué à tous les utilisateurs du rôle) ---
  const openRoleAccess = async () => {
    try {
      const res = await api().get('/role-section-access');
      setRoleAccessConfig(res.data?.role_section_access || {});
    } catch (e) { setRoleAccessConfig({}); }
    setRoleAccessSelected('commercial');
    setRoleAccessOpen(true);
  };

  // Valeur effective d'un toggle pour le rôle sélectionné (config sauvegardée sinon défaut historique).
  const roleAccessValue = (role, to) => {
    const cfg = roleAccessConfig[role];
    if (cfg && Object.prototype.hasOwnProperty.call(cfg, to)) return !!cfg[to];
    const isAdminRole = ['admin', 'super_admin'].includes(role);
    return roleDefaultAccess(role, to, { isAdmin: isAdminRole, caCornerAccess: isAdminRole });
  };

  const setRoleAccessValue = (role, to, val) => {
    setRoleAccessConfig(prev => ({ ...prev, [role]: { ...(prev[role] || {}), [to]: val } }));
  };

  const resetRoleAccess = (role) => {
    const isAdminRole = ['admin', 'super_admin'].includes(role);
    const d = {};
    SECTION_DEFS.forEach(s => { d[s.to] = roleDefaultAccess(role, s.to, { isAdmin: isAdminRole, caCornerAccess: isAdminRole }); });
    setRoleAccessConfig(prev => ({ ...prev, [role]: d }));
  };

  const saveRoleAccess = async () => {
    setSavingRoleAccess(true);
    try {
      // On matérialise toutes les sections pour le rôle sélectionné afin d'éviter les ambiguïtés.
      const materialized = { ...roleAccessConfig };
      const role = roleAccessSelected;
      const full = {};
      SECTION_DEFS.forEach(s => { full[s.to] = roleAccessValue(role, s.to); });
      materialized[role] = full;
      await api().put('/admin/role-section-access', { role_section_access: materialized });
      setRoleAccessConfig(materialized);
      toast.success(language === 'fr' ? 'Accès par rôle enregistré' : 'Role access saved');
      setRoleAccessOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally { setSavingRoleAccess(false); }
  };

  // --- Accès fin aux sous-onglets Admin (par rôle + surcharge par utilisateur) ---
  const fetchAdminTabAccess = async () => {
    try {
      const res = await api().get('/admin-tab-access');
      setAtaConfig({ role_config: res.data?.role_config || {}, user_config: res.data?.user_config || {} });
    } catch (e) { /* noop */ }
  };
  const ataRoleValue = (role, key) => {
    const rc = ataConfig.role_config || {};
    if (rc[role] && Object.prototype.hasOwnProperty.call(rc[role], key)) return !!rc[role][key];
    return tabBaseDefault(role, key);
  };
  const ataUserValue = (userId, key) => {
    const uc = ataConfig.user_config || {};
    if (uc[userId] && Object.prototype.hasOwnProperty.call(uc[userId], key)) return !!uc[userId][key];
    return ataRoleValue(ataSelectedRoleForUser(userId), key);
  };
  const ataSelectedRoleForUser = (userId) => (users.find(u => u.id === userId)?.role) || 'admin';
  const ataUserHasOverride = (userId, key) => {
    const uc = ataConfig.user_config || {};
    return !!(uc[userId] && Object.prototype.hasOwnProperty.call(uc[userId], key));
  };
  const setAtaRoleValue = (role, key, val) => {
    setAtaConfig(prev => ({ ...prev, role_config: { ...prev.role_config, [role]: { ...(prev.role_config[role] || {}), [key]: val } } }));
  };
  const setAtaUserValue = (userId, key, val) => {
    setAtaConfig(prev => ({ ...prev, user_config: { ...prev.user_config, [userId]: { ...(prev.user_config[userId] || {}), [key]: val } } }));
  };
  const clearAtaUserOverride = (userId, key) => {
    setAtaConfig(prev => {
      const u = { ...(prev.user_config[userId] || {}) };
      delete u[key];
      const uc = { ...prev.user_config };
      if (Object.keys(u).length) uc[userId] = u; else delete uc[userId];
      return { ...prev, user_config: uc };
    });
  };
  const resetAtaRole = (role) => {
    setAtaConfig(prev => { const rc = { ...prev.role_config }; delete rc[role]; return { ...prev, role_config: rc }; });
  };
  const resetAtaUser = (userId) => {
    setAtaConfig(prev => { const uc = { ...prev.user_config }; delete uc[userId]; return { ...prev, user_config: uc }; });
  };
  const saveAdminTabAccess = async () => {
    setSavingAta(true);
    try {
      await api().put('/admin/admin-tab-access', ataConfig);
      toast.success(language === 'fr' ? 'Accès aux onglets enregistré' : 'Tab access saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally { setSavingAta(false); }
  };
  const exportConnectionsCsv = () => {
    const now = Date.now();
    const statut = (u) => !u.last_login ? 'Jamais connecté'
      : ((now - new Date(u.last_login).getTime()) <= 30 * 864e5 ? 'Actif (30 j)' : 'Inactif (> 30 j)');
    const rows = [...users].sort((a, b) => (b.login_count || 0) - (a.login_count || 0));
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Nom', 'Email', 'Rôle', 'Statut connexion', 'Dernière connexion', 'Nombre de connexions'];
    const lines = rows.map(u => [
      u.name, u.email, u.role, statut(u),
      u.last_login ? format(new Date(u.last_login), 'dd/MM/yyyy HH:mm', { locale: dateLocale }) : 'Jamais',
      u.login_count || 0,
    ].map(esc).join(';'));
    const csv = '\uFEFF' + [header.map(esc).join(';'), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historique_connexions_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(language === 'fr' ? 'Export généré' : 'Export generated');
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api().put(`/admin/users/${userId}/role`, { role: newRole });
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      
      setEditingUserRole(null);
      toast.success(language === 'fr' ? 'Rôle mis à jour' : 'Role updated');
    } catch (error) {
      console.error('Failed to update role:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
      toast.error(message);
    }
  };

  const openEditProfile = (u) => {
    setEditProfileForm({ name: u.name || '', email: u.email || '' });
    setEditProfileUser(u);
  };

  const handleSaveProfile = async () => {
    if (!editProfileUser) return;
    setSavingProfile(true);
    try {
      const res = await api().put(`/admin/users/${editProfileUser.id}/profile`, {
        name: editProfileForm.name, email: editProfileForm.email,
      });
      const upd = res.data?.user || {};
      setUsers(users.map(u => u.id === editProfileUser.id ? { ...u, name: upd.name ?? u.name, email: upd.email ?? u.email } : u));
      toast.success(language === 'fr' ? 'Fiche profil mise à jour' : 'Profile updated');
      setEditProfileUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed'));
    } finally { setSavingProfile(false); }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    try {
      await api().delete(`/admin/users/${userId}`);
      
      // Update local state
      setUsers(users.filter(u => u.id !== userId));
      setDeletingUser(null);
      toast.success(language === 'fr' ? 'Utilisateur supprimé' : 'User deleted');
    } catch (error) {
      console.error('Failed to delete user:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la suppression' : 'Delete failed');
      toast.error(message);
    }
  };

  // Approve or reject user
  const handleApproveUser = async (userId, action) => {
    setApprovingUser(userId);
    try {
      await api().put(`/admin/users/${userId}/approve`, { action });
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, is_approved: action === 'approve', approval_status: action === 'approve' ? 'approved' : 'rejected' }
          : u
      ));
      
      toast.success(
        action === 'approve'
          ? (language === 'fr' ? 'Utilisateur validé avec succès' : 'User approved successfully')
          : (language === 'fr' ? 'Demande refusée' : 'Request rejected')
      );
    } catch (error) {
      console.error('Failed to approve/reject user:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la validation' : 'Approval failed');
      toast.error(message);
    } finally {
      setApprovingUser(null);
    }
  };

  // Open visibility configuration modal for a user
  const openVisibilityConfig = async (userToConfig) => {
    setConfiguringVisibility(userToConfig);
    setLoadingVisibility(true);
    
    try {
      // Fetch users list filtered by hierarchy (only users BELOW the configured user)
      // and current visibility settings
      const [usersListRes, visibilityRes] = await Promise.all([
        api().get(`/admin/users/list-simple?for_user_role=${userToConfig.role}`),
        api().get(`/admin/users/${userToConfig.id}/visibility`)
      ]);
      
      // Also exclude the user being configured from the list
      setAllUsersList(usersListRes.data.filter(u => u.id !== userToConfig.id));
      setVisibilityScope(visibilityRes.data.visibility_scope || 'own');
      setSelectedVisibleUsers(visibilityRes.data.visible_users || []);
    } catch (error) {
      console.error('Failed to load visibility config:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load');
    } finally {
      setLoadingVisibility(false);
    }
  };

  // Save visibility settings
  const handleSaveVisibility = async () => {
    if (!configuringVisibility) return;
    
    setSavingVisibility(true);
    try {
      await api().put(`/admin/users/${configuringVisibility.id}/visibility`, {
        visibility_scope: visibilityScope,
        visible_users: visibilityScope === 'specific' ? selectedVisibleUsers : []
      });
      
      // Update local users state
      setUsers(users.map(u => 
        u.id === configuringVisibility.id 
          ? { 
              ...u, 
              visibility_scope: visibilityScope, 
              visible_users: visibilityScope === 'specific' ? selectedVisibleUsers : [] 
            }
          : u
      ));
      
      toast.success(language === 'fr' ? 'Droits de visibilité mis à jour' : 'Visibility rights updated');
      setConfiguringVisibility(null);
    } catch (error) {
      console.error('Failed to save visibility:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Save failed');
      toast.error(message);
    } finally {
      setSavingVisibility(false);
    }
  };

  // Toggle user selection in visibility config
  const toggleVisibleUser = (userId) => {
    setSelectedVisibleUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Resend a single failed email
  const handleResendEmail = async (emailId) => {
    setResendingEmail(emailId);
    try {
      const response = await api().post(`/admin/email-history/${emailId}/resend`);
      if (response.data.status === 'sent') {
        // Update local state
        setEmailHistory(prev => prev.map(email => 
          email.id === emailId ? { ...email, status: 'sent' } : email
        ));
        toast.success(language === 'fr' ? 'Email renvoyé avec succès' : 'Email resent successfully');
      } else {
        toast.error(response.data.message || (language === 'fr' ? 'Échec du renvoi' : 'Resend failed'));
      }
    } catch (error) {
      console.error('Failed to resend email:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du renvoi' : 'Resend failed');
      toast.error(message);
    } finally {
      setResendingEmail(null);
    }
  };

  // Resend multiple selected failed emails
  const handleResendBulk = async () => {
    if (selectedEmails.length === 0) {
      toast.error(language === 'fr' ? 'Aucun email sélectionné' : 'No emails selected');
      return;
    }
    
    setResendingBulk(true);
    try {
      const response = await api().post('/admin/email-history/resend-bulk', selectedEmails);
      const { success, failed, skipped } = response.data;
      
      // Update local state for successful resends
      if (success > 0) {
        setEmailHistory(prev => prev.map(email => 
          selectedEmails.includes(email.id) && email.status === 'failed'
            ? { ...email, status: 'sent' }
            : email
        ));
      }
      
      toast.success(
        language === 'fr' 
          ? `${success} envoyé(s), ${failed} échec(s), ${skipped} ignoré(s)`
          : `${success} sent, ${failed} failed, ${skipped} skipped`
      );
      setSelectedEmails([]);
    } catch (error) {
      console.error('Failed to resend emails:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du renvoi' : 'Resend failed');
      toast.error(message);
    } finally {
      setResendingBulk(false);
    }
  };

  // Toggle email selection
  const toggleEmailSelection = (emailId) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  // Select/Deselect all failed emails
  const toggleSelectAllFailed = () => {
    const failedEmails = emailHistory.filter(e => e.status === 'failed').map(e => e.id);
    if (selectedEmails.length === failedEmails.length) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(failedEmails);
    }
  };

  const roleOptions = [
    'super_admin', 'admin', 'dg', 'dop', 'directeur_commercial',
    'directeur_regional', 'directrice_regionale', 'business_manager', 
    'commercial', 'assistante_commerciale',
    'rh', 'qualite', 'offre', 'marketing', 'achat', 'comptabilite',
    'autre', 'readonly', 'prestataire'
  ];

  // --- Navigation Admin groupée par catégories (remplace la longue barre d'onglets) ---
  const L = (fr, en) => (language === 'fr' ? fr : en);
  const adminTabDefs = {
    users: { label: t('users'), icon: Users },
    permissions: { label: L('Permissions', 'Permissions'), icon: Key, sa: true },
    'admin-tab-access': { label: L('Accès Onglets Admin', 'Admin Tabs Access'), icon: Lock, sa: true },
    'ca-corner-access': { label: L('Accès CA Corner', 'CA Corner Access'), icon: FileSpreadsheet },
    'corner-access': { label: L('Accès Corner', 'Corner Access'), icon: Send, sa: true },
    'prerequis-access': { label: L('Accès Prérequis', 'Prerequis Access'), icon: Shield, sa: true },
    'capex-sales-access': { label: L('Accès Capex Sales', 'Capex Sales Access'), icon: Shield, sa: true },
    'capex-sales-import': { label: L('Import Opportunités', 'Opportunities Import'), icon: FileSpreadsheet, sa: true },
    organisations: { label: 'Organisations', icon: HardHat },
    'login-history': { label: L('Historique connexions', 'Login History'), icon: LogIn },
    emails: { label: L('Historique Emails', 'Email History'), icon: Mail },
    'email-routing': { label: L('Routage Emails', 'Email Routing'), icon: Mail },
    'email-templates': { label: L('Templates Email', 'Email Templates'), icon: FileText, sa: true },
    recipients: { label: L('Destinataires', 'Recipients'), icon: Send },
    'hg-recipients': { label: L('Destinataires HG', 'HG Recipients'), icon: Building2 },
    'contract-recipients': { label: L('Destinataires Contrat', 'Contract Recipients'), icon: FileSignature },
    'chantier-recipients': { label: L('Destinataires Chantier', 'Chantier Recipients'), icon: HardHat },
    'prerequis-recipients': { label: L('Destinataires Prérequis', 'Prerequis Recipients'), icon: HardHat, sa: true },
    'primes-emails': { label: L('Emails Primes', 'Primes Emails'), icon: Wallet, sa: true },
    'checklist-chantier': { label: 'Checklist Chantier', icon: HardHat },
    'prerequis-checklist': { label: L('Checklist Prérequis', 'Prerequis Checklist'), icon: ShieldCheck, sa: true },
    'sla-agenceur': { label: 'SLA Agenceur', icon: Clock },
    'agenceurs-config': { label: L('Prestataires', 'Providers'), icon: UserCog },
    'relances-pv': { label: L('Relances PV', 'PV Reminders'), icon: Clock },
    'planning-chantiers': { label: 'Planning chantiers', icon: CalendarDays },
    'upload-categories': { label: L('Catégories Upload', 'Upload Categories'), icon: Tag },
    clusters: { label: 'Clusters', icon: Settings },
    'rapport-direction': { label: L('Rapport Direction', 'Direction Report'), icon: BarChart3 },
    'business-relance': { label: L('Relance Business Commercial', 'Business Reminder'), icon: Clock },
    'bulk-export': { label: L('Extraction Massive', 'Bulk Export'), icon: Download },
    diagnostic: { label: L('Diagnostic', 'Diagnostic'), icon: Settings, sa: true },
  };
  const adminCategories = [
    { id: 'users_access', label: L('Utilisateurs & Accès', 'Users & Access'), icon: Users, tabs: ['users', 'login-history', 'permissions', 'admin-tab-access', 'ca-corner-access', 'corner-access', 'prerequis-access', 'capex-sales-access', 'organisations'] },
    { id: 'emails', label: L('Emails & Destinataires', 'Emails & Recipients'), icon: Mail, tabs: ['emails', 'email-routing', 'email-templates', 'recipients', 'hg-recipients', 'contract-recipients', 'chantier-recipients', 'prerequis-recipients', 'primes-emails'] },
    { id: 'chantier_corner', label: L('Chantier & Corner', 'Site & Corner'), icon: HardHat, tabs: ['checklist-chantier', 'prerequis-checklist', 'sla-agenceur', 'agenceurs-config', 'relances-pv', 'planning-chantiers', 'upload-categories'] },
    { id: 'system', label: L('Pilotage & Système', 'Config & System'), icon: Settings, tabs: ['clusters', 'rapport-direction', 'business-relance', 'bulk-export', 'capex-sales-import', 'diagnostic'] },
  ];
  // Visibilité effective d'un sous-onglet Admin pour un rôle/utilisateur (surcharge user > config rôle > défaut visible).
  const adminTabAccessValue = (role, userId, key) => resolveTabVisible(ataConfig, role, userId, key);
  const isTabVisible = (key) => {
    if (key === 'primes-emails') return isSuperAdmin && (user?.email || '').trim().toLowerCase() === 'c.perez@wasabicorner.fr';
    if (adminTabDefs[key]?.sa && !isSuperAdmin) return false;
    if (isSuperAdmin) return true;
    // Rôle admin (non super) : applique la matrice d'accès fine aux sous-onglets.
    return adminTabAccessValue(user?.role, user?.id, key);
  };
  // Sous-onglets contrôlables (visibles par un admin, hors onglets super_admin only).
  const controllableAdminTabs = Object.keys(adminTabDefs).filter(k => !adminTabDefs[k]?.sa);
  const selectAdminTab = (key) => {
    setAdminTab(key);
    // Dispatch du chargement des données (évalué au clic : fonctions déjà initialisées).
    const fetchMap = {
      emails: fetchEmailHistory, recipients: fetchEmailRecipients, 'hg-recipients': fetchHgRecipients,
      'contract-recipients': fetchContractRecipients, 'chantier-recipients': fetchChantierRecipients,
      'ca-corner-access': fetchCaCornerConfig, organisations: fetchOrganisations, 'sla-agenceur': fetchSlaSettings,
      'agenceurs-config': fetchAgenceursConfig, 'checklist-chantier': fetchChecklistItems,
      'upload-categories': fetchUploadCategories, 'email-routing': fetchEmailRouting,
      'relances-pv': fetchPvRelanceConfig, 'rapport-direction': fetchDirReportConfig,
      'business-relance': fetchBizReminderConfig,
      'prerequis-checklist': fetchPrereqCheckItems, 'prerequis-recipients': fetchPrerequisRecipients,
      'corner-access': fetchCornerAccess, 'prerequis-access': fetchPrerequisAccess,
      'capex-sales-access': fetchCsAccess,
      'capex-sales-import': fetchCsImportLog,
      permissions: fetchPermissions, 'email-templates': fetchEmailTemplates,
      'admin-tab-access': fetchAdminTabAccess,
      'primes-emails': fetchPrimesEmails,
    };
    const fn = fetchMap[key];
    if (fn) { try { fn(); } catch (e) {} }
  };
  // Lien direct vers un sous-onglet : /admin?tab=prerequis-recipients
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    if (t && adminTabDefs[t]) {
      const cat = adminCategories.find(c => c.tabs.includes(t));
      if (cat) setAdminCat(cat.id);
      selectAdminTab(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleEditCluster = (cluster) => {
    setEditingCluster({
      ...cluster,
      food_cost_rate: (cluster.food_cost_rate * 100).toFixed(2),
      other_cost_rate: (cluster.other_cost_rate * 100).toFixed(2),
      labor_cost_rate: cluster.labor_cost_rate ? (cluster.labor_cost_rate * 100).toFixed(2) : '',
      commission_rate: cluster.commission_rate ? (cluster.commission_rate * 100).toFixed(2) : '',
      contribution_margin_rate: cluster.contribution_margin_rate ? (cluster.contribution_margin_rate * 100).toFixed(2) : '',
    });
  };

  const handleSaveCluster = async () => {
    if (!editingCluster) return;
    
    setSavingCluster(true);
    try {
      const updateData = {
        name: editingCluster.name,
        arpc_label: editingCluster.arpc_label,
        min_ca: parseFloat(editingCluster.min_ca || editingCluster.ca_min || 0),
        max_ca: parseFloat(editingCluster.max_ca || editingCluster.ca_max) || 999999999,
        food_cost_rate: parseFloat(editingCluster.food_cost_rate) / 100,
        other_cost_rate: parseFloat(editingCluster.other_cost_rate) / 100,
        labor_cost_rate: editingCluster.labor_cost_rate ? parseFloat(editingCluster.labor_cost_rate) / 100 : null,
        commission_rate: editingCluster.commission_rate ? parseFloat(editingCluster.commission_rate) / 100 : null,
        contribution_margin_rate: editingCluster.contribution_margin_rate ? parseFloat(editingCluster.contribution_margin_rate) / 100 : null,
        description: editingCluster.description,
      };
      
      await api().put(`/admin/clusters/${editingCluster.id}`, updateData);
      
      // Update local state
      setClusters(clusters.map(c => 
        c.id === editingCluster.id 
          ? { ...c, ...updateData }
          : c
      ));
      
      setEditingCluster(null);
      toast.success(language === 'fr' ? 'Cluster mis à jour' : 'Cluster updated');
    } catch (error) {
      console.error('Failed to update cluster:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
    } finally {
      setSavingCluster(false);
    }
  };

  const handleResetClusters = async () => {
    if (!window.confirm(language === 'fr' 
      ? 'Réinitialiser tous les clusters aux valeurs par défaut ?' 
      : 'Reset all clusters to default values?')) {
      return;
    }
    
    try {
      const response = await api().post('/admin/clusters/reset');
      setClusters(response.data.clusters);
      toast.success(language === 'fr' ? 'Clusters réinitialisés' : 'Clusters reset');
    } catch (error) {
      console.error('Failed to reset clusters:', error);
      toast.error(t('error'));
    }
  };

  // ==================== LABOR RATES HANDLERS ====================
  const handleEditLaborRate = (rate) => {
    setEditingLaborRate({
      ...rate,
      weekly_hours: rate.weekly_hours.toFixed(1),
      hourly_rate: Math.abs(rate.hourly_rate).toFixed(2),
    });
  };

  const handleSaveLaborRate = async () => {
    if (!editingLaborRate) return;

    setSavingLaborRate(true);
    try {
      const updateData = {
        name: editingLaborRate.name,
        weekly_hours: parseFloat(editingLaborRate.weekly_hours),
        hourly_rate: -Math.abs(parseFloat(editingLaborRate.hourly_rate)), // Always negative (cost)
        description: editingLaborRate.description,
      };

      const response = await api().put(`/admin/labor-rates/${editingLaborRate.id}`, updateData);
      
      // Update local state
      setLaborRates(prev => prev.map(r => 
        r.id === editingLaborRate.id ? response.data : r
      ));
      
      setEditingLaborRate(null);
      toast.success(language === 'fr' ? 'Taux mis à jour' : 'Rate updated');
    } catch (error) {
      console.error('Failed to update labor rate:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Update failed');
    } finally {
      setSavingLaborRate(false);
    }
  };

  const handleResetLaborRates = async () => {
    if (!window.confirm(language === 'fr' 
      ? 'Réinitialiser tous les taux horaires aux valeurs par défaut ?' 
      : 'Reset all labor rates to default values?')) {
      return;
    }
    
    try {
      await api().post('/admin/labor-rates/reset');
      const response = await api().get('/admin/labor-rates');
      setLaborRates(response.data);
      toast.success(language === 'fr' ? 'Taux réinitialisés' : 'Rates reset');
    } catch (error) {
      console.error('Failed to reset labor rates:', error);
      toast.error(t('error'));
    }
  };

  const handleSaveCaPerEmployee = async () => {
    const v = parseFloat(caPerEmployeeInput);
    if (!v || v <= 0) {
      toast.error(language === 'fr' ? 'Valeur invalide' : 'Invalid value');
      return;
    }
    setSavingCaPerEmployee(true);
    try {
      const res = await api().put('/admin/config/labor', { ca_per_employee: v });
      setCaPerEmployee(res.data.ca_per_employee);
      setCaPerEmployeeInput(String(res.data.ca_per_employee));
      toast.success(language === 'fr' ? 'Seuil CA par salarié enregistré' : 'CA per employee threshold saved');
    } catch (error) {
      console.error('Failed to save CA per employee:', error);
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSavingCaPerEmployee(false);
    }
  };

  // Recalculate all simulations with current cluster values
  const handleRecalculateAllSimulations = async () => {
    if (!window.confirm(language === 'fr' 
      ? 'Recalculer TOUTES les simulations avec les valeurs actuelles des clusters ?\n\nCela mettra à jour les taux Food Cost et Other Cost de toutes les analyses.' 
      : 'Recalculate ALL simulations with current cluster values?\n\nThis will update Food Cost and Other Cost rates for all analyses.')) {
      return;
    }

    setRecalculatingAll(true);
    try {
      const response = await api().post('/admin/recalculate-all-simulations');
      const data = response.data;
      toast.success(
        language === 'fr' 
          ? `${data.updated_count}/${data.total_simulations} simulations recalculées avec succès !` 
          : `${data.updated_count}/${data.total_simulations} simulations recalculated successfully!`
      );
      if (data.errors?.length) {
        console.warn('Recalculation errors:', data.errors);
      }
    } catch (error) {
      console.error('Failed to recalculate simulations:', error);
      toast.error(language === 'fr' ? 'Erreur lors du recalcul' : 'Recalculation failed');
    } finally {
      setRecalculatingAll(false);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/clusters/import-excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Import failed');
      }
      
      setClusters(data.clusters);
      toast.success(language === 'fr' 
        ? `${data.clusters.length} clusters importés avec succès` 
        : `${data.clusters.length} clusters imported successfully`);
    } catch (error) {
      console.error('Failed to import Excel:', error);
      toast.error(language === 'fr' 
        ? `Erreur d'import: ${error.message}` 
        : `Import error: ${error.message}`);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/clusters/export-template`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clusters_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(language === 'fr' ? 'Template exporté' : 'Template exported');
    } catch (error) {
      console.error('Failed to export template:', error);
      toast.error(t('error'));
    }
  };

  // ========== EMAIL TEMPLATES FUNCTIONS ==========
  
  const fetchEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await api().get('/admin/email-templates');
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
      toast.error('Erreur lors du chargement des templates');
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await api().put(`/admin/email-templates/${editingTemplate.type}`, {
        subject: editingTemplate.subject,
        body: editingTemplate.body
      });
      toast.success('Template sauvegardé avec succès');
      await fetchEmailTemplates();
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingTemplate(false);
    }
  };
  
  const handleResetTemplate = async (templateType) => {
    if (!window.confirm('Réinitialiser ce template aux valeurs par défaut ?')) return;
    
    try {
      await api().delete(`/admin/email-templates/${templateType}`);
      toast.success('Template réinitialisé');
      await fetchEmailTemplates();
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const fetchClusterHistory = async () => {
    try {
      const response = await api().get('/admin/clusters/history');
      setClusterHistory(response.data);
      setShowHistory(true);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error(t('error'));
    }
  };

  const formatHistoryAction = (action) => {
    const actions = {
      'update': { label: language === 'fr' ? 'Modification' : 'Update', color: 'bg-blue-100 text-blue-800' },
      'create': { label: language === 'fr' ? 'Création' : 'Create', color: 'bg-green-100 text-green-800' },
      'delete': { label: language === 'fr' ? 'Suppression' : 'Delete', color: 'bg-red-100 text-red-800' },
      'reset': { label: language === 'fr' ? 'Réinitialisation' : 'Reset', color: 'bg-amber-100 text-amber-800' },
      'import': { label: language === 'fr' ? 'Import Excel' : 'Excel Import', color: 'bg-violet-100 text-violet-800' },
    };
    return actions[action] || { label: action, color: 'bg-slate-100 text-slate-800' };
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-slate-500">{language === 'fr' ? 'Accès réservé aux administrateurs' : 'Admin access required'}</p>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      </AppLayout>
    );
  }

  const dateLocale = language === 'fr' ? fr : enUS;

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="admin-page">
        {/* En-tête §6.7 */}
        <PageHeader overline="Système · Administration de la plateforme" title={t('adminDashboard')} />

        {/* Indicateurs — bloc scène graphite §6.7 */}
        {(() => {
          const totalU = users.length;
          const pendingU = users.filter(u => u.is_approved === false && u.approval_status === 'pending').length;
          const adminsU = users.filter(u => ['admin', 'super_admin'].includes(u.role)).length;
          const now = Date.now();
          const active30 = users.filter(u => u.last_login && (now - new Date(u.last_login).getTime()) <= 30 * 864e5).length;
          const adminStats = [
            { key: 'total', testid: 'admin-stat-users', label: language === 'fr' ? 'Utilisateurs' : 'Users', value: totalU, legend: language === 'fr' ? 'Total des comptes' : 'Total accounts' },
            { key: 'active30', testid: 'admin-stat-active', label: language === 'fr' ? 'Actifs (30 j)' : 'Active (30d)', value: active30, legend: language === 'fr' ? 'Connectés sous 30 jours' : 'Logged in last 30d' },
            { key: 'pending', testid: 'admin-stat-pending', label: language === 'fr' ? 'Comptes en attente' : 'Pending accounts', value: pendingU, legend: language === 'fr' ? 'À valider' : 'To approve', accent: pendingU > 0 },
            { key: 'admins', testid: 'admin-stat-admins', label: language === 'fr' ? 'Administrateurs' : 'Admins', value: adminsU, legend: language === 'fr' ? 'Rôles admin & super admin' : 'Admin & super admin' },
          ];
          return (
            <div className="rounded-scene bg-graphite-900 p-6 grid grid-cols-2 lg:grid-cols-4" data-testid="admin-scene">
              {adminStats.map((s, i) => (
                <div key={s.key} className={`px-5 ${i > 0 ? 'lg:border-l border-graphite-600' : ''}`} data-testid={s.testid}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ondark-3 mb-1.5">{s.label}</p>
                  <p className={`font-display font-semibold text-[40px] leading-none tabular-nums ${s.accent ? 'text-orange' : 'text-white'}`}>{s.value}</p>
                  <p className="text-[13px] text-ondark-2 mt-1.5">{s.legend}</p>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Tabs for Users, Clusters, and Emails */}
        <Tabs value={adminTab} onValueChange={setAdminTab} className="space-y-4">
          {/* Navigation : catégorie (segmenté) puis sous-onglets soulignés §6.7 */}
          <div className="space-y-3" data-testid="admin-nav">
            <div className="inline-flex flex-wrap items-center gap-0.5 rounded-ctrl bg-segmented p-1" data-testid="admin-nav-categories">
              {adminCategories.map(cat => {
                const activeCat = adminCat === cat.id;
                const CatIcon = cat.icon;
                return (
                  <button key={cat.id} type="button"
                    onClick={() => {
                      setAdminCat(cat.id);
                      const first = cat.tabs.find(isTabVisible);
                      if (first) selectAdminTab(first);
                    }}
                    className={`inline-flex items-center gap-2 px-4 h-9 rounded-[7px] text-[13px] font-semibold transition-colors ${activeCat
                      ? 'bg-graphite-900 text-white'
                      : 'text-text-2 hover:text-ink'}`}
                    data-testid={`admin-cat-${cat.id}`}>
                    <CatIcon className="w-4 h-4" /> {cat.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-4 border-b border-border" data-testid="admin-nav-tabs">
              {(adminCategories.find(c => c.id === adminCat)?.tabs || []).filter(isTabVisible).map(key => {
                const def = adminTabDefs[key];
                if (!def) return null;
                const TabIcon = def.icon;
                const activeTab = adminTab === key;
                return (
                  <button key={key} type="button"
                    onClick={() => selectAdminTab(key)}
                    className={`inline-flex items-center gap-1.5 pb-2 -mb-px border-b-2 text-[13px] font-medium transition-colors ${activeTab
                      ? 'border-graphite-900 text-ink'
                      : 'border-transparent text-text-3 hover:text-ink'}`}
                    data-testid={`tab-${key}`}>
                    <TabIcon className="w-3.5 h-3.5" /> {def.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {t('users')}
                    </CardTitle>
                    <CardDescription>
                      {language === 'fr' ? 'Gestion des utilisateurs et historique des connexions' : 'User management and login history'}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => fetchLoginHistory(null)}
                    disabled={loadingLoginHistory}
                    className="gap-2"
                  >
                    {loadingLoginHistory ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                    {language === 'fr' ? 'Historique des connexions' : 'Login History'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={openRoleAccess}
                    className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300"
                    data-testid="role-access-btn"
                  >
                    <Shield className="w-4 h-4" />
                    {language === 'fr' ? 'Accès par rôle' : 'Access by role'}
                  </Button>
                  <Button
                    onClick={() => setCreateUserModal(true)}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="create-user-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                    Créer un utilisateur
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('name')}</TableHead>
                        <TableHead>{t('email')}</TableHead>
                        <TableHead>{t('role')}</TableHead>
                        <TableHead>{language === 'fr' ? 'Visibilité' : 'Visibility'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Dernière connexion' : 'Last Login'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Organisation' : 'Organization'}</TableHead>
                        <TableHead className="text-center">{language === 'fr' ? 'Connexions' : 'Logins'}</TableHead>
                        <TableHead>{t('createdAt')}</TableHead>
                        {isSuperAdmin && <TableHead className="text-right">{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={!u.is_approved && u.approval_status === 'pending' ? 'bg-amber-50 dark:bg-amber-900/20' : ''}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <span>{u.name}</span>
                              {isSuperAdmin && (
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEditProfile(u)}
                                        title={language === 'fr' ? 'Modifier nom / email' : 'Edit name / email'}
                                        data-testid={`user-edit-profile-${u.id}`}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            {editingUserRole === u.id ? (
                              <div className="flex items-center gap-2">
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  defaultValue={u.role}
                                  onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                                >
                                  {roleOptions.map(role => (
                                    <option key={role} value={role} disabled={
                                      !isSuperAdmin && (role === 'admin' || role === 'super_admin')
                                    }>
                                      {role}
                                    </option>
                                  ))}
                                </select>
                                <Button size="sm" variant="ghost" onClick={() => setEditingUserRole(null)}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Badge className={
                                  ['super_admin', 'admin'].includes(u.role)
                                    ? 'bg-graphite-900 text-white'
                                    : 'bg-surface-2 text-[#2E2D2A]'
                                }>
                                  {u.role === 'super_admin' && <Shield className="w-3 h-3 mr-1" />}
                                  {u.role}
                                </Badge>
                                {isSuperAdmin && u.id !== user?.id && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    onClick={() => setEditingUserRole(u.id)}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Visibility Scope Column */}
                            {u.role === 'admin' || u.role === 'super_admin' ? (
                              <Badge variant="outline" className="bg-graphite-900 text-white border-transparent">
                                <Eye className="w-3 h-3 mr-1" />
                                {language === 'fr' ? 'Tous' : 'All'}
                              </Badge>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={
                                  u.visibility_scope === 'all'
                                    ? 'bg-graphite-900 text-white border-transparent'
                                    : u.visibility_scope === 'specific'
                                    ? 'bg-surface-2 text-[#2E2D2A] border-transparent'
                                    : 'bg-surface-2 text-[#2E2D2A] border-transparent'
                                }>
                                  <Eye className="w-3 h-3 mr-1" />
                                  {u.visibility_scope === 'all' 
                                    ? (language === 'fr' ? 'Tous' : 'All')
                                    : u.visibility_scope === 'specific'
                                    ? (language === 'fr' ? `Équipe (${u.visible_users?.length || 0})` : `Team (${u.visible_users?.length || 0})`)
                                    : (language === 'fr' ? 'Perso' : 'Own')
                                  }
                                </Badge>
                                {isSuperAdmin && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => openVisibilityConfig(u)}
                                    data-testid={`visibility-config-${u.id}`}
                                  >
                                    <Settings className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Approval Status Column */}
                            {u.is_approved === false && u.approval_status === 'pending' ? (
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-orange-tint text-orange-tint-text border-transparent">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'En attente' : 'Pending'}
                                </Badge>
                                {isSuperAdmin && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-orange-text hover:bg-surface-2"
                                      onClick={() => handleApproveUser(u.id, 'approve')}
                                      disabled={approvingUser === u.id}
                                      data-testid={`approve-user-${u.id}`}
                                    >
                                      {approvingUser === u.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <><Check className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Valider' : 'Approve'}</>
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-danger hover:bg-surface-2"
                                      onClick={() => handleApproveUser(u.id, 'reject')}
                                      disabled={approvingUser === u.id}
                                      data-testid={`reject-user-${u.id}`}
                                    >
                                      <X className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Refuser' : 'Reject'}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ) : u.approval_status === 'rejected' ? (
                              <Badge variant="outline" className="bg-surface-2 text-[#2E2D2A] border-transparent">
                                <XCircle className="w-3 h-3 mr-1" />
                                {language === 'fr' ? 'Refusé' : 'Rejected'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-surface border-border text-ink">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {language === 'fr' ? 'Actif' : 'Active'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.last_login 
                              ? format(new Date(u.last_login), 'dd/MM/yy HH:mm', { locale: dateLocale })
                              : <span className="text-slate-400">-</span>
                            }
                          </TableCell>
                          <TableCell>
                            {u.role === 'prestataire' ? (
                              assigningOrgUser === u.id ? (
                                <select
                                  className="border rounded px-2 py-1 text-sm"
                                  defaultValue={u.organisation_id || ''}
                                  onChange={(e) => handleAssignOrg(u.id, e.target.value || null)}
                                  onBlur={() => setAssigningOrgUser(null)}
                                  autoFocus
                                >
                                  <option value="">— Aucune —</option>
                                  {organisations.map(o => (
                                    <option key={o.id} value={o.id}>{o.nom}</option>
                                  ))}
                                </select>
                              ) : (
                                <div className="flex items-center gap-1">
                                  {u.organisation_name ? (
                                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                      <HardHat className="w-3 h-3 mr-1" />{u.organisation_name}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-slate-400">Non affecté</span>
                                  )}
                                  {isSuperAdmin && (
                                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                                      onClick={() => { fetchOrganisations(); setAssigningOrgUser(u.id); }}
                                    ><Pencil className="w-3 h-3" /></Button>
                                  )}
                                </div>
                              )
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant="outline"
                              className="cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                              onClick={() => fetchLoginHistory(u)}
                              data-testid={`login-count-${u.id}`}
                            >
                              {u.login_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {format(new Date(u.created_at), 'dd/MM/yyyy', { locale: dateLocale })}
                          </TableCell>
                          {isSuperAdmin && (
                            <TableCell className="text-right">
                              {u.id !== user?.id && u.role !== 'super_admin' && (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 gap-1"
                                    onClick={async () => {
                                      if (!window.confirm(`Envoyer un email de réinitialisation à ${u.email} ?`)) return;
                                      try {
                                        const res = await api().post(`/admin/reset-user-password/${u.id}`);
                                        toast.success(res.data.message);
                                      } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                                    }}
                                    data-testid={`reset-pwd-${u.id}`}
                                  >
                                    <KeyRound className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setDeletingUser(u)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Login History Modal */}
                {showLoginHistory && (() => {
                  // Filter login history by search
                  const searchLower = loginHistorySearch.toLowerCase().trim();
                  const filteredHistory = searchLower
                    ? loginHistory.filter(log =>
                        (log.user_name || '').toLowerCase().includes(searchLower) ||
                        (log.user_email || '').toLowerCase().includes(searchLower) ||
                        (log.user_role || '').toLowerCase().includes(searchLower)
                      )
                    : loginHistory;

                  // Compute stats for per-user view
                  const MONTH_NAMES_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
                  const monthlyStats = {};
                  const yearlyStats = {};
                  const dailyGroups = {};

                  filteredHistory.forEach(log => {
                    const d = new Date(log.timestamp);
                    const yearKey = d.getFullYear().toString();
                    const monthKey = `${yearKey}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    const dayKey = format(d, 'dd/MM/yyyy');

                    yearlyStats[yearKey] = (yearlyStats[yearKey] || 0) + 1;
                    monthlyStats[monthKey] = (monthlyStats[monthKey] || { label: `${MONTH_NAMES_FR[d.getMonth()]} ${yearKey}`, count: 0 });
                    monthlyStats[monthKey].count += 1;
                    if (!dailyGroups[dayKey]) dailyGroups[dayKey] = [];
                    dailyGroups[dayKey].push(log);
                  });

                  const sortedMonths = Object.entries(monthlyStats).sort((a, b) => b[0].localeCompare(a[0]));
                  const sortedYears = Object.entries(yearlyStats).sort((a, b) => b[0].localeCompare(a[0]));
                  const sortedDays = Object.entries(dailyGroups).sort((a, b) => {
                    const [dA, mA, yA] = a[0].split('/');
                    const [dB, mB, yB] = b[0].split('/');
                    return new Date(yB, mB - 1, dB) - new Date(yA, mA - 1, dA);
                  });

                  return (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between p-4 border-b dark:border-slate-700">
                        <div>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <LogIn className="w-5 h-5" />
                            {selectedUserHistory
                              ? `Connexions — ${selectedUserHistory.name}`
                              : (language === 'fr' ? 'Historique des connexions' : 'Login History')}
                          </h3>
                          {selectedUserHistory && (
                            <p className="text-sm text-slate-500 mt-0.5">
                              {selectedUserHistory.email} · <Badge variant="outline" className="text-[10px]">{selectedUserHistory.role}</Badge> · {loginHistory.length} connexion(s) au total
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedUserHistory && (
                            <Button variant="outline" size="sm" onClick={() => { setLoginHistorySearch(''); fetchLoginHistory(null); }} data-testid="view-all-logins">
                              Voir tout
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => { setShowLoginHistory(false); setSelectedUserHistory(null); setLoginHistorySearch(''); }}>
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Search bar */}
                      <div className="px-4 pt-3 pb-2 border-b dark:border-slate-700">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            value={loginHistorySearch}
                            onChange={(e) => setLoginHistorySearch(e.target.value)}
                            placeholder="Rechercher par nom, email ou rôle..."
                            className="w-full pl-9 pr-9 py-2 text-sm border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-all"
                            data-testid="login-history-search"
                          />
                          {loginHistorySearch && (
                            <button onClick={() => setLoginHistorySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {searchLower && (
                          <p className="text-xs text-slate-500 mt-1.5">{filteredHistory.length} résultat{filteredHistory.length !== 1 ? 's' : ''} pour "{loginHistorySearch}"</p>
                        )}
                      </div>

                      <div className="overflow-y-auto flex-1 p-4">
                        {/* Stats section for per-user view */}
                        {selectedUserHistory && filteredHistory.length > 0 && (
                          <div className="mb-4 space-y-3">
                            {/* Yearly stats */}
                            <div className="flex flex-wrap gap-2">
                              {sortedYears.map(([year, count]) => (
                                <div key={year} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-center" data-testid={`yearly-stat-${year}`}>
                                  <p className="text-lg font-bold text-blue-700">{count}</p>
                                  <p className="text-[10px] text-blue-500 font-medium">{year}</p>
                                </div>
                              ))}
                            </div>
                            {/* Monthly stats */}
                            <div className="flex flex-wrap gap-1.5">
                              {sortedMonths.map(([key, { label, count }]) => (
                                <div key={key} className="bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 flex items-center gap-2" data-testid={`monthly-stat-${key}`}>
                                  <span className="text-xs text-slate-600">{label}</span>
                                  <Badge variant="secondary" className="text-[10px] font-bold">{count}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Grouped by date view for per-user, flat table for global */}
                        {selectedUserHistory ? (
                          <div className="space-y-1">
                            {sortedDays.map(([dayKey, logs]) => (
                              <LoginDayGroup key={dayKey} dayKey={dayKey} logs={logs} />
                            ))}
                          </div>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Heure</TableHead>
                                <TableHead>{t('name')}</TableHead>
                                <TableHead>{t('email')}</TableHead>
                                <TableHead>{t('role')}</TableHead>
                                <TableHead>IP</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredHistory.map((log) => (
                                <TableRow key={log.id} data-testid={`login-entry-${log.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => { setLoginHistorySearch(''); fetchLoginHistory({ id: log.user_id, name: log.user_name, email: log.user_email, role: log.user_role }); }}>
                                  <TableCell className="whitespace-nowrap font-medium">
                                    {format(new Date(log.timestamp), 'dd/MM/yyyy', { locale: dateLocale })}
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-blue-600 font-mono text-sm">
                                    {format(new Date(log.timestamp), 'HH:mm:ss', { locale: dateLocale })}
                                  </TableCell>
                                  <TableCell className="font-medium">{log.user_name}</TableCell>
                                  <TableCell>{log.user_email}</TableCell>
                                  <TableCell><Badge variant="secondary">{log.user_role}</Badge></TableCell>
                                  <TableCell className="text-slate-500 text-xs">{log.ip_address}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}

                        {filteredHistory.length === 0 && (
                          <p className="text-center text-slate-500 py-8">
                            {searchLower ? 'Aucun résultat pour cette recherche' : (language === 'fr' ? 'Aucun historique de connexion' : 'No login history')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {/* Delete User Confirmation Modal */}
                {deletingUser && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full p-6">
                      <div className="flex items-center gap-3 text-red-600 mb-4">
                        <Trash2 className="w-6 h-6" />
                        <h3 className="text-lg font-semibold">
                          {language === 'fr' ? 'Confirmer la suppression' : 'Confirm Deletion'}
                        </h3>
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 mb-6">
                        {language === 'fr' 
                          ? `Êtes-vous sûr de vouloir supprimer l'utilisateur "${deletingUser.name}" (${deletingUser.email}) ? Cette action est irréversible.`
                          : `Are you sure you want to delete user "${deletingUser.name}" (${deletingUser.email})? This action cannot be undone.`
                        }
                      </p>
                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setDeletingUser(null)}>
                          {language === 'fr' ? 'Annuler' : 'Cancel'}
                        </Button>
                        <Button variant="destructive" onClick={() => handleDeleteUser(deletingUser.id)}>
                          <Trash2 className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Supprimer' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Visibility Configuration Modal */}
                {configuringVisibility && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                              <Eye className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold">
                                {language === 'fr' ? 'Configuration des droits de visibilité' : 'Visibility Rights Configuration'}
                              </h3>
                              <p className="text-sm text-slate-500">
                                {configuringVisibility.name} ({configuringVisibility.role})
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setConfiguringVisibility(null)}>
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="p-6 overflow-y-auto max-h-[55vh]">
                        {loadingVisibility ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {/* Visibility Scope Selection */}
                            <div className="space-y-3">
                              <Label className="text-base font-medium">
                                {language === 'fr' ? 'Portée de la visibilité' : 'Visibility Scope'}
                              </Label>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div 
                                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                    visibilityScope === 'own' 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                  onClick={() => setVisibilityScope('own')}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      visibilityScope === 'own' ? 'border-blue-500' : 'border-slate-300'
                                    }`}>
                                      {visibilityScope === 'own' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                                    </div>
                                    <span className="font-medium">{language === 'fr' ? 'Perso' : 'Own'}</span>
                                  </div>
                                  <p className="text-xs text-slate-500">
                                    {language === 'fr' 
                                      ? 'Uniquement ses propres simulations' 
                                      : 'Only their own simulations'}
                                  </p>
                                </div>
                                
                                <div 
                                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                    visibilityScope === 'specific' 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                  onClick={() => setVisibilityScope('specific')}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      visibilityScope === 'specific' ? 'border-blue-500' : 'border-slate-300'
                                    }`}>
                                      {visibilityScope === 'specific' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                                    </div>
                                    <span className="font-medium">{language === 'fr' ? 'Équipe' : 'Team'}</span>
                                  </div>
                                  <p className="text-xs text-slate-500">
                                    {language === 'fr' 
                                      ? 'Ses simulations + utilisateurs sélectionnés' 
                                      : 'Own + selected users simulations'}
                                  </p>
                                </div>
                                
                                <div 
                                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                    visibilityScope === 'all' 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                  onClick={() => setVisibilityScope('all')}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      visibilityScope === 'all' ? 'border-blue-500' : 'border-slate-300'
                                    }`}>
                                      {visibilityScope === 'all' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                                    </div>
                                    <span className="font-medium">{language === 'fr' ? 'Tous' : 'All'}</span>
                                  </div>
                                  <p className="text-xs text-slate-500">
                                    {language === 'fr' 
                                      ? 'Toutes les simulations de la plateforme' 
                                      : 'All simulations on the platform'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* User Selection (only shown when scope is 'specific') */}
                            {visibilityScope === 'specific' && (
                              <div className="space-y-3">
                                <Label className="text-base font-medium">
                                  {language === 'fr' 
                                    ? `Utilisateurs visibles (${selectedVisibleUsers.length} sélectionné${selectedVisibleUsers.length > 1 ? 's' : ''})` 
                                    : `Visible Users (${selectedVisibleUsers.length} selected)`}
                                </Label>
                                <p className="text-sm text-slate-500">
                                  {language === 'fr' 
                                    ? 'Sélectionnez les utilisateurs dont cet utilisateur pourra voir les simulations' 
                                    : 'Select users whose simulations this user will be able to see'}
                                </p>
                                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                                  {allUsersList.length === 0 ? (
                                    <p className="text-center text-slate-500 py-4">
                                      {language === 'fr' ? 'Aucun utilisateur disponible' : 'No users available'}
                                    </p>
                                  ) : (
                                    allUsersList.map(u => (
                                      <div 
                                        key={u.id}
                                        className={`p-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer ${
                                          selectedVisibleUsers.includes(u.id) ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => toggleVisibleUser(u.id)}
                                      >
                                        <div className="flex items-center gap-3">
                                          <Checkbox 
                                            checked={selectedVisibleUsers.includes(u.id)}
                                            onCheckedChange={() => toggleVisibleUser(u.id)}
                                          />
                                          <div>
                                            <p className="font-medium text-sm">{u.name}</p>
                                            <p className="text-xs text-slate-500">{u.email}</p>
                                          </div>
                                        </div>
                                        <Badge variant="secondary" className="text-xs">
                                          {u.role}
                                        </Badge>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setConfiguringVisibility(null)}>
                          {language === 'fr' ? 'Annuler' : 'Cancel'}
                        </Button>
                        <Button 
                          onClick={handleSaveVisibility}
                          disabled={savingVisibility || (visibilityScope === 'specific' && selectedVisibleUsers.length === 0)}
                        >
                          {savingVisibility ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'fr' ? 'Enregistrement...' : 'Saving...'}</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> {language === 'fr' ? 'Enregistrer' : 'Save'}</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Create User Modal */}
            <Dialog open={createUserModal} onOpenChange={setCreateUserModal}>
              <DialogContent className="max-w-md" data-testid="create-user-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-emerald-600" />
                    Créer un utilisateur
                  </DialogTitle>
                  <DialogDescription>
                    L'utilisateur sera créé et activé immédiatement (pas d'approbation nécessaire).
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Nom complet *</Label>
                    <Input value={newUserForm.name} onChange={e => setNewUserForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Prénom Nom" data-testid="new-user-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Email *</Label>
                    <Input type="email" value={newUserForm.email} onChange={e => setNewUserForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="email@exemple.fr" data-testid="new-user-email" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Mot de passe *</Label>
                    <Input type="password" value={newUserForm.password} onChange={e => setNewUserForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="Minimum 6 caractères" data-testid="new-user-password" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Rôle</Label>
                    <select value={newUserForm.role} onChange={e => setNewUserForm(p => ({ ...p, role: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="new-user-role">
                      {roleOptions.filter(r => isSuperAdmin || !['super_admin', 'admin'].includes(r)).map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  {organisations.length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-sm">Organisation (optionnel)</Label>
                      <select value={newUserForm.organisation_id} onChange={e => setNewUserForm(p => ({ ...p, organisation_id: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="new-user-org">
                        <option value="">Aucune</option>
                        {organisations.map(o => (
                          <option key={o.id} value={o.id}>{o.nom}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setCreateUserModal(false)}>Annuler</Button>
                  <Button onClick={handleCreateUser} disabled={creatingUser} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                    data-testid="confirm-create-user-btn">
                    {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Créer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Modale — Accès par rôle */}
            <Dialog open={roleAccessOpen} onOpenChange={setRoleAccessOpen}>
              <DialogContent className="max-w-lg" data-testid="role-access-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-indigo-600" />{language === 'fr' ? 'Accès par rôle' : 'Access by role'}</DialogTitle>
                  <DialogDescription>
                    {language === 'fr'
                      ? "Définissez la visibilité des sections du menu pour chaque rôle. Ce réglage s'applique à TOUS les utilisateurs du rôle et fait foi (il n'y a plus d'accès individuel par utilisateur)."
                      : 'Set menu section visibility per role. This applies to ALL users of the role and is authoritative (no more per-user access).'}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-slate-500">{language === 'fr' ? 'Rôle' : 'Role'}</span>
                  <select
                    value={roleAccessSelected}
                    onChange={(e) => setRoleAccessSelected(e.target.value)}
                    className="flex-1 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                    data-testid="role-access-select">
                    {roleOptions.filter(r => r !== 'super_admin').map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 -mx-1">
                  {SECTION_DEFS.map(s => (
                    <label key={s.to} className="flex items-center justify-between gap-3 px-1 py-2.5 cursor-pointer"
                      data-testid={`role-access-row-${s.to.replace(/^\//, '')}`}>
                      <span className="text-sm text-slate-700 dark:text-slate-200">{s.label}</span>
                      <Switch
                        checked={roleAccessValue(roleAccessSelected, s.to)}
                        onCheckedChange={(v) => setRoleAccessValue(roleAccessSelected, s.to, v)}
                        data-testid={`role-access-switch-${s.to.replace(/^\//, '')}`} />
                    </label>
                  ))}
                </div>
                <DialogFooter>
                  <div className="flex items-center justify-between w-full gap-2">
                    <button type="button"
                      onClick={() => resetRoleAccess(roleAccessSelected)}
                      className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                      data-testid="role-access-reset">
                      {language === 'fr' ? 'Réinitialiser (défaut historique)' : 'Reset to default'}
                    </button>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setRoleAccessOpen(false)}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
                      <Button onClick={saveRoleAccess} disabled={savingRoleAccess} data-testid="role-access-save">
                        {savingRoleAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'fr' ? 'Enregistrer' : 'Save')}
                      </Button>
                    </div>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>



            <Dialog open={!!editProfileUser} onOpenChange={(o) => !o && setEditProfileUser(null)}>
              <DialogContent className="max-w-md" data-testid="edit-profile-modal">
                <DialogHeader>
                  <DialogTitle>{language === 'fr' ? 'Modifier la fiche profil' : 'Edit profile'}</DialogTitle>
                  <DialogDescription>
                    {language === 'fr' ? 'Corrigez le nom/prénom et l\'email de l\'utilisateur (typographie, casse, etc.).' : 'Edit the user name and email.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-sm">{language === 'fr' ? 'Nom et prénom' : 'Full name'}</Label>
                    <Input value={editProfileForm.name}
                      onChange={(e) => setEditProfileForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Ex. Jean Dupont" className="mt-1" data-testid="edit-profile-name" />
                  </div>
                  <div>
                    <Label className="text-sm">Email</Label>
                    <Input type="email" value={editProfileForm.email}
                      onChange={(e) => setEditProfileForm(p => ({ ...p, email: e.target.value }))}
                      className="mt-1" data-testid="edit-profile-email" />
                    <p className="text-xs text-slate-400 mt-1">{language === 'fr' ? 'L\'email sert d\'identifiant de connexion.' : 'Email is the login identifier.'}</p>
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setEditProfileUser(null)}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
                  <Button onClick={handleSaveProfile} disabled={savingProfile} className="gap-1" data-testid="confirm-edit-profile-btn">
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {language === 'fr' ? 'Enregistrer' : 'Save'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
          {/* Historique des connexions — vue synthétique (admin/super_admin) */}
          <TabsContent value="login-history">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <LogIn className="w-5 h-5 text-indigo-600" />
                      {language === 'fr' ? 'Historique des connexions' : 'Login history'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'fr'
                        ? 'Dernière connexion et nombre de connexions par utilisateur. Cliquez sur une ligne pour le détail (dates, heures, IP).'
                        : 'Last login and login count per user. Click a row for details.'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <Input value={connSearch} onChange={(e) => setConnSearch(e.target.value)}
                        placeholder={language === 'fr' ? 'Rechercher…' : 'Search…'}
                        className="pl-8 h-9 w-48" data-testid="conn-search" />
                    </div>
                    <select value={connSort} onChange={(e) => setConnSort(e.target.value)}
                      className="h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                      data-testid="conn-sort">
                      <option value="last_login">{language === 'fr' ? 'Trier : dernière connexion' : 'Sort: last login'}</option>
                      <option value="count_desc">{language === 'fr' ? 'Trier : + de connexions' : 'Sort: most logins'}</option>
                      <option value="count_asc">{language === 'fr' ? 'Trier : - de connexions' : 'Sort: fewest logins'}</option>
                      <option value="name">{language === 'fr' ? 'Trier : nom' : 'Sort: name'}</option>
                    </select>
                    <Button variant="outline" className="h-9 gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300"
                      onClick={exportConnectionsCsv} data-testid="conn-export">
                      <FileSpreadsheet className="w-4 h-4" /> {language === 'fr' ? 'Exporter Excel' : 'Export Excel'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const q = connSearch.toLowerCase().trim();
                  let rows = users.filter(u => !q || [u.name, u.email, u.role].some(v => (v || '').toLowerCase().includes(q)));
                  rows = [...rows].sort((a, b) => {
                    if (connSort === 'count_desc') return (b.login_count || 0) - (a.login_count || 0);
                    if (connSort === 'count_asc') return (a.login_count || 0) - (b.login_count || 0);
                    if (connSort === 'name') return (a.name || '').localeCompare(b.name || '');
                    const ta = a.last_login ? new Date(a.last_login).getTime() : 0;
                    const tb = b.last_login ? new Date(b.last_login).getTime() : 0;
                    return tb - ta;
                  });
                  const totalConn = users.reduce((s, u) => s + (u.login_count || 0), 0);
                  const neverLogged = users.filter(u => !u.last_login).length;
                  return (
                    <>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                          <p className="text-xs uppercase text-slate-400">{language === 'fr' ? 'Connexions totales' : 'Total logins'}</p>
                          <p className="text-xl font-heading font-bold text-slate-900 dark:text-white">{totalConn}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                          <p className="text-xs uppercase text-slate-400">{language === 'fr' ? 'Utilisateurs' : 'Users'}</p>
                          <p className="text-xl font-heading font-bold text-slate-900 dark:text-white">{users.length}</p>
                        </div>
                        <div className="rounded-xl border border-amber-200 dark:border-amber-800 p-3">
                          <p className="text-xs uppercase text-amber-500">{language === 'fr' ? 'Jamais connectés' : 'Never logged in'}</p>
                          <p className="text-xl font-heading font-bold text-amber-600 dark:text-amber-400">{neverLogged}</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                              <TableHead className="hidden sm:table-cell">Email</TableHead>
                              <TableHead>{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                              <TableHead>{language === 'fr' ? 'Dernière connexion' : 'Last login'}</TableHead>
                              <TableHead className="text-right">{language === 'fr' ? 'Connexions' : 'Logins'}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map(u => (
                              <TableRow key={u.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                onClick={() => fetchLoginHistory(u)} data-testid={`conn-row-${u.id}`}>
                                <TableCell className="font-medium">{u.name}</TableCell>
                                <TableCell className="hidden sm:table-cell text-slate-500">{u.email}</TableCell>
                                <TableCell><Badge variant="secondary" className="text-[10px]">{u.role}</Badge></TableCell>
                                <TableCell className={u.last_login ? '' : 'text-amber-600 dark:text-amber-400'}>
                                  {u.last_login ? format(new Date(u.last_login), 'dd/MM/yy HH:mm', { locale: dateLocale }) : (language === 'fr' ? 'Jamais' : 'Never')}
                                </TableCell>
                                <TableCell className="text-right font-mono tabular-nums">{u.login_count || 0}</TableCell>
                              </TableRow>
                            ))}
                            {rows.length === 0 && (
                              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">{language === 'fr' ? 'Aucun utilisateur' : 'No users'}</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clusters">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-heading">
                      {language === 'fr' ? 'Configuration des Clusters (Données Excel)' : 'Cluster Configuration (Excel Data)'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'fr' 
                        ? 'Définissez tous les taux par tranche de CA mensuel - Ces données proviennent du fichier Excel CCD PL Corners' 
                        : 'Define all rates by monthly revenue bracket - This data comes from the CCD PL Corners Excel file'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".xlsx,.xls"
                      onChange={handleImportExcel}
                      className="hidden"
                      data-testid="excel-file-input"
                    />
                    
                    {/* Import Excel Button */}
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={importing}
                      className="gap-2"
                      data-testid="import-excel-btn"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {language === 'fr' ? 'Importer Excel' : 'Import Excel'}
                    </Button>
                    
                    {/* Export Template Button */}
                    <Button 
                      variant="outline" 
                      onClick={handleExportTemplate}
                      className="gap-2"
                      data-testid="export-template-btn"
                    >
                      <Download className="w-4 h-4" />
                      {language === 'fr' ? 'Exporter Template' : 'Export Template'}
                    </Button>
                    
                    {/* Reset Button */}
                    <Button variant="outline" onClick={handleResetClusters} className="gap-2">
                      <RotateCcw className="w-4 h-4" />
                      {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </Button>
                    
                    {/* Recalculate All Simulations Button */}
                    {isSuperAdmin && (
                      <Button 
                        variant="default" 
                        onClick={handleRecalculateAllSimulations}
                        disabled={recalculatingAll}
                        className="gap-2 bg-blue-600 hover:bg-blue-700"
                      >
                        {recalculatingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        {language === 'fr' ? 'Recalculer Toutes les Analyses' : 'Recalculate All Simulations'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">{language === 'fr' ? 'Cluster' : 'Cluster'}</TableHead>
                        <TableHead className="min-w-[120px]">{language === 'fr' ? 'ARPC Annuel' : 'Annual ARPC'}</TableHead>
                        <TableHead className="text-right min-w-[100px]">{language === 'fr' ? 'CA Mensuel' : 'Monthly CA'}</TableHead>
                        <TableHead className="text-right min-w-[80px] text-red-600">Food %</TableHead>
                        <TableHead className="text-right min-w-[80px] text-blue-600">Labor %</TableHead>
                        <TableHead className="text-right min-w-[80px] text-amber-600">Other %</TableHead>
                        <TableHead className="text-right min-w-[80px] text-violet-600">Comm. %</TableHead>
                        <TableHead className="text-right min-w-[80px] text-emerald-600">Marge %</TableHead>
                        <TableHead className="text-center min-w-[60px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clusters.map((cluster) => (
                        <TableRow key={cluster.id || cluster.name} data-testid={`cluster-row-${cluster.id || cluster.name}`}>
                          {editingCluster?.id === cluster.id ? (
                            <>
                              <TableCell>
                                <Input
                                  value={editingCluster.name}
                                  onChange={(e) => setEditingCluster({...editingCluster, name: e.target.value})}
                                  className="h-8 text-sm w-24"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={editingCluster.arpc_label || ''}
                                  onChange={(e) => setEditingCluster({...editingCluster, arpc_label: e.target.value})}
                                  className="h-8 text-sm w-28"
                                  placeholder="ex: 180-360 K€"
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-xs">
                                  <Input
                                    type="number"
                                    value={editingCluster.min_ca || editingCluster.ca_min || 0}
                                    onChange={(e) => setEditingCluster({...editingCluster, min_ca: e.target.value})}
                                    className="h-7 text-right font-mono w-16 text-xs"
                                  />
                                  <span>-</span>
                                  <Input
                                    type="number"
                                    value={(editingCluster.max_ca || editingCluster.ca_max) >= 999999999 ? '' : (editingCluster.max_ca || editingCluster.ca_max)}
                                    onChange={(e) => setEditingCluster({...editingCluster, max_ca: e.target.value || 999999999})}
                                    className="h-7 text-right font-mono w-16 text-xs"
                                    placeholder="∞"
                                  />
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={editingCluster.food_cost_rate}
                                    onChange={(e) => setEditingCluster({...editingCluster, food_cost_rate: e.target.value})}
                                    className="h-8 text-right font-mono w-14"
                                  />
                                  <span className="text-xs">%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={editingCluster.labor_cost_rate || ''}
                                    onChange={(e) => setEditingCluster({...editingCluster, labor_cost_rate: e.target.value})}
                                    className="h-8 text-right font-mono w-16"
                                  />
                                  <span className="text-xs">%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={editingCluster.other_cost_rate}
                                    onChange={(e) => setEditingCluster({...editingCluster, other_cost_rate: e.target.value})}
                                    className="h-8 text-right font-mono w-16"
                                  />
                                  <span className="text-xs">%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={editingCluster.commission_rate || ''}
                                    onChange={(e) => setEditingCluster({...editingCluster, commission_rate: e.target.value})}
                                    className="h-8 text-right font-mono w-16"
                                  />
                                  <span className="text-xs">%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={editingCluster.contribution_margin_rate || ''}
                                    onChange={(e) => setEditingCluster({...editingCluster, contribution_margin_rate: e.target.value})}
                                    className="h-8 text-right font-mono w-16"
                                  />
                                  <span className="text-xs">%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-center gap-1">
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    onClick={handleSaveCluster}
                                    disabled={savingCluster}
                                    className="h-8 w-8 text-emerald-600 hover:text-emerald-700"
                                  >
                                    {savingCluster ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                  </Button>
                                  <Button 
                                    size="icon" 
                                    variant="ghost" 
                                    onClick={() => setEditingCluster(null)}
                                    className="h-8 w-8 text-red-600 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="font-medium text-sm">{cluster.name}</TableCell>
                              <TableCell className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                {cluster.arpc_label || '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs text-slate-500">
                                {(cluster.min_ca || cluster.ca_min || 0).toLocaleString('fr-FR')} - {(cluster.max_ca || cluster.ca_max) >= 999999999 ? '∞' : `${(cluster.max_ca || cluster.ca_max || 0).toLocaleString('fr-FR')}`} €
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-red-600">
                                {formatPercent(cluster.food_cost_rate)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-blue-600">
                                {cluster.labor_cost_rate ? formatPercent(cluster.labor_cost_rate) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-amber-600">
                                {formatPercent(cluster.other_cost_rate)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-violet-600">
                                {cluster.commission_rate ? formatPercent(cluster.commission_rate) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-emerald-600">
                                {cluster.contribution_margin_rate ? formatPercent(cluster.contribution_margin_rate) : '-'}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => handleEditCluster(cluster)}
                                  className="h-8 w-8"
                                  data-testid={`edit-cluster-${cluster.id || cluster.name}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                <div className="mt-4 flex items-center justify-between flex-wrap gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-1">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>{language === 'fr' ? 'Note:' : 'Note:'}</strong>{' '}
                      {language === 'fr' 
                        ? 'Les taux sont automatiquement appliqués lors de la création d\'une simulation en fonction du CA mensuel prévisionnel.'
                        : 'Rates are automatically applied when creating a simulation based on the projected monthly revenue.'}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={fetchClusterHistory}
                    className="gap-2"
                    data-testid="show-history-btn"
                  >
                    <History className="w-4 h-4" />
                    {language === 'fr' ? 'Voir l\'historique' : 'View History'}
                  </Button>
                </div>

                {/* History Section */}
                {showHistory && (
                  <div className="mt-6 border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-lg flex items-center gap-2">
                        <History className="w-5 h-5" />
                        {language === 'fr' ? 'Historique des modifications' : 'Modification History'}
                      </h4>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowHistory(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {clusterHistory.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">
                        {language === 'fr' ? 'Aucun historique disponible' : 'No history available'}
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {clusterHistory.map((entry) => {
                          const actionInfo = formatHistoryAction(entry.action);
                          return (
                            <div 
                              key={entry.id} 
                              className="p-3 border rounded-lg bg-slate-50 dark:bg-slate-800/50"
                              data-testid={`history-entry-${entry.id}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${actionInfo.color}`}>
                                    {actionInfo.label}
                                  </span>
                                  <span className="font-medium text-sm">{entry.cluster_name}</span>
                                </div>
                                <span className="text-xs text-slate-500">
                                  {format(new Date(entry.modified_at), 'dd/MM/yyyy HH:mm', { locale: dateLocale })}
                                </span>
                              </div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">
                                <span className="font-medium">{language === 'fr' ? 'Par:' : 'By:'}</span> {entry.modified_by}
                              </div>
                              {entry.changes && Object.keys(entry.changes).length > 0 && (
                                <div className="mt-2 text-xs">
                                  <span className="font-medium text-slate-700 dark:text-slate-300">
                                    {language === 'fr' ? 'Changements:' : 'Changes:'}
                                  </span>
                                  <div className="mt-1 grid grid-cols-2 gap-1">
                                    {Object.entries(entry.changes).map(([field, change]) => (
                                      <div key={field} className="bg-white dark:bg-slate-900 p-1.5 rounded">
                                        <span className="font-medium">{field}:</span>{' '}
                                        {typeof change === 'object' && change.old !== undefined ? (
                                          <>
                                            <span className="text-red-500 line-through">
                                              {typeof change.old === 'number' ? (change.old * 100).toFixed(2) + '%' : change.old}
                                            </span>
                                            {' → '}
                                            <span className="text-emerald-600">
                                              {typeof change.new === 'number' ? (change.new * 100).toFixed(2) + '%' : change.new}
                                            </span>
                                          </>
                                        ) : (
                                          <span>{JSON.stringify(change)}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Labor Rates Configuration Card */}
            <Card className="border border-slate-200 dark:border-slate-800 mt-6">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {language === 'fr' ? 'Configuration des Taux Horaires' : 'Labor Rates Configuration'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'fr' 
                        ? 'Définissez les heures et taux horaires par rôle - L\'utilisateur ne saisit que le nombre de personnes' 
                        : 'Define weekly hours and hourly rates by role - User only enters number of people'}
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={handleResetLaborRates} className="gap-2">
                    <RotateCcw className="w-4 h-4" />
                    {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[150px]">{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                        <TableHead className="text-right min-w-[120px]">{language === 'fr' ? 'Heures/Semaine' : 'Hours/Week'}</TableHead>
                        <TableHead className="text-right min-w-[120px]">{language === 'fr' ? 'Taux Horaire (€)' : 'Hourly Rate (€)'}</TableHead>
                        <TableHead className="text-right min-w-[150px]">{language === 'fr' ? 'Coût Hebdo.' : 'Weekly Cost'}</TableHead>
                        <TableHead className="text-right min-w-[150px]">{language === 'fr' ? 'Coût Annuel' : 'Annual Cost'}</TableHead>
                        <TableHead className="text-center min-w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {laborRates.map((rate) => {
                        const weeklyCost = rate.weekly_hours * Math.abs(rate.hourly_rate);
                        const annualCost = weeklyCost * 52;
                        
                        return (
                          <TableRow key={rate.id} data-testid={`labor-rate-row-${rate.id}`}>
                            {editingLaborRate?.id === rate.id ? (
                              <>
                                <TableCell>
                                  <Input
                                    value={editingLaborRate.name}
                                    onChange={(e) => setEditingLaborRate({...editingLaborRate, name: e.target.value})}
                                    className="h-8 text-sm"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    step="0.5"
                                    value={editingLaborRate.weekly_hours}
                                    onChange={(e) => setEditingLaborRate({...editingLaborRate, weekly_hours: e.target.value})}
                                    className="h-8 text-sm w-20 text-right"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editingLaborRate.hourly_rate}
                                    onChange={(e) => setEditingLaborRate({...editingLaborRate, hourly_rate: e.target.value})}
                                    className="h-8 text-sm w-24 text-right"
                                  />
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-red-600">
                                  -{(parseFloat(editingLaborRate.weekly_hours || 0) * parseFloat(editingLaborRate.hourly_rate || 0)).toFixed(2)} €
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-red-600">
                                  -{(parseFloat(editingLaborRate.weekly_hours || 0) * parseFloat(editingLaborRate.hourly_rate || 0) * 52).toFixed(0)} €
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex justify-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={handleSaveLaborRate}
                                      disabled={savingLaborRate}
                                      className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    >
                                      {savingLaborRate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setEditingLaborRate(null)}
                                      className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell className="font-medium">{rate.name}</TableCell>
                                <TableCell className="text-right font-mono">{rate.weekly_hours}h</TableCell>
                                <TableCell className="text-right font-mono text-red-600">{rate.hourly_rate.toFixed(2)} €</TableCell>
                                <TableCell className="text-right font-mono text-red-600">-{weeklyCost.toFixed(2)} €</TableCell>
                                <TableCell className="text-right font-mono text-red-600">-{annualCost.toFixed(0)} €</TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditLaborRate(rate)}
                                    className="h-7 w-7 p-0"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>{language === 'fr' ? 'Note:' : 'Note:'}</strong>{' '}
                    {language === 'fr' 
                      ? 'Lors de la création d\'une simulation, l\'utilisateur saisit uniquement le nombre de personnes par rôle. Les heures et taux sont automatiquement appliqués.'
                      : 'When creating a simulation, the user only enters the number of people per role. Hours and rates are automatically applied.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CA moyen par salarié (ETP auto) Configuration Card */}
            <Card className="border border-slate-200 dark:border-slate-800 mt-6" data-testid="ca-per-employee-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {language === 'fr' ? 'Personnel automatique (ETP) — CA par salarié' : 'Automatic staffing (FTE) — CA per employee'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Seuil utilisé à l'étape 4 du simulateur pour calculer le nombre d'ETP : Total = CA prévisionnel ÷ ce montant (arrondi). Répartition : Chef Confirmé = 0, Chef Junior = 1, Second = 1, Commis = le reste."
                    : 'Threshold used at step 4 to compute FTE: Total = projected CA ÷ this amount (rounded). Distribution: Senior Chef = 0, Junior Chef = 1, Second = 1, Commis = remainder.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[220px]">
                    <Label htmlFor="ca-per-employee-input" className="text-sm font-medium mb-2 block">
                      {language === 'fr' ? 'CA moyen par salarié (€)' : 'Average CA per employee (€)'}
                    </Label>
                    <Input
                      id="ca-per-employee-input"
                      type="number"
                      min="1"
                      step="1000"
                      value={caPerEmployeeInput}
                      onChange={(e) => setCaPerEmployeeInput(e.target.value)}
                      className="h-11 text-lg font-semibold"
                      data-testid="ca-per-employee-input"
                    />
                  </div>
                  <Button
                    onClick={handleSaveCaPerEmployee}
                    disabled={savingCaPerEmployee}
                    className="h-11 gap-2"
                    data-testid="ca-per-employee-save-btn"
                  >
                    {savingCaPerEmployee ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {language === 'fr' ? 'Enregistrer' : 'Save'}
                  </Button>
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {language === 'fr'
                      ? `Exemple : un corner à 600 000 € de CA prévisionnel donnera ${Math.round(600000 / (caPerEmployee || 120000))} ETP (Chef Confirmé 0 · Chef Junior 1 · Second 1 · Commis ${Math.max(0, Math.round(600000 / (caPerEmployee || 120000)) - 2)}).`
                      : `Example: a corner at €600,000 projected CA gives ${Math.round(600000 / (caPerEmployee || 120000))} FTE.`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="emails">
            <EmailPausePanel api={api} language={language} />
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      {language === 'fr' ? 'Historique des Emails' : 'Email History'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'fr' ? 'Suivi des notifications envoyées aux utilisateurs' : 'Track notifications sent to users'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {selectedEmails.length > 0 && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={handleResendBulk}
                        disabled={resendingBulk}
                        className="gap-2 bg-blue-600 hover:bg-blue-700"
                      >
                        {resendingBulk ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                        {language === 'fr' ? `Renvoyer (${selectedEmails.length})` : `Resend (${selectedEmails.length})`}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchEmailHistory}
                      disabled={loadingEmailHistory}
                      className="gap-2"
                    >
                      {loadingEmailHistory ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      {language === 'fr' ? 'Actualiser' : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Email Stats */}
                {emailStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
                        <Send className="w-4 h-4" />
                        <span className="text-sm font-medium">{language === 'fr' ? 'Total Envoyés' : 'Total Sent'}</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{emailStats.sent}</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-1">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">{language === 'fr' ? 'Échecs' : 'Failed'}</span>
                      </div>
                      <p className="text-2xl font-bold text-red-700 dark:text-red-300">{emailStats.failed}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">{language === 'fr' ? "Aujourd'hui" : 'Today'}</span>
                      </div>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">{emailStats.today}</p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">{language === 'fr' ? '7 derniers jours' : 'Last 7 days'}</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{emailStats.recent_7d}</p>
                    </div>
                  </div>
                )}

                {/* Email Type Breakdown */}
                {emailStats?.by_type && Object.keys(emailStats.by_type).length > 0 && (
                  <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3">
                      {language === 'fr' ? 'Par Type' : 'By Type'}
                    </h4>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(emailStats.by_type).map(([type, count]) => (
                        <Badge key={type} variant="outline" className="px-3 py-1">
                          {type === 'new_simulation' && (language === 'fr' ? 'Nouvelle Simulation' : 'New Simulation')}
                          {type === 'validation' && (language === 'fr' ? 'Validation' : 'Validation')}
                          {type === 'admin_decision' && (language === 'fr' ? 'Décision Admin' : 'Admin Decision')}
                          {type === 'review_request' && (language === 'fr' ? 'Demande Révision' : 'Review Request')}
                          {!['new_simulation', 'validation', 'admin_decision', 'review_request'].includes(type) && type}
                          : <span className="font-bold ml-1">{count}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {loadingEmailHistory ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : emailHistory.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{language === 'fr' ? 'Aucun email envoyé pour le moment' : 'No emails sent yet'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox 
                              checked={emailHistory.filter(e => e.status === 'failed').length > 0 && 
                                       selectedEmails.length === emailHistory.filter(e => e.status === 'failed').length}
                              onCheckedChange={toggleSelectAllFailed}
                              title={language === 'fr' ? 'Sélectionner tous les échecs' : 'Select all failed'}
                            />
                          </TableHead>
                          <TableHead>{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Destinataire' : 'Recipient'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Sujet' : 'Subject'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Projet' : 'Project'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Déclenché par' : 'Triggered by'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                          <TableHead className="w-24">{language === 'fr' ? 'Action' : 'Action'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailHistory.map((email) => (
                          <TableRow key={email.id} className={email.status === 'failed' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                            <TableCell>
                              {email.status === 'failed' && (
                                <Checkbox 
                                  checked={selectedEmails.includes(email.id)}
                                  onCheckedChange={() => toggleEmailSelection(email.id)}
                                />
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm">
                              {email.sent_at ? format(new Date(email.sent_at), 'dd/MM/yyyy HH:mm', { locale: language === 'fr' ? fr : enUS }) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                email.email_type === 'new_simulation' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                email.email_type === 'validation' ? 'bg-green-50 text-green-700 border-green-200' :
                                email.email_type === 'admin_decision' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                email.email_type === 'review_request' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-slate-700 border-slate-200'
                              }>
                                {email.email_type === 'new_simulation' && (language === 'fr' ? 'Nouvelle Sim.' : 'New Sim.')}
                                {email.email_type === 'validation' && 'Validation'}
                                {email.email_type === 'admin_decision' && (language === 'fr' ? 'Décision' : 'Decision')}
                                {email.email_type === 'review_request' && (language === 'fr' ? 'Révision' : 'Review')}
                                {!['new_simulation', 'validation', 'admin_decision', 'review_request'].includes(email.email_type) && email.email_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[180px] truncate text-sm" title={email.recipient}>
                              {email.recipient}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate text-sm" title={email.subject}>
                              {email.subject}
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate text-sm" title={email.project_name}>
                              {email.project_name || '-'}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {email.triggered_by || '-'}
                            </TableCell>
                            <TableCell>
                              {email.status === 'sent' && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'Envoyé' : 'Sent'}
                                </Badge>
                              )}
                              {email.status === 'failed' && (
                                <Badge className="bg-red-100 text-red-700 border-red-200" title={email.error_message}>
                                  <XCircle className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'Échec' : 'Failed'}
                                </Badge>
                              )}
                              {email.status === 'disabled' && (
                                <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'Désactivé' : 'Disabled'}
                                </Badge>
                              )}
                              {email.status === 'pending' && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {language === 'fr' ? 'En attente' : 'Pending'}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {email.status === 'failed' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleResendEmail(email.id)}
                                  disabled={resendingEmail === email.id}
                                  className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                >
                                  {resendingEmail === email.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <RotateCw className="w-3 h-3" />
                                  )}
                                  {language === 'fr' ? 'Renvoyer' : 'Resend'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recipients Tab */}
          <TabsContent value="recipients">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  {language === 'fr' ? 'Destinataires des Emails CAPEX' : 'CAPEX Email Recipients'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Gérez les destinataires qui recevront les notifications des études CAPEX' 
                    : 'Manage recipients who will receive CAPEX study notifications'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new recipient form */}
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {language === 'fr' ? 'Ajouter un destinataire' : 'Add a recipient'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nom' : 'Name'}</Label>
                      <Input
                        placeholder="Jean Dupont"
                        value={newRecipient.name}
                        onChange={(e) => setNewRecipient(prev => ({...prev, name: e.target.value}))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="jean@example.com"
                        value={newRecipient.email}
                        onChange={(e) => setNewRecipient(prev => ({...prev, email: e.target.value}))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Rôle' : 'Role'}</Label>
                      <select
                        className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                        value={newRecipient.role}
                        onChange={(e) => setNewRecipient(prev => ({...prev, role: e.target.value}))}
                      >
                        <option value="admin">{language === 'fr' ? 'Direction' : 'Management'}</option>
                        <option value="commercial">Commercial</option>
                        <option value="operations">{language === 'fr' ? 'Opérations' : 'Operations'}</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleAddRecipient} className="w-full gap-2">
                        <Send className="w-4 h-4" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Recipients list */}
                {loadingRecipients ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : emailRecipients.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>{language === 'fr' ? 'Aucun destinataire configuré' : 'No recipients configured'}</p>
                    <p className="text-sm mt-1">{language === 'fr' ? 'Ajoutez des destinataires pour recevoir les notifications' : 'Add recipients to receive notifications'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Ajouté le' : 'Added on'}</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailRecipients.map((recipient) => (
                          <TableRow key={recipient.id}>
                            <TableCell className="font-medium">{recipient.name}</TableCell>
                            <TableCell>{recipient.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                recipient.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                recipient.role === 'commercial' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                'bg-blue-50 text-blue-700 border-blue-200'
                              }>
                                {recipient.role === 'admin' && (language === 'fr' ? 'Direction' : 'Management')}
                                {recipient.role === 'commercial' && 'Commercial'}
                                {recipient.role === 'operations' && (language === 'fr' ? 'Opérations' : 'Operations')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recipient.created_at ? format(new Date(recipient.created_at), 'dd/MM/yyyy', { locale: language === 'fr' ? fr : enUS }) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRecipient(recipient.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HG Recipients Tab */}
          <TabsContent value="hg-recipients">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {language === 'fr' ? 'Destinataires HG (Envoi Dossiers Gagnés)' : 'HG Recipients (Won Cases Delivery)'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Gérez les destinataires qui recevront les exports PDF/Excel des dossiers gagnés ou signés' 
                    : 'Manage recipients who will receive PDF/Excel exports of won or signed cases'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new HG recipient form */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <h4 className="font-medium mb-4 flex items-center gap-2 text-blue-800 dark:text-blue-300">
                    <Plus className="w-4 h-4" />
                    {language === 'fr' ? 'Ajouter un destinataire HG' : 'Add an HG recipient'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nom (optionnel)' : 'Name (optional)'}</Label>
                      <Input
                        placeholder="Jean Dupont"
                        value={newHgRecipient.name}
                        onChange={(e) => setNewHgRecipient(prev => ({...prev, name: e.target.value}))}
                        data-testid="hg-recipient-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="hg@example.com"
                        value={newHgRecipient.email}
                        onChange={(e) => setNewHgRecipient(prev => ({...prev, email: e.target.value}))}
                        data-testid="hg-recipient-email-input"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={handleAddHgRecipient} 
                        className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                        data-testid="add-hg-recipient-btn"
                      >
                        <Plus className="w-4 h-4" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* HG Recipients list */}
                {loadingHgRecipients ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : hgRecipients.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>{language === 'fr' ? 'Aucun destinataire HG configuré' : 'No HG recipients configured'}</p>
                    <p className="text-sm mt-1">{language === 'fr' ? 'Ajoutez des destinataires pour envoyer les dossiers gagnés' : 'Add recipients to send won cases'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Ajouté le' : 'Added on'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Ajouté par' : 'Added by'}</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {hgRecipients.map((recipient) => (
                          <TableRow key={recipient.id} data-testid={`hg-recipient-row-${recipient.id}`}>
                            <TableCell className="font-medium">{recipient.name || '-'}</TableCell>
                            <TableCell>{recipient.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                recipient.active 
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                  : 'bg-slate-50 text-slate-500 border-slate-200'
                              }>
                                {recipient.active 
                                  ? (language === 'fr' ? 'Actif' : 'Active')
                                  : (language === 'fr' ? 'Inactif' : 'Inactive')
                                }
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recipient.created_at ? format(new Date(recipient.created_at), 'dd/MM/yyyy', { locale: language === 'fr' ? fr : enUS }) : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recipient.created_by || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteHgRecipient(recipient.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-hg-recipient-${recipient.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Info note */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                  <p className="text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {language === 'fr' 
                      ? 'Les destinataires HG recevront automatiquement les exports PDF et Excel lorsqu\'un utilisateur clique sur "Envoyer chez HG" depuis le tableau de bord pour un dossier gagné ou signé.' 
                      : 'HG recipients will automatically receive PDF and Excel exports when a user clicks "Send to HG" from the dashboard for a won or signed case.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chantier (Pilotage / PV) Recipients Tab */}
          <TabsContent value="chantier-recipients">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <HardHat className="w-5 h-5" />
                  {language === 'fr' ? 'Destinataires Chantier (Soumission / PV)' : 'Chantier Recipients (Submission / PV)'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? 'Gérez les destinataires qui recevront les emails de Pilotage Chantier : soumission du PV de livraison et diffusion du PV.'
                    : 'Manage recipients who will receive Pilotage Chantier emails: PV submission and PV distribution.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new Chantier recipient form */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                  <h4 className="font-medium mb-4 flex items-center gap-2 text-amber-800 dark:text-amber-300">
                    <Plus className="w-4 h-4" />
                    {language === 'fr' ? 'Ajouter un destinataire Chantier' : 'Add a Chantier recipient'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nom (optionnel)' : 'Name (optional)'}</Label>
                      <Input
                        placeholder="Service Travaux"
                        value={newChantierRecipient.name}
                        onChange={(e) => setNewChantierRecipient(prev => ({...prev, name: e.target.value}))}
                        data-testid="chantier-recipient-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="chantier@example.com"
                        value={newChantierRecipient.email}
                        onChange={(e) => setNewChantierRecipient(prev => ({...prev, email: e.target.value}))}
                        data-testid="chantier-recipient-email-input"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={handleAddChantierRecipient}
                        className="w-full gap-2 bg-amber-600 hover:bg-amber-700"
                        data-testid="add-chantier-recipient-btn"
                      >
                        <Plus className="w-4 h-4" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Chantier Recipients list */}
                {loadingChantierRecipients ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : chantierRecipients.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <HardHat className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>{language === 'fr' ? 'Aucun destinataire chantier configuré' : 'No chantier recipients configured'}</p>
                    <p className="text-sm mt-1">{language === 'fr' ? 'Ajoutez des destinataires pour les emails de PV de livraison' : 'Add recipients for PV delivery emails'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>{language === 'fr' ? 'Ajouté le' : 'Added on'}</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {chantierRecipients.map((recipient) => (
                          <TableRow key={recipient.id} data-testid={`chantier-recipient-row-${recipient.id}`}>
                            <TableCell className="font-medium">{recipient.name || '-'}</TableCell>
                            <TableCell>{recipient.email}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recipient.created_at ? format(new Date(recipient.created_at), 'dd/MM/yyyy', { locale: language === 'fr' ? fr : enUS }) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteChantierRecipient(recipient.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-chantier-recipient-${recipient.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Info note */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {language === 'fr'
                      ? 'Ces destinataires reçoivent en copie les emails de Pilotage Chantier (soumission et diffusion du PV de livraison), en plus des administrateurs inclus ci-dessous.'
                      : 'These recipients are CC-ed on Pilotage Chantier emails, in addition to the included admins below.'}
                  </p>
                </div>

                {/* Included admins (already receiving via email routing) */}
                <div>
                  <h4 className="font-semibold mb-1 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {language === 'fr' ? 'Destinataires déjà inclus (administrateurs)' : 'Already-included recipients (admins)'}
                  </h4>
                  <p className="text-sm text-slate-500 mb-4">
                    {language === 'fr'
                      ? 'Ces administrateurs reçoivent automatiquement les rapports de Pilotage Chantier. Activez/désactivez chacun ci-dessous.'
                      : 'These admins automatically receive Pilotage Chantier reports. Toggle each below.'}
                  </p>
                  {chantierIncludedAdmins.length === 0 ? (
                    <p className="text-sm text-slate-400">{language === 'fr' ? 'Aucun administrateur' : 'No admin'}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                            <TableHead className="text-right">{language === 'fr' ? 'Reçoit les rapports' : 'Receives reports'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {chantierIncludedAdmins.map((admin) => (
                            <TableRow key={admin.user_id} data-testid={`chantier-admin-row-${admin.user_id}`}>
                              <TableCell className="font-medium">{admin.name}</TableCell>
                              <TableCell>{admin.email}</TableCell>
                              <TableCell className="text-sm text-slate-500">{admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}</TableCell>
                              <TableCell className="text-right">
                                <Switch
                                  checked={admin.enabled}
                                  disabled={togglingAdminId === admin.user_id}
                                  onCheckedChange={(val) => handleToggleIncludedAdmin(admin.user_id, val)}
                                  data-testid={`chantier-admin-toggle-${admin.user_id}`}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Contract Recipients Tab */}
          {/* Bulk Export (Extraction Massive) Tab */}
          <TabsContent value="bulk-export">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  {language === 'fr' ? 'Extraction Massive des Simulations CAPEX' : 'Bulk Export of CAPEX Simulations'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? 'Exportez en masse les études CAPEX par statut, au format Excel (consolidé ou détaillé) ou PDF.'
                    : 'Bulk export CAPEX studies by status, as Excel (consolidated or detailed) or PDF.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Status selector */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">
                    {language === 'fr' ? 'Filtrer par statut' : 'Filter by status'}
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'all', label: language === 'fr' ? 'Tous' : 'All' },
                      { key: 'in_progress', label: language === 'fr' ? 'En cours' : 'In progress' },
                      { key: 'won', label: language === 'fr' ? 'Gagné / Signé' : 'Won / Signed' },
                      { key: 'nogo', label: 'NO GO' },
                      { key: 'lost', label: language === 'fr' ? 'Perdu / Fermé' : 'Lost / Closed' },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setBulkStatus(opt.key)}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                          bulkStatus === opt.key
                            ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                        }`}
                        data-testid={`bulk-status-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Export actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Consolidated Excel */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-semibold">{language === 'fr' ? 'Excel consolidé' : 'Consolidated Excel'}</h4>
                    </div>
                    <p className="text-sm text-slate-500 flex-1 mb-4">
                      {language === 'fr'
                        ? 'Un seul fichier : 1 ligne par simulation (projet, statut, CA, CAPEX, EBITDA, payback…). Idéal pour le reporting.'
                        : 'Single file: one row per simulation (project, status, CA, CAPEX, EBITDA, payback…).'}
                    </p>
                    <Button
                      onClick={() => handleBulkExport('excel-summary')}
                      disabled={bulkExporting !== null}
                      className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="bulk-export-excel-summary-btn"
                    >
                      {bulkExporting === 'excel-summary' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {language === 'fr' ? 'Télécharger' : 'Download'}
                    </Button>
                  </div>

                  {/* Detailed Excel ZIP */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold">{language === 'fr' ? 'Excel détaillé (ZIP)' : 'Detailed Excel (ZIP)'}</h4>
                    </div>
                    <p className="text-sm text-slate-500 flex-1 mb-4">
                      {language === 'fr'
                        ? 'Archive ZIP : le fichier Excel complet (ETUDE CAPEX, P&L…) de chaque simulation.'
                        : 'ZIP archive: the full Excel file of each simulation.'}
                    </p>
                    <Button
                      onClick={() => handleBulkExport('excel-zip')}
                      disabled={bulkExporting !== null}
                      className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                      data-testid="bulk-export-excel-zip-btn"
                    >
                      {bulkExporting === 'excel-zip' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {language === 'fr' ? 'Télécharger' : 'Download'}
                    </Button>
                  </div>

                  {/* PDF ZIP */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold">{language === 'fr' ? 'PDF (ZIP)' : 'PDF (ZIP)'}</h4>
                    </div>
                    <p className="text-sm text-slate-500 flex-1 mb-4">
                      {language === 'fr'
                        ? 'Archive ZIP : le rapport PDF complet de chaque simulation.'
                        : 'ZIP archive: the full PDF report of each simulation.'}
                    </p>
                    <Button
                      onClick={() => handleBulkExport('pdf-zip')}
                      disabled={bulkExporting !== null}
                      className="w-full gap-2 bg-red-600 hover:bg-red-700"
                      data-testid="bulk-export-pdf-zip-btn"
                    >
                      {bulkExporting === 'pdf-zip' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {language === 'fr' ? 'Télécharger' : 'Download'}
                    </Button>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {language === 'fr'
                      ? "L'extraction ZIP peut prendre quelques instants selon le nombre de dossiers. Le PDF utilise le résumé IA déjà généré (sans régénération)."
                      : 'ZIP export may take a moment depending on the number of files. PDFs use the already-generated AI summary.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CA Corner Access Tab */}
          <TabsContent value="ca-corner-access">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  {language === 'fr' ? 'Accès au module CA Corner' : 'CA Corner module access'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Activez, profil par profil, qui peut consulter le module « CA CORNER » (menu latéral). Par défaut chaque nouveau profil est sur OFF. Admin et Super Admin y ont toujours accès."
                    : 'Enable CA CORNER module access per user profile. New profiles default to OFF. Admins always have access.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 max-w-2xl">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher un profil (nom, email)…' : 'Search a profile…'}
                    value={caCornerUserSearch}
                    onChange={(e) => setCaCornerUserSearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="ca-corner-user-search"
                  />
                  <span className="text-sm text-slate-500">
                    {caCornerUsers.filter(u => !u.locked && caCornerUserAccess[u.id]).length}
                    {' '}{language === 'fr' ? 'profil(s) activé(s)' : 'enabled'} · {caCornerUsers.length} {language === 'fr' ? 'au total' : 'total'}
                  </span>
                </div>
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-100 dark:divide-slate-800 max-h-[480px] overflow-y-auto" data-testid="ca-corner-users-list">
                  {loadingCaCornerUsers ? (
                    <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
                  ) : caCornerUsers.length === 0 ? (
                    <p className="p-6 text-center text-sm text-slate-400">{language === 'fr' ? 'Aucun profil' : 'No profiles'}</p>
                  ) : caCornerUsers
                      .filter(u => {
                        const q = caCornerUserSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                      })
                      .map((u) => (
                        <div key={u.id} className="flex items-center justify-between gap-3 p-3" data-testid={`ca-corner-user-${u.id}`}>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 dark:text-slate-100 truncate">{u.name}</p>
                            <p className="text-xs text-slate-400 truncate">{u.email} · {u.role}</p>
                          </div>
                          {u.locked ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 whitespace-nowrap">
                              {language === 'fr' ? 'Accès permanent' : 'Always on'}
                            </span>
                          ) : (
                            <Switch
                              checked={!!caCornerUserAccess[u.id]}
                              onCheckedChange={(val) => setCaCornerUserAccess(prev => ({ ...prev, [u.id]: val }))}
                              data-testid={`ca-corner-user-switch-${u.id}`}
                            />
                          )}
                        </div>
                      ))}
                </div>
                <Button onClick={handleSaveCaCornerAccess} disabled={savingCaCornerAccess} className="gap-2" data-testid="ca-corner-access-save-btn">
                  {savingCaCornerAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </Button>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 mt-6" data-testid="ca-corner-alert-config-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  {language === 'fr' ? 'Alerte « nouveaux clients en retard »' : 'Late new-clients alert'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Alerte email (Suivi Facturation) envoyée quand l'atterrissage d'un nouveau corner est inférieur au seuil vs sa prévision, après un nombre de mois donné. L'email part aux destinataires ci-dessous ET au « Responsable dossier » de chaque corner concerné."
                    : 'Email alert for late new corners.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 max-w-2xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">{language === 'fr' ? 'Seuil d\'écart run-rate (%)' : 'Run-rate threshold (%)'}</Label>
                    <Input type="number" step="1"
                      value={caCornerAlertConfig.threshold_pct}
                      onChange={(e) => setCaCornerAlertConfig(prev => ({ ...prev, threshold_pct: e.target.value }))}
                      className="mt-1" data-testid="ca-corner-alert-threshold" />
                    <p className="text-xs text-slate-400 mt-1">{language === 'fr' ? 'Ex. -15 : alerte si run-rate inférieur de plus de 15 % à la prévi.' : 'e.g. -15'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">{language === 'fr' ? 'Nombre de mois minimum' : 'Min. months'}</Label>
                    <Input type="number" step="1" min="1" max="12"
                      value={caCornerAlertConfig.min_months}
                      onChange={(e) => setCaCornerAlertConfig(prev => ({ ...prev, min_months: e.target.value }))}
                      className="mt-1" data-testid="ca-corner-alert-months" />
                    <p className="text-xs text-slate-400 mt-1">{language === 'fr' ? 'Le corner n\'est évalué qu\'après ce nombre de mois pleins.' : 'Evaluated after N full months.'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">{language === 'fr' ? 'Seuil d\'alerte qualité (%)' : 'Quality alert threshold (%)'}</Label>
                    <Input type="number" step="0.5" min="0" max="100"
                      value={caCornerAlertConfig.quality_threshold_pct}
                      onChange={(e) => setCaCornerAlertConfig(prev => ({ ...prev, quality_threshold_pct: e.target.value }))}
                      className="mt-1" data-testid="ca-corner-alert-quality-threshold" />
                    <p className="text-xs text-slate-400 mt-1">{language === 'fr' ? 'Ex. 90 : alerte pour tout corner dont la moyenne d\'audits qualité est inférieure à ce %.' : 'e.g. 90'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">{language === 'fr' ? 'Rappel de renouvellement (mois avant échéance)' : 'Renewal reminder (months before)'}</Label>
                    <Input type="number" step="1" min="1" max="24"
                      value={caCornerAlertConfig.renewal_reminder_months}
                      onChange={(e) => setCaCornerAlertConfig(prev => ({ ...prev, renewal_reminder_months: e.target.value }))}
                      className="mt-1" data-testid="ca-corner-alert-renewal-months" />
                    <p className="text-xs text-slate-400 mt-1">{language === 'fr' ? 'Ex. 6 : rappel envoyé dès 6 mois avant chaque Date FDC, puis chaque mois jusqu\'à l\'échéance.' : 'e.g. 6'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">{language === 'fr' ? 'Destinataires de l\'alerte' : 'Alert recipients'}</Label>
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher un utilisateur…' : 'Search a user…'}
                    value={caCornerAlertSearch}
                    onChange={(e) => setCaCornerAlertSearch(e.target.value)}
                    className="max-w-xs mt-2 mb-2"
                    data-testid="ca-corner-alert-search"
                  />
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-100 dark:divide-slate-800 max-h-[320px] overflow-y-auto" data-testid="ca-corner-alert-users-list">
                    {(caCornerAlertConfig.eligible_users || [])
                      .filter(u => {
                        const q = caCornerAlertSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                      })
                      .map(u => (
                        <label key={u.id} className="flex items-center gap-3 p-3 cursor-pointer" data-testid={`ca-corner-alert-user-${u.id}`}>
                          <input type="checkbox"
                            checked={caCornerAlertConfig.recipients.includes(u.id)}
                            onChange={() => toggleAlertRecipient(u.id)}
                            className="w-4 h-4"
                            data-testid={`ca-corner-alert-checkbox-${u.id}`} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 dark:text-slate-100 truncate">{u.name}</p>
                            <p className="text-xs text-slate-400 truncate">{u.email} · {u.role}</p>
                          </div>
                        </label>
                      ))}
                    {(caCornerAlertConfig.eligible_users || []).length === 0 && (
                      <p className="p-6 text-center text-sm text-slate-400">{language === 'fr' ? 'Aucun utilisateur éligible' : 'No eligible users'}</p>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{caCornerAlertConfig.recipients.length} {language === 'fr' ? 'destinataire(s) sélectionné(s)' : 'selected'}</p>
                </div>
                <Button onClick={handleSaveCaCornerAlertConfig} disabled={savingCaCornerAlert} className="gap-2" data-testid="ca-corner-alert-save-btn">
                  {savingCaCornerAlert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {language === 'fr' ? 'Enregistrer l\'alerte' : 'Save alert'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>



          <TabsContent value="contract-recipients">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileSignature className="w-5 h-5" />
                  {language === 'fr' ? 'Destinataires Contrat (Envoi Demandes)' : 'Contract Recipients (Request Delivery)'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Gérez les destinataires qui pourront recevoir les demandes de contrat par email' 
                    : 'Manage recipients who can receive contract requests by email'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new Contract recipient form */}
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700">
                  <h4 className="font-medium mb-4 flex items-center gap-2 text-emerald-800 dark:text-emerald-300">
                    <Plus className="w-4 h-4" />
                    {language === 'fr' ? 'Ajouter un destinataire Contrat' : 'Add a Contract recipient'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nom (optionnel)' : 'Name (optional)'}</Label>
                      <Input
                        placeholder="Service Juridique"
                        value={newContractRecipient.name}
                        onChange={(e) => setNewContractRecipient(prev => ({...prev, name: e.target.value}))}
                        data-testid="contract-recipient-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="contrat@example.com"
                        value={newContractRecipient.email}
                        onChange={(e) => setNewContractRecipient(prev => ({...prev, email: e.target.value}))}
                        data-testid="contract-recipient-email-input"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button 
                        onClick={handleAddContractRecipient} 
                        className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                        data-testid="add-contract-recipient-btn"
                      >
                        <Plus className="w-4 h-4" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Contract Recipients list */}
                {loadingContractRecipients ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : contractRecipients.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileSignature className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>{language === 'fr' ? 'Aucun destinataire contrat configuré' : 'No contract recipients configured'}</p>
                    <p className="text-sm mt-1">{language === 'fr' ? 'Ajoutez des destinataires pour les demandes de contrat' : 'Add recipients for contract requests'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Ajouté le' : 'Added on'}</TableHead>
                          <TableHead>{language === 'fr' ? 'Ajouté par' : 'Added by'}</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contractRecipients.map((recipient) => (
                          <TableRow key={recipient.id} data-testid={`contract-recipient-row-${recipient.id}`}>
                            <TableCell className="font-medium">{recipient.name || '-'}</TableCell>
                            <TableCell>{recipient.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                recipient.active !== false
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                  : 'bg-slate-50 text-slate-500 border-slate-200'
                              }>
                                {recipient.active !== false
                                  ? (language === 'fr' ? 'Actif' : 'Active')
                                  : (language === 'fr' ? 'Inactif' : 'Inactive')
                                }
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recipient.created_at ? format(new Date(recipient.created_at), 'dd/MM/yyyy', { locale: language === 'fr' ? fr : enUS }) : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {recipient.created_by || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteContractRecipient(recipient.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-contract-recipient-${recipient.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Info note */}
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                  <p className="text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {language === 'fr' 
                      ? 'Les destinataires contrat apparaîtront dans la liste de sélection rapide lorsqu\'un utilisateur clique sur "Envoyer contrat" depuis une étude CAPEX.' 
                      : 'Contract recipients will appear in the quick selection list when a user clicks "Send contract" from a CAPEX study.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Permissions Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="permissions">
              <Card className="border border-slate-200 dark:border-slate-800">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    {language === 'fr' ? 'Gestion des Permissions' : 'Permission Management'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' 
                      ? 'Configurez les droits d\'accès pour chaque famille d\'utilisateurs' 
                      : 'Configure access rights for each user family'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingPermissions ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : (
                    <>
                      {/* Role selector */}
                      <div className="flex items-center gap-4 flex-wrap">
                        <Label className="font-medium">{language === 'fr' ? 'Sélectionner un rôle:' : 'Select a role:'}</Label>
                        <div className="flex gap-2 flex-wrap">
                          {availableRoles.map((role) => (
                            <Button
                              key={role}
                              variant={selectedPermRole === role ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setSelectedPermRole(role)}
                              className={selectedPermRole === role ? 'bg-blue-600' : ''}
                              data-testid={`perm-role-${role}`}
                            >
                              {role === 'super_admin' && <Shield className="w-3 h-3 mr-1" />}
                              {role === 'admin' && <UserCog className="w-3 h-3 mr-1" />}
                              {role === 'commercial' && <Users className="w-3 h-3 mr-1" />}
                              {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Permissions grid for selected role */}
                      {selectedPermRole && editedPermissions[selectedPermRole] && (
                        <div className="space-y-4">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                            <h4 className="font-medium mb-4 flex items-center gap-2">
                              <Lock className="w-4 h-4" />
                              {language === 'fr' ? `Permissions pour: ${selectedPermRole.replace('_', ' ').toUpperCase()}` : `Permissions for: ${selectedPermRole.replace('_', ' ').toUpperCase()}`}
                            </h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {Object.entries(editedPermissions[selectedPermRole] || {}).map(([permission, value]) => (
                                <div 
                                  key={permission}
                                  className={`p-3 rounded-lg border ${value ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {permission === 'view_simulations' && <Eye className="w-4 h-4 text-slate-500" />}
                                      {permission === 'create_simulation' && <Plus className="w-4 h-4 text-slate-500" />}
                                      {permission === 'edit_simulation' && <FileEdit className="w-4 h-4 text-slate-500" />}
                                      {permission === 'delete_simulation' && <Trash2 className="w-4 h-4 text-slate-500" />}
                                      {permission === 'export_simulation' && <Download className="w-4 h-4 text-slate-500" />}
                                      {permission === 'make_decision' && <Check className="w-4 h-4 text-slate-500" />}
                                      {permission === 'manage_users' && <Users className="w-4 h-4 text-slate-500" />}
                                      {permission === 'view_analytics' && <ChartBar className="w-4 h-4 text-slate-500" />}
                                      <span className="text-sm font-medium">
                                        {permissionLabels[permission]?.[language] || permission.replace(/_/g, ' ')}
                                      </span>
                                    </div>
                                    <Switch
                                      checked={value}
                                      onCheckedChange={(checked) => handlePermissionChange(selectedPermRole, permission, checked)}
                                      disabled={selectedPermRole === 'super_admin'}
                                      data-testid={`perm-switch-${permission}`}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-3 justify-end">
                            <Button
                              variant="outline"
                              onClick={() => handleResetPermissions(selectedPermRole)}
                              disabled={selectedPermRole === 'super_admin'}
                              className="gap-2"
                              data-testid="reset-permissions-btn"
                            >
                              <RotateCcw className="w-4 h-4" />
                              {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                            </Button>
                            <Button
                              onClick={() => handleSavePermissions(selectedPermRole)}
                              disabled={savingPermissions || selectedPermRole === 'super_admin'}
                              className="gap-2 bg-blue-600 hover:bg-blue-700"
                              data-testid="save-permissions-btn"
                            >
                              {savingPermissions ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                              {language === 'fr' ? 'Enregistrer' : 'Save'}
                            </Button>
                          </div>

                          {selectedPermRole === 'super_admin' && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-700 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                {language === 'fr' 
                                  ? 'Les permissions du Super Admin ne peuvent pas être modifiées.' 
                                  : 'Super Admin permissions cannot be modified.'}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Legend */}
                      <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                        <h4 className="font-medium mb-3">{language === 'fr' ? 'Légende des permissions' : 'Permission Legend'}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-emerald-500"></div>
                            <span>{language === 'fr' ? 'Autorisé' : 'Allowed'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-red-500"></div>
                            <span>{language === 'fr' ? 'Non autorisé' : 'Not allowed'}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Admin Tabs Access — accès fin aux sous-onglets (super_admin only) */}
          {isSuperAdmin && (
            <TabsContent value="admin-tab-access">
              <Card className="border border-slate-200 dark:border-slate-800">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Lock className="w-5 h-5" />{L('Accès aux onglets Admin', 'Admin tabs access')}
                  </CardTitle>
                  <CardDescription>
                    {L("Choisissez quels sous-onglets de l'administration sont visibles, par rôle et par utilisateur. La surcharge par utilisateur est prioritaire sur le réglage du rôle.",
                       'Choose which admin sub-tabs are visible, per role and per user. A user override takes precedence over the role setting.')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Par rôle */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Label className="font-medium">{L('Par rôle', 'By role')}</Label>
                      <select value={ataSelectedRole} onChange={e => setAtaSelectedRole(e.target.value)}
                        data-testid="ata-role-select"
                        className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                        {roleOptions.filter(r => r !== 'super_admin').map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <button type="button" onClick={() => resetAtaRole(ataSelectedRole)} data-testid="ata-role-reset"
                        className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                        {L('Réinitialiser (tout visible)', 'Reset (all visible)')}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {controllableAdminTabs.map(key => {
                        const on = ataRoleValue(ataSelectedRole, key);
                        return (
                          <label key={key} data-testid={`ata-role-row-${key}`}
                            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border cursor-pointer ${on ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                            <span className="text-sm text-slate-700 dark:text-slate-200">{adminTabDefs[key].label}</span>
                            <Switch checked={on} onCheckedChange={v => setAtaRoleValue(ataSelectedRole, key, v)}
                              data-testid={`ata-role-switch-${key}`} />
                          </label>
                        );
                      })}
                    </div>
                    <div className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">{L('Onglets Croquis Agenceur', 'Croquis Agenceur tabs')}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {CROQUIS_TABS.map(({ key, label }) => {
                        const on = ataRoleValue(ataSelectedRole, key);
                        return (
                          <label key={key} data-testid={`ata-role-row-${key}`}
                            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border cursor-pointer ${on ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                            <span className="text-sm text-slate-700 dark:text-slate-200">{label}</span>
                            <Switch checked={on} onCheckedChange={v => setAtaRoleValue(ataSelectedRole, key, v)}
                              data-testid={`ata-role-switch-${key}`} />
                          </label>
                        );
                      })}
                    </div>

                  </div>

                  {/* Par utilisateur */}
                  <div className="space-y-3 border-t border-slate-100 dark:border-slate-800 pt-6">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Label className="font-medium">{L('Par utilisateur (surcharge)', 'By user (override)')}</Label>
                      <select value={ataSelectedUserId} onChange={e => setAtaSelectedUserId(e.target.value)}
                        data-testid="ata-user-select"
                        className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm min-w-[220px]">
                        <option value="">{L('— Choisir un utilisateur —', '— Select a user —')}</option>
                        {users.filter(u => u.role !== 'super_admin').map(u => (
                          <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
                        ))}
                      </select>
                      {ataSelectedUserId && (
                        <button type="button" onClick={() => resetAtaUser(ataSelectedUserId)} data-testid="ata-user-reset"
                          className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                          {L('Retirer toutes les surcharges', 'Clear all overrides')}
                        </button>
                      )}
                    </div>
                    {ataSelectedUserId ? (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {controllableAdminTabs.map(key => {
                          const on = ataUserValue(ataSelectedUserId, key);
                          const override = ataUserHasOverride(ataSelectedUserId, key);
                          return (
                            <div key={key} data-testid={`ata-user-row-${key}`}
                              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${on ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                              <span className="text-sm text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                                {adminTabDefs[key].label}
                                {override && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">{L('surcharge', 'override')}</span>}
                              </span>
                              <div className="flex items-center gap-2">
                                <Switch checked={on} onCheckedChange={v => setAtaUserValue(ataSelectedUserId, key, v)}
                                  data-testid={`ata-user-switch-${key}`} />
                                {override && (
                                  <button type="button" onClick={() => clearAtaUserOverride(ataSelectedUserId, key)}
                                    title={L('Revenir au réglage du rôle', 'Revert to role setting')}
                                    data-testid={`ata-user-clear-${key}`}
                                    className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">{L('Onglets Croquis Agenceur', 'Croquis Agenceur tabs')}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {CROQUIS_TABS.map(({ key, label }) => {
                          const on = ataUserValue(ataSelectedUserId, key);
                          const override = ataUserHasOverride(ataSelectedUserId, key);
                          return (
                            <div key={key} data-testid={`ata-user-row-${key}`}
                              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${on ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                              <span className="text-sm text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                                {label}
                                {override && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">{L('surcharge', 'override')}</span>}
                              </span>
                              <div className="flex items-center gap-2">
                                <Switch checked={on} onCheckedChange={v => setAtaUserValue(ataSelectedUserId, key, v)}
                                  data-testid={`ata-user-switch-${key}`} />
                                {override && (
                                  <button type="button" onClick={() => clearAtaUserOverride(ataSelectedUserId, key)}
                                    title={L('Revenir au réglage du rôle', 'Revert to role setting')}
                                    data-testid={`ata-user-clear-${key}`}
                                    className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">{L('Sélectionnez un utilisateur pour définir des exceptions individuelles.', 'Select a user to set individual exceptions.')}</p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={saveAdminTabAccess} disabled={savingAta}
                      className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="ata-save-btn">
                      {savingAta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {L('Enregistrer', 'Save')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Email Templates Tab */}
          {isSuperAdmin && (
            <TabsContent value="email-templates">
              <Card className="border border-slate-200 dark:border-slate-800">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    {language === 'fr' ? 'Templates Email' : 'Email Templates'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' 
                      ? 'Personnalisez le contenu des emails automatiques' 
                      : 'Customize automatic email content'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                    </div>
                  ) : editingTemplate ? (
                    /* Template Editor */
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold">{editingTemplate.name}</h3>
                          <p className="text-sm text-slate-500">{editingTemplate.description}</p>
                        </div>
                        <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                          <X className="w-4 h-4 mr-2" />
                          Annuler
                        </Button>
                      </div>
                      
                      {/* Subject */}
                      <div className="space-y-2">
                        <Label>Objet du mail</Label>
                        <Input
                          value={editingTemplate.subject}
                          onChange={(e) => setEditingTemplate({
                            ...editingTemplate,
                            subject: e.target.value
                          })}
                          placeholder="Objet de l'email"
                        />
                      </div>
                      
                      {/* Body */}
                      <div className="space-y-2">
                        <Label>Contenu du mail</Label>
                        <textarea
                          className="w-full h-64 p-4 border rounded-lg font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-purple-500"
                          value={editingTemplate.body}
                          onChange={(e) => setEditingTemplate({
                            ...editingTemplate,
                            body: e.target.value
                          })}
                          placeholder="Contenu de l'email"
                        />
                      </div>
                      
                      {/* Variables */}
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <h4 className="font-medium mb-2">Variables disponibles :</h4>
                        <div className="flex flex-wrap gap-2">
                          {editingTemplate.variables.map((v) => (
                            <Badge 
                              key={v} 
                              variant="outline" 
                              className="cursor-pointer hover:bg-purple-100"
                              onClick={() => {
                                setEditingTemplate({
                                  ...editingTemplate,
                                  body: editingTemplate.body + `{${v}}`
                                });
                              }}
                            >
                              {`{${v}}`}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Cliquez sur une variable pour l'ajouter au contenu
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-3">
                        <Button 
                          onClick={handleSaveTemplate}
                          disabled={savingTemplate}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {savingTemplate ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Sauvegarder
                        </Button>
                        {editingTemplate.is_custom && (
                          <Button 
                            variant="outline" 
                            onClick={() => handleResetTemplate(editingTemplate.type)}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Réinitialiser par défaut
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Templates List */
                    <div className="grid gap-4">
                      {emailTemplates.map((template) => (
                        <div 
                          key={template.type}
                          className="p-4 border rounded-lg hover:border-purple-300 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{template.name}</h4>
                                {template.is_custom && (
                                  <Badge variant="outline" className="text-purple-600 border-purple-300">
                                    Personnalisé
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                              <div className="mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs font-mono">
                                <strong>Objet:</strong> {template.subject}
                              </div>
                              {template.updated_at && (
                                <p className="text-xs text-slate-400 mt-2">
                                  Modifié le {new Date(template.updated_at).toLocaleDateString('fr-FR')} par {template.updated_by}
                                </p>
                              )}
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setEditingTemplate(template)}
                            >
                              <FileEdit className="w-4 h-4 mr-2" />
                              Modifier
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Diagnostic Tab */}
          {isSuperAdmin && (
            <TabsContent value="diagnostic">
              <Card className="border border-slate-200 dark:border-slate-800">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Settings className="w-5 h-5 text-slate-600" />
                    {language === 'fr' ? 'Diagnostic Système' : 'System Diagnostic'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' ? 'Testez la configuration email et les intégrations' : 'Test email configuration and integrations'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <DiagnosticPanel api={api} language={language} />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Organisations Tab */}
          <TabsContent value="organisations">
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <HardHat className="w-5 h-5 text-orange-500" />
                  Gestion des Organisations Prestataires
                </CardTitle>
                <CardDescription>
                  Créez et gérez les organisations de prestataires. Chaque prestataire est rattaché à une organisation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Create new org form */}
                <div className="bg-orange-50 dark:bg-orange-900/10 p-4 rounded-lg border border-orange-200 dark:border-orange-800 space-y-3">
                  <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Nouvelle organisation
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Nom de l'organisation *"
                      value={newOrgForm.nom}
                      onChange={e => setNewOrgForm(p => ({ ...p, nom: e.target.value }))}
                      className="border rounded-md px-3 py-2 text-sm"
                      data-testid="new-org-name"
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={newOrgForm.description}
                      onChange={e => setNewOrgForm(p => ({ ...p, description: e.target.value }))}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                    <input
                      type="email"
                      placeholder="Email de contact"
                      value={newOrgForm.contact_email}
                      onChange={e => setNewOrgForm(p => ({ ...p, contact_email: e.target.value }))}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Téléphone"
                      value={newOrgForm.contact_phone}
                      onChange={e => setNewOrgForm(p => ({ ...p, contact_phone: e.target.value }))}
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <Button
                    onClick={handleCreateOrg}
                    disabled={creatingOrg || !newOrgForm.nom.trim()}
                    className="bg-orange-500 hover:bg-orange-600 text-white gap-1"
                    data-testid="create-org-btn"
                  >
                    {creatingOrg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Créer
                  </Button>
                </div>

                {/* Organisations list */}
                {loadingOrgs ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                ) : organisations.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucune organisation créée</p>
                ) : (
                  <div className="space-y-3" data-testid="organisations-list">
                    {organisations.map(org => (
                      <div key={org.id} className="border rounded-lg p-4 bg-white dark:bg-slate-800 flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {editingOrg?.id === org.id ? (
                            <div className="space-y-2">
                              <input type="text" value={editingOrg.nom} onChange={e => setEditingOrg(p => ({ ...p, nom: e.target.value }))}
                                className="border rounded px-2 py-1 text-sm w-full" />
                              <input type="text" value={editingOrg.description || ''} onChange={e => setEditingOrg(p => ({ ...p, description: e.target.value }))}
                                placeholder="Description" className="border rounded px-2 py-1 text-sm w-full" />
                              <div className="flex gap-2">
                                <input type="email" value={editingOrg.contact_email || ''} onChange={e => setEditingOrg(p => ({ ...p, contact_email: e.target.value }))}
                                  placeholder="Email" className="border rounded px-2 py-1 text-sm flex-1" />
                                <input type="text" value={editingOrg.contact_phone || ''} onChange={e => setEditingOrg(p => ({ ...p, contact_phone: e.target.value }))}
                                  placeholder="Tél." className="border rounded px-2 py-1 text-sm flex-1" />
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" className="bg-orange-500 text-white gap-1" onClick={handleUpdateOrg}>
                                  <Save className="w-3.5 h-3.5" /> Sauvegarder
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingOrg(null)}><X className="w-3.5 h-3.5" /></Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-slate-900 dark:text-white">{org.nom}</h4>
                                <Badge variant="outline" className="text-xs">{org.member_count || 0} membre{(org.member_count || 0) > 1 ? 's' : ''}</Badge>
                                <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">{org.chantier_count || 0} chantier{(org.chantier_count || 0) > 1 ? 's' : ''}</Badge>
                              </div>
                              {org.description && <p className="text-xs text-slate-500 mt-1">{org.description}</p>}
                              <div className="flex gap-3 mt-1 text-xs text-slate-400">
                                {org.contact_email && <span>{org.contact_email}</span>}
                                {org.contact_phone && <span>{org.contact_phone}</span>}
                              </div>
                            </>
                          )}
                        </div>
                        {!editingOrg && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50"
                              onClick={() => setEditingOrg({ ...org })} title="Modifier"
                              data-testid={`edit-org-${org.id}`}
                            ><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                              onClick={() => handleDeleteOrg(org.id)} title="Supprimer"
                              data-testid={`delete-org-${org.id}`}
                            ><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SLA Agenceur Tab */}
          <TabsContent value="sla-agenceur">
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-orange-500" />Paramètres SLA — Relances Agenceurs</CardTitle>
                <p className="text-sm text-slate-500">Configurez les délais de relance automatique après envoi d'une commande agenceur.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Délai SLA cible (heures)</Label>
                    <Input type="number" value={slaSettings.sla_delay_hours} onChange={e => setSlaSettings(s => ({ ...s, sla_delay_hours: parseInt(e.target.value) || 0 }))} data-testid="sla-delay" />
                    <p className="text-xs text-slate-500">Au-delà de ce délai, le SLA est considéré comme non respecté. <strong>Défaut : 72h (3 jours)</strong></p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">1ère relance après (heures)</Label>
                    <Input type="number" value={slaSettings.relance1_hours} onChange={e => setSlaSettings(s => ({ ...s, relance1_hours: parseInt(e.target.value) || 0 }))} data-testid="sla-relance1" />
                    <p className="text-xs text-slate-500">Relance email automatique à l'agenceur. <strong>Défaut : 72h (3 jours)</strong></p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">2ème relance après (heures)</Label>
                    <Input type="number" value={slaSettings.relance2_hours} onChange={e => setSlaSettings(s => ({ ...s, relance2_hours: parseInt(e.target.value) || 0 }))} data-testid="sla-relance2" />
                    <p className="text-xs text-slate-500">2ème relance + alerte interne admin. <strong>Défaut : 120h (5 jours)</strong></p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <Button onClick={handleSaveSla} disabled={slaSaving} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="save-sla-btn">
                    {slaSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Enregistrer
                  </Button>
                  <span className="text-xs text-slate-400">Les modifications s'appliquent aux prochains dossiers envoyés.</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prestataires Agenceurs Config Tab */}
          <TabsContent value="agenceurs-config">
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-indigo-500" />
                  Prestataires Agenceurs
                </CardTitle>
                <CardDescription>
                  Gérez la liste des prestataires agenceurs et leurs emails par défaut. Ces emails sont pré-remplis automatiquement dans le formulaire de commande agenceur.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new agenceur form */}
                <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Ajouter un prestataire
                  </h4>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs text-slate-500">Nom du prestataire</Label>
                      <Input
                        placeholder="Ex: DSAO, Maxime RAPHAEL..."
                        value={newAgenceur.name}
                        onChange={e => setNewAgenceur(p => ({ ...p, name: e.target.value }))}
                        data-testid="new-agenceur-name"
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs text-slate-500">Email par défaut</Label>
                      <Input
                        type="email"
                        placeholder="Ex: contact@prestataire.fr"
                        value={newAgenceur.email}
                        onChange={e => setNewAgenceur(p => ({ ...p, email: e.target.value }))}
                        data-testid="new-agenceur-email"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={handleAddAgenceur}
                        disabled={savingAgenceur || !newAgenceur.name.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                        data-testid="add-agenceur-btn"
                      >
                        {savingAgenceur ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Ajouter
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Agenceurs list table */}
                {loadingAgenceurs ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : agenceursConfig.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <UserCog className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p>Aucun prestataire configuré</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Nom du prestataire</TableHead>
                        <TableHead className="w-1/3">Email par défaut</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agenceursConfig.map(ag => (
                        <TableRow key={ag.id} data-testid={`agenceur-row-${ag.id}`}>
                          {editingAgenceur?.id === ag.id ? (
                            <>
                              <TableCell>
                                <Input
                                  value={editingAgenceur.name}
                                  onChange={e => setEditingAgenceur(p => ({ ...p, name: e.target.value }))}
                                  data-testid="edit-agenceur-name"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="email"
                                  value={editingAgenceur.email}
                                  onChange={e => setEditingAgenceur(p => ({ ...p, email: e.target.value }))}
                                  data-testid="edit-agenceur-email"
                                />
                              </TableCell>
                              <TableCell className="text-right space-x-1">
                                <Button size="sm" variant="ghost" onClick={handleUpdateAgenceur} disabled={savingAgenceur} data-testid="save-edit-agenceur-btn">
                                  {savingAgenceur ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 text-green-600" />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingAgenceur(null)} data-testid="cancel-edit-agenceur-btn">
                                  <X className="w-4 h-4 text-slate-400" />
                                </Button>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="font-medium" data-testid={`agenceur-name-${ag.id}`}>{ag.name}</TableCell>
                              <TableCell className="text-slate-600" data-testid={`agenceur-email-${ag.id}`}>{ag.email || <span className="italic text-slate-400">Non défini</span>}</TableCell>
                              <TableCell className="text-right space-x-1">
                                <Button size="sm" variant="ghost" onClick={() => setEditingAgenceur({ ...ag })} data-testid={`edit-agenceur-${ag.id}`}>
                                  <Pencil className="w-4 h-4 text-blue-500" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteAgenceur(ag.id)}
                                  disabled={deletingAgenceurId === ag.id}
                                  data-testid={`delete-agenceur-${ag.id}`}
                                >
                                  {deletingAgenceurId === ag.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                                </Button>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <p className="text-xs text-slate-400 pt-2">
                  Lorsqu'un prestataire est sélectionné dans le formulaire Dossier Agenceur, son email par défaut est automatiquement pré-rempli. L'option "Autre" permet une saisie libre.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Checklist Chantier Tab */}
          <TabsContent value="checklist-chantier">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HardHat className="w-5 h-5 text-orange-500" />
                  Points de validation — Checklist Chantier
                </CardTitle>
                <CardDescription>
                  Gérez les points de contrôle du formulaire Pilotage Chantier. Les prestataires verront uniquement les points actifs.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Add new item form */}
                <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900 space-y-3">
                  <p className="font-medium text-sm text-slate-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Ajouter un point de validation</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Libellé *</Label>
                      <Input
                        value={newChecklistItem.label}
                        onChange={e => setNewChecklistItem(prev => ({ ...prev, label: e.target.value }))}
                        placeholder="Ex: Vérification des soudures"
                        data-testid="new-checklist-label"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Catégorie</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={newChecklistItem.category}
                        onChange={e => setNewChecklistItem(prev => ({ ...prev, category: e.target.value }))}
                        data-testid="new-checklist-category"
                      >
                        {checklistCategories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Options (virgule)</Label>
                      <Input
                        value={newChecklistItem.options}
                        onChange={e => setNewChecklistItem(prev => ({ ...prev, options: e.target.value }))}
                        placeholder="Oui,Non,Partiellement"
                        data-testid="new-checklist-options"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Commentaire si</Label>
                      <Input
                        value={newChecklistItem.comment_on}
                        onChange={e => setNewChecklistItem(prev => ({ ...prev, comment_on: e.target.value }))}
                        placeholder="Non,Partiellement"
                        data-testid="new-checklist-comment-on"
                      />
                    </div>
                  </div>
                  <Button onClick={handleAddChecklistItem} disabled={savingChecklistItem} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="add-checklist-btn">
                    {savingChecklistItem ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
                  </Button>
                </div>

                {/* Category filter */}
                {checklistCategories.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500 font-medium">Filtrer par catégorie :</span>
                    <Button
                      variant={filterCategory === '' ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setFilterCategory('')}
                    >
                      Toutes ({checklistItems.length})
                    </Button>
                    {checklistCategories.map(cat => {
                      const count = checklistItems.filter(it => it.category === cat).length;
                      return (
                        <Button
                          key={cat}
                          variant={filterCategory === cat ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setFilterCategory(cat)}
                          data-testid={`filter-cat-${cat}`}
                        >
                          {cat} ({count})
                        </Button>
                      );
                    })}
                  </div>
                )}

                {/* Items list */}
                {loadingChecklist ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                ) : checklistItems.length === 0 ? (
                  <p className="text-center text-slate-400 py-4">Aucun point de validation configuré</p>
                ) : (
                  <Table data-testid="checklist-items-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Libellé</TableHead>
                        <TableHead className="w-44">Catégorie</TableHead>
                        <TableHead className="w-40">Options</TableHead>
                        <TableHead className="w-24 text-center">Actif</TableHead>
                        <TableHead className="w-32 text-center">Ordre</TableHead>
                        <TableHead className="w-28 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...checklistItems].sort((a, b) => a.order - b.order).filter(item => !filterCategory || item.category === filterCategory).map((item, idx) => (
                        <TableRow key={item.id} className={!item.active ? 'opacity-50' : ''} data-testid={`checklist-row-${item.id}`}>
                          <TableCell className="font-mono text-xs text-slate-400">{idx + 1}</TableCell>
                          <TableCell>
                            {editingChecklistItem?.id === item.id ? (
                              <Input
                                value={editingChecklistItem.label}
                                onChange={e => setEditingChecklistItem(prev => ({ ...prev, label: e.target.value }))}
                                className="text-sm"
                                data-testid={`edit-checklist-label-${item.id}`}
                              />
                            ) : (
                              <span className="text-sm font-medium">{item.label}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {editingChecklistItem?.id === item.id ? (
                              <select
                                className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                                value={editingChecklistItem.category || ''}
                                onChange={e => setEditingChecklistItem(prev => ({ ...prev, category: e.target.value }))}
                              >
                                {checklistCategories.map(cat => (
                                  <option key={cat} value={cat}>{cat}</option>
                                ))}
                              </select>
                            ) : (
                              <Badge variant="outline" className={`text-xs ${
                                item.category === 'Pose & Installation' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                item.category === 'Tests & Contrôles' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                item.category === 'Finitions' ? 'bg-green-50 text-green-700 border-green-200' :
                                'bg-slate-50 text-slate-600'
                              }`}>{item.category || '—'}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {editingChecklistItem?.id === item.id ? (
                              <Input
                                value={Array.isArray(editingChecklistItem.options) ? editingChecklistItem.options.join(',') : editingChecklistItem.options}
                                onChange={e => setEditingChecklistItem(prev => ({ ...prev, options: e.target.value }))}
                                className="text-xs"
                              />
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {(item.options || []).map(o => (
                                  <Badge key={o} variant="outline" className="text-xs">{o}</Badge>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch
                              checked={item.active}
                              onCheckedChange={() => handleToggleChecklistItem(item)}
                              data-testid={`toggle-checklist-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleMoveChecklistItem(item, 'up')} disabled={idx === 0} data-testid={`move-up-${item.id}`}>
                                <span className="text-xs">&#9650;</span>
                              </Button>
                              <span className="text-xs text-slate-400 w-6 text-center">{item.order}</span>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleMoveChecklistItem(item, 'down')} disabled={idx === checklistItems.length - 1} data-testid={`move-down-${item.id}`}>
                                <span className="text-xs">&#9660;</span>
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {editingChecklistItem?.id === item.id ? (
                              <div className="flex gap-1 justify-end">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={handleUpdateChecklistItem} disabled={savingChecklistItem}>
                                  {savingChecklistItem ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" onClick={() => setEditingChecklistItem(null)}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-end">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => setEditingChecklistItem({ ...item, options: Array.isArray(item.options) ? item.options.join(',') : item.options, comment_on: Array.isArray(item.comment_on) ? item.comment_on.join(',') : item.comment_on })} data-testid={`edit-checklist-${item.id}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteChecklistItem(item.id)} data-testid={`delete-checklist-${item.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <p className="text-xs text-slate-400">
                  Les points actifs seront visibles dans le formulaire du prestataire. Désactivez un point pour le masquer temporairement sans le supprimer.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upload Categories Tab */}
          <TabsContent value="upload-categories">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-emerald-500" />
                  Catégories de pièces jointes — Annonce ALL
                </CardTitle>
                <CardDescription>
                  Gérez les catégories proposées lors de l'upload de fichiers dans les annonces.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new category */}
                <div className="flex items-end gap-3 p-4 border rounded-lg bg-slate-50 dark:bg-slate-900">
                  <div className="flex-1">
                    <Label className="text-xs">Nouvelle catégorie</Label>
                    <Input
                      value={newUploadCatLabel}
                      onChange={e => setNewUploadCatLabel(e.target.value)}
                      placeholder="Ex: Contrat, Visuel validé..."
                      data-testid="new-upload-cat-label"
                      onKeyDown={e => { if (e.key === 'Enter') handleAddUploadCat(); }}
                    />
                  </div>
                  <Button onClick={handleAddUploadCat} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="add-upload-cat-btn">
                    <Plus className="w-4 h-4" /> Ajouter
                  </Button>
                </div>

                {/* List */}
                {loadingUploadCats ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
                ) : uploadCategories.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Aucune catégorie configurée</p>
                ) : (
                  <div className="space-y-2">
                    {uploadCategories.map(cat => (
                      <div key={cat.id} className={`flex items-center gap-3 p-3 rounded-lg border ${cat.active ? 'bg-white dark:bg-slate-900 border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`} data-testid={`upload-cat-${cat.id}`}>
                        <Tag className="w-4 h-4 text-emerald-500 shrink-0" />
                        {editingUploadCat?.id === cat.id ? (
                          <Input
                            value={editingUploadCat.label}
                            onChange={e => setEditingUploadCat(prev => ({ ...prev, label: e.target.value }))}
                            className="h-8 text-sm flex-1"
                            data-testid={`edit-upload-cat-input-${cat.id}`}
                            autoFocus
                          />
                        ) : (
                          <span className="text-sm font-medium flex-1">{cat.label}</span>
                        )}
                        <Switch
                          checked={cat.active}
                          onCheckedChange={(checked) => handleUpdateUploadCat({ ...cat, active: checked })}
                          data-testid={`toggle-upload-cat-${cat.id}`}
                        />
                        {editingUploadCat?.id === cat.id ? (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => handleUpdateUploadCat(editingUploadCat)}>
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" onClick={() => setEditingUploadCat(null)}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => setEditingUploadCat({ ...cat })} data-testid={`edit-upload-cat-${cat.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteUploadCat(cat.id)} data-testid={`delete-upload-cat-${cat.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs text-slate-400">
                  Les catégories actives seront proposées lors de l'upload de fichiers dans le formulaire Annonce ALL.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Direction Report Tab */}
          <TabsContent value="rapport-direction">
            <Card data-testid="rapport-direction-config">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {language === 'fr' ? 'Rapport Direction (mensuel)' : 'Direction Report (monthly)'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Reporting exécutif automatique (simulations, CA validé, payback, deals brûlants, PV) envoyé chaque mois."
                    : 'Automatic executive report (simulations, validated revenue, payback, hot deals, PVs) sent monthly.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <span className="text-sm font-medium">{language === 'fr' ? 'Activé' : 'Enabled'}</span>
                    <button
                      onClick={() => saveDirReportConfig({ enabled: !dirCfg.enabled })}
                      disabled={savingDirCfg}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${dirCfg.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      data-testid="dir-enabled-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${dirCfg.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Fréquence' : 'Frequency'}</label>
                    <select
                      value={dirCfg.frequency || 'monthly'}
                      onChange={(e) => saveDirReportConfig({ frequency: e.target.value })}
                      className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                      data-testid="dir-frequency-select"
                    >
                      <option value="monthly">{language === 'fr' ? 'Mensuel' : 'Monthly'}</option>
                      <option value="weekly">{language === 'fr' ? 'Hebdomadaire' : 'Weekly'}</option>
                    </select>
                  </div>
                  {(dirCfg.frequency || 'monthly') === 'weekly' ? (
                    <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                      <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Jour' : 'Day'}</label>
                      <select
                        value={dirCfg.weekday}
                        onChange={(e) => saveDirReportConfig({ weekday: parseInt(e.target.value, 10) })}
                        className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                        data-testid="dir-weekday-select"
                      >
                        {['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'].map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                      <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Jour du mois' : 'Day of month'}</label>
                      <select
                        value={dirCfg.day_of_month}
                        onChange={(e) => saveDirReportConfig({ day_of_month: parseInt(e.target.value, 10) })}
                        className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                        data-testid="dir-day-select"
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Heure (Paris)' : 'Hour (Paris)'}</label>
                    <select
                      value={dirCfg.hour}
                      onChange={(e) => saveDirReportConfig({ hour: parseInt(e.target.value, 10) })}
                      className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                      data-testid="dir-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold mb-2">{language === 'fr' ? 'Destinataires' : 'Recipients'}</p>
                  <p className="text-xs text-slate-400 mb-3">
                    {language === 'fr'
                      ? "Si vide, le rapport est envoyé aux administrateurs/directions configurés par défaut."
                      : 'If empty, the report goes to the default admin/management recipients.'}
                  </p>
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="email"
                      placeholder="direction@wasabi.fr"
                      value={newDirRecipient}
                      onChange={(e) => setNewDirRecipient(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addDirRecipient(); }}
                      className="max-w-sm"
                      data-testid="dir-recipient-input"
                    />
                    <Button onClick={addDirRecipient} disabled={savingDirCfg} data-testid="dir-recipient-add">
                      {language === 'fr' ? 'Ajouter' : 'Add'}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(dirCfg.recipients || []).length === 0 ? (
                      <p className="text-sm text-slate-400 italic">{language === 'fr' ? 'Aucun destinataire personnalisé (par défaut : admins).' : 'No custom recipient (default: admins).'}</p>
                    ) : (dirCfg.recipients || []).map((email) => (
                      <div key={email} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2" data-testid={`dir-recipient-${email}`}>
                        <span className="text-sm">{email}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeDirRecipient(email)} className="text-red-500 hover:text-red-600">
                          {language === 'fr' ? 'Retirer' : 'Remove'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button variant="outline" onClick={runDirReportNow} className="gap-2" data-testid="dir-run-now">
                    <Mail className="w-4 h-4" />
                    {language === 'fr' ? 'Envoyer le rapport maintenant (test)' : 'Send report now (test)'}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    {language === 'fr'
                      ? "Mensuel : couvre le mois précédent. Hebdomadaire : couvre les 7 derniers jours. Le bouton force l'envoi immédiat."
                      : 'Monthly: covers the previous month. Weekly: covers the last 7 days. This button forces an immediate send.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Commercial Reminder Tab */}
          <TabsContent value="business-relance">
            <Card data-testid="business-relance-config">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {language === 'fr' ? 'Relance Business Commercial' : 'Business Commercial Reminder'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Rappel automatique envoyé une fois par semaine aux commerciaux/assistantes dont la fiche hebdomadaire n'est pas encore validée."
                    : 'Automatic reminder sent once a week to commercials/assistants whose weekly report is not yet validated.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <span className="text-sm font-medium">{language === 'fr' ? 'Activé' : 'Enabled'}</span>
                    <button
                      onClick={() => saveBizReminderConfig({ enabled: !bizReminderCfg.enabled })}
                      disabled={savingBizReminderCfg}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bizReminderCfg.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      data-testid="biz-reminder-enabled-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bizReminderCfg.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Jour' : 'Day'}</label>
                    <select
                      value={bizReminderCfg.day}
                      onChange={(e) => saveBizReminderConfig({ day: parseInt(e.target.value, 10) })}
                      className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                      data-testid="biz-reminder-day-select"
                    >
                      {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((d, i) => (
                        <option key={i} value={i}>{language === 'fr' ? d : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][i]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Heure (Paris)' : 'Hour (Paris)'}</label>
                    <select
                      value={bizReminderCfg.hour}
                      onChange={(e) => saveBizReminderConfig({ hour: parseInt(e.target.value, 10) })}
                      className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                      data-testid="biz-reminder-hour-select"
                    >
                      {Array.from({ length: 22 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button variant="outline" onClick={runBizReminderNow} className="gap-2" data-testid="biz-reminder-run-now">
                    <Mail className="w-4 h-4" />
                    {language === 'fr' ? 'Envoyer les relances maintenant (test)' : 'Send reminders now (test)'}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    {language === 'fr'
                      ? "Une seule relance par semaine est envoyée le jour et l'heure configurés. Le bouton force un envoi immédiat pour les fiches non validées."
                      : 'Only one reminder per week is sent on the configured day and hour. This button forces an immediate send for unvalidated reports.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="relances-pv">
            <Card data-testid="relances-pv-config">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {language === 'fr' ? 'Relances PV de Livraison' : 'PV Delivery Reminders'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr'
                    ? "Récapitulatif hebdomadaire des PV non signés (≥ J+7), envoyé aux destinataires ci-dessous."
                    : 'Weekly recap of unsigned PVs (≥ D+7), sent to the recipients below.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable + schedule */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <span className="text-sm font-medium">{language === 'fr' ? 'Activé' : 'Enabled'}</span>
                    <button
                      onClick={() => savePvRelanceConfig({ enabled: !pvRelanceCfg.enabled })}
                      disabled={savingPvCfg}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${pvRelanceCfg.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      data-testid="pv-enabled-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${pvRelanceCfg.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Jour' : 'Day'}</label>
                    <select
                      value={pvRelanceCfg.weekday}
                      onChange={(e) => savePvRelanceConfig({ weekday: parseInt(e.target.value, 10) })}
                      className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                      data-testid="pv-weekday-select"
                    >
                      {['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'].map((d, i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <label className="text-xs text-slate-500 uppercase tracking-wide">{language === 'fr' ? 'Heure (Paris)' : 'Hour (Paris)'}</label>
                    <select
                      value={pvRelanceCfg.hour}
                      onChange={(e) => savePvRelanceConfig({ hour: parseInt(e.target.value, 10) })}
                      className="mt-1 w-full bg-transparent text-sm font-medium focus:outline-none"
                      data-testid="pv-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Recipients */}
                <div>
                  <p className="text-sm font-semibold mb-2">{language === 'fr' ? 'Destinataires' : 'Recipients'}</p>
                  <p className="text-xs text-slate-400 mb-3">
                    {language === 'fr'
                      ? "Si vide, le récap est envoyé aux administrateurs/directions configurés par défaut."
                      : 'If empty, the recap goes to the default admin/management recipients.'}
                  </p>
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="email"
                      placeholder="email@wasabi.fr"
                      value={newPvRecipient}
                      onChange={(e) => setNewPvRecipient(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addPvRecipient(); }}
                      className="max-w-sm"
                      data-testid="pv-recipient-input"
                    />
                    <Button onClick={addPvRecipient} disabled={savingPvCfg} data-testid="pv-recipient-add">
                      {language === 'fr' ? 'Ajouter' : 'Add'}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(pvRelanceCfg.recipients || []).length === 0 ? (
                      <p className="text-sm text-slate-400 italic">{language === 'fr' ? 'Aucun destinataire personnalisé (par défaut : admins).' : 'No custom recipient (default: admins).'}</p>
                    ) : (pvRelanceCfg.recipients || []).map((email) => (
                      <div key={email} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2" data-testid={`pv-recipient-${email}`}>
                        <span className="text-sm">{email}</span>
                        <Button variant="ghost" size="sm" onClick={() => removePvRecipient(email)} className="text-red-500 hover:text-red-600">
                          {language === 'fr' ? 'Retirer' : 'Remove'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Test now */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Button variant="outline" onClick={runPvRelanceNow} className="gap-2" data-testid="pv-run-now">
                    <Mail className="w-4 h-4" />
                    {language === 'fr' ? 'Envoyer le récap maintenant (test)' : 'Send recap now (test)'}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    {language === 'fr'
                      ? "Note : un destinataire ne reçoit qu'un récap par période de 7 jours (anti-doublon)."
                      : 'Note: a recipient receives at most one recap per 7-day window (anti-duplicate).'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Email Routing Tab */}
          <TabsContent value="email-routing">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-500" />
                      Routage des Emails
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Configurez qui reçoit chaque type d'email. Par défaut, tous les Admin/Super Admin reçoivent tous les emails.
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchRoutingHistory} disabled={loadingRoutingHistory} data-testid="routing-history-btn">
                    {loadingRoutingHistory ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <History className="w-4 h-4 mr-1" />}
                    Historique
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRouting ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                ) : emailRouting.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Aucune configuration</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-64 sticky left-0 bg-white z-10">Type d'email</TableHead>
                          {emailRouting[0]?.recipients?.map(r => (
                            <TableHead key={r.user_id} className="text-center min-w-32">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-xs font-semibold">{r.name}</span>
                                <span className="text-[10px] text-slate-400">{r.email}</span>
                                <Badge variant="outline" className="text-[9px]">{r.role}</Badge>
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          let lastModule = '';
                          return emailRouting.map(row => {
                            const showModule = row.module !== lastModule;
                            lastModule = row.module;
                            return (
                              <React.Fragment key={row.email_type}>
                                {showModule && (
                                  <TableRow>
                                    <TableCell colSpan={1 + (row.recipients?.length || 0)} className="bg-slate-50 py-1.5 px-4">
                                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{row.module}</span>
                                    </TableCell>
                                  </TableRow>
                                )}
                                <TableRow>
                                  <TableCell className="text-sm font-medium sticky left-0 bg-white z-10 min-w-[220px]">{row.label}</TableCell>
                                  {row.recipients?.map(r => (
                                    <TableCell key={r.user_id} className="text-center">
                                      <Switch
                                        checked={r.enabled}
                                        onCheckedChange={(checked) => toggleEmailRouting(row.email_type, r.user_id, checked)}
                                        data-testid={`route-${row.email_type}-${r.user_id}`}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </React.Fragment>
                            );
                          });
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Routing History Modal */}
            <Dialog open={showRoutingHistory} onOpenChange={setShowRoutingHistory}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-500" />
                    Historique des modifications
                  </DialogTitle>
                  <DialogDescription>Journal des changements de routage emails</DialogDescription>
                </DialogHeader>
                <div className="overflow-y-auto flex-1 pr-1">
                  {routingHistory.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">Aucune modification enregistrée</p>
                  ) : (
                    <div className="space-y-2">
                      {routingHistory.map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`routing-history-entry-${idx}`}>
                          <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${entry.new ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-700">{entry.changed_by_name}</span>
                              <span className="text-xs text-slate-400">a {entry.new ? 'activé' : 'désactivé'}</span>
                              <Badge variant={entry.new ? 'default' : 'destructive'} className="text-[10px]">
                                {entry.new ? 'ON' : 'OFF'}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              <span className="font-medium">{entry.email_type}</span> pour <span className="font-medium">{entry.target_name}</span>
                              <span className="text-slate-400"> ({entry.target_email})</span>
                            </p>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              <Clock className="w-3 h-3 inline mr-0.5" />
                              {new Date(entry.changed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Prerequis Checklist Tab */}
          {isSuperAdmin && (
            <TabsContent value="prerequis-checklist">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-amber-500" />
                    Points de vérification — Prérequis Techniques
                  </CardTitle>
                  <CardDescription>
                    Gérez les points de contrôle additionnels du formulaire Prérequis Techniques. Configurez le type de réponse : boutons radio, sélection, cases à cocher, texte libre ou nombre.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Add new item */}
                  <div className="border rounded-lg p-4 bg-slate-50 space-y-3">
                    <p className="font-medium text-sm text-slate-700 flex items-center gap-2"><Plus className="w-4 h-4" /> Ajouter un point de vérification</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Libellé *</Label>
                        <Input
                          value={newPrereqCheckItem.label}
                          onChange={e => setNewPrereqCheckItem(prev => ({ ...prev, label: e.target.value }))}
                          placeholder="Ex: Dalle béton conforme"
                          data-testid="new-prereq-check-label"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Type de réponse</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={newPrereqCheckItem.response_type}
                          onChange={e => setNewPrereqCheckItem(prev => ({ ...prev, response_type: e.target.value }))}
                          data-testid="new-prereq-check-type"
                        >
                          <option value="radio">Boutons radio (choix unique)</option>
                          <option value="select">Liste déroulante</option>
                          <option value="checkbox">Cases à cocher (choix multiple)</option>
                          <option value="yes_no">Oui / Non</option>
                          <option value="text">Texte libre</option>
                          <option value="number">Nombre</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Catégorie</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={newPrereqCheckItem.category}
                          onChange={e => setNewPrereqCheckItem(prev => ({ ...prev, category: e.target.value }))}
                          data-testid="new-prereq-check-cat"
                        >
                          {prereqCheckCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {(newPrereqCheckItem.response_type === 'radio' || newPrereqCheckItem.response_type === 'select' || newPrereqCheckItem.response_type === 'checkbox') && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Options (séparées par virgule)</Label>
                          <Input
                            value={newPrereqCheckItem.options}
                            onChange={e => setNewPrereqCheckItem(prev => ({ ...prev, options: e.target.value }))}
                            placeholder="Conforme,Non conforme,À vérifier"
                            data-testid="new-prereq-check-options"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Commentaire obligatoire si</Label>
                          <Input
                            value={newPrereqCheckItem.comment_on}
                            onChange={e => setNewPrereqCheckItem(prev => ({ ...prev, comment_on: e.target.value }))}
                            placeholder="Non conforme,À vérifier"
                            data-testid="new-prereq-check-comment-on"
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox checked={newPrereqCheckItem.required} onCheckedChange={v => setNewPrereqCheckItem(prev => ({ ...prev, required: v }))} />
                        Obligatoire
                      </label>
                      <Button onClick={handleAddPrereqCheckItem} disabled={savingPrereqCheckItem} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white gap-1" data-testid="add-prereq-check-btn">
                        {savingPrereqCheckItem ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
                      </Button>
                    </div>
                  </div>

                  {/* Category filter */}
                  {prereqCheckCategories.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-slate-500 font-medium">Filtrer :</span>
                      <Button variant={prereqCheckFilterCat === '' ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setPrereqCheckFilterCat('')}>
                        Toutes ({prereqCheckItems.length})
                      </Button>
                      {prereqCheckCategories.map(cat => {
                        const count = prereqCheckItems.filter(i => i.category === cat).length;
                        if (count === 0) return null;
                        return (
                          <Button key={cat} variant={prereqCheckFilterCat === cat ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setPrereqCheckFilterCat(cat)}>
                            {cat} ({count})
                          </Button>
                        );
                      })}
                    </div>
                  )}

                  {/* Items list */}
                  {loadingPrereqCheck ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
                  ) : prereqCheckItems.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">Aucun point configuré. Ajoutez votre premier point ci-dessus.</p>
                  ) : (
                    <div className="space-y-2">
                      {prereqCheckItems
                        .filter(i => !prereqCheckFilterCat || i.category === prereqCheckFilterCat)
                        .sort((a, b) => a.order - b.order)
                        .map(item => (
                        <div key={item.id} className={`border rounded-lg p-3 ${item.active ? 'bg-white' : 'bg-slate-100 opacity-60'}`} data-testid={`prereq-check-item-${item.id}`}>
                          {editingPrereqCheckItem?.id === item.id ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs">Libellé</Label>
                                  <Input value={editingPrereqCheckItem.label} onChange={e => setEditingPrereqCheckItem(prev => ({ ...prev, label: e.target.value }))} />
                                </div>
                                <div>
                                  <Label className="text-xs">Type de réponse</Label>
                                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editingPrereqCheckItem.response_type} onChange={e => setEditingPrereqCheckItem(prev => ({ ...prev, response_type: e.target.value }))}>
                                    <option value="radio">Boutons radio</option>
                                    <option value="select">Liste déroulante</option>
                                    <option value="checkbox">Cases à cocher</option>
                                    <option value="yes_no">Oui / Non</option>
                                    <option value="text">Texte libre</option>
                                    <option value="number">Nombre</option>
                                  </select>
                                </div>
                                <div>
                                  <Label className="text-xs">Catégorie</Label>
                                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editingPrereqCheckItem.category} onChange={e => setEditingPrereqCheckItem(prev => ({ ...prev, category: e.target.value }))}>
                                    {prereqCheckCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                  </select>
                                </div>
                              </div>
                              {(editingPrereqCheckItem.response_type === 'radio' || editingPrereqCheckItem.response_type === 'select' || editingPrereqCheckItem.response_type === 'checkbox') && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">Options</Label>
                                    <Input value={Array.isArray(editingPrereqCheckItem.options) ? editingPrereqCheckItem.options.join(', ') : editingPrereqCheckItem.options} onChange={e => setEditingPrereqCheckItem(prev => ({ ...prev, options: e.target.value }))} />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Commentaire si</Label>
                                    <Input value={Array.isArray(editingPrereqCheckItem.comment_on) ? editingPrereqCheckItem.comment_on.join(', ') : editingPrereqCheckItem.comment_on} onChange={e => setEditingPrereqCheckItem(prev => ({ ...prev, comment_on: e.target.value }))} />
                                  </div>
                                </div>
                              )}
                              <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 text-sm">
                                  <Checkbox checked={editingPrereqCheckItem.required} onCheckedChange={v => setEditingPrereqCheckItem(prev => ({ ...prev, required: v }))} />
                                  Obligatoire
                                </label>
                                <Button onClick={handleUpdatePrereqCheckItem} disabled={savingPrereqCheckItem} size="sm" className="gap-1">
                                  {savingPrereqCheckItem ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Enregistrer
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setEditingPrereqCheckItem(null)}><X className="w-3 h-3" /></Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Switch checked={item.active} onCheckedChange={() => handleTogglePrereqCheckItem(item)} />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-slate-800">{item.label}</span>
                                    <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                                    <Badge className={`text-[10px] ${
                                      item.response_type === 'radio' ? 'bg-blue-100 text-blue-700' :
                                      item.response_type === 'select' ? 'bg-purple-100 text-purple-700' :
                                      item.response_type === 'checkbox' ? 'bg-green-100 text-green-700' :
                                      item.response_type === 'yes_no' ? 'bg-amber-100 text-amber-700' :
                                      item.response_type === 'text' ? 'bg-slate-100 text-slate-700' :
                                      'bg-teal-100 text-teal-700'
                                    }`}>
                                      {item.response_type === 'radio' ? 'Radio' : item.response_type === 'select' ? 'Liste' : item.response_type === 'checkbox' ? 'Checkbox' : item.response_type === 'yes_no' ? 'Oui/Non' : item.response_type === 'text' ? 'Texte' : 'Nombre'}
                                    </Badge>
                                    {item.required && <Badge variant="destructive" className="text-[10px]">Obligatoire</Badge>}
                                  </div>
                                  {item.options && item.options.length > 0 && item.response_type !== 'text' && item.response_type !== 'number' && (
                                    <p className="text-xs text-slate-400 mt-0.5">Options : {item.options.join(', ')}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditingPrereqCheckItem({ ...item, options: Array.isArray(item.options) ? item.options.join(', ') : item.options })}>
                                  <Pencil className="w-3.5 h-3.5 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDeletePrereqCheckItem(item.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Prerequis Recipients Tab */}
          {isSuperAdmin && (
            <TabsContent value="prerequis-recipients">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HardHat className="w-5 h-5 text-amber-500" />
                    Destinataires Prérequis Techniques
                  </CardTitle>
                  <CardDescription>
                    Ces destinataires recevront les emails lors de la soumission ou validation des fiches Prérequis Techniques.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingPrerequisRecipients ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                  ) : (
                    <div className="space-y-6">
                      {/* Add new recipient */}
                      <div className="flex flex-wrap items-end gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex-1 min-w-[200px] space-y-1.5">
                          <Label>Choisir un utilisateur</Label>
                          <select className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm" value=""
                            onChange={e => { const u = prereqUserOptions.find(x => x.id === e.target.value); if (u) setNewPrerequisRecipient({ email: u.email, name: u.name || '' }); }}
                            data-testid="prerequis-recipient-user-select">
                            <option value="">— Sélectionner —</option>
                            {prereqUserOptions.filter(u => u.email && !prerequisRecipients.some(r => r.email === u.email)).map(u => <option key={u.id} value={u.id}>{u.name || u.email} — {u.email}</option>)}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[200px] space-y-1.5">
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            placeholder="email@exemple.com"
                            value={newPrerequisRecipient.email}
                            onChange={e => setNewPrerequisRecipient(prev => ({ ...prev, email: e.target.value }))}
                            data-testid="prerequis-recipient-email-input"
                          />
                        </div>
                        <div className="flex-1 space-y-1.5">
                          <Label>Nom</Label>
                          <Input
                            placeholder="Nom du destinataire"
                            value={newPrerequisRecipient.name}
                            onChange={e => setNewPrerequisRecipient(prev => ({ ...prev, name: e.target.value }))}
                            data-testid="prerequis-recipient-name-input"
                          />
                        </div>
                        <Button onClick={handleAddPrerequisRecipient} data-testid="add-prerequis-recipient-btn">
                          <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                      </div>

                      {/* Recipients list */}
                      {prerequisRecipients.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-6">Aucun destinataire configuré</p>
                      ) : (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead>Nom</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Ajouté le</TableHead>
                                <TableHead className="w-20">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {prerequisRecipients.map(r => (
                                <TableRow key={r.id} data-testid={`prerequis-recipient-row-${r.id}`}>
                                  <TableCell className="font-medium">{r.name || '—'}</TableCell>
                                  <TableCell className="text-sm text-slate-500">{r.email}</TableCell>
                                  <TableCell className="text-xs text-slate-400">
                                    {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '—'}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeletePrerequisRecipient(r.id)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`delete-prerequis-recipient-${r.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Primes Emails Tab */}
          {isSuperAdmin && (
            <TabsContent value="primes-emails">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-emerald-600" />
                        Emails d'envoi — Traitement des Primes
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Adresse(s) email de chaque commercial pour l'envoi du récapitulatif des primes du mois (Excel en pièce jointe). Séparez plusieurs adresses par une virgule.
                      </CardDescription>
                    </div>
                    <Button onClick={handleSavePrimesEmails} disabled={savingPrimesEmails} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="save-primes-emails-btn">
                      {savingPrimesEmails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Enregistrer
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingPrimesEmails ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
                  ) : primesCommerciaux.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">Aucun commercial configuré. Ajoutez-en depuis la page « Traitement des Primes » → Config.</p>
                  ) : (
                    <div className="space-y-3">
                      {primesCommerciaux.map(c => (
                        <div key={c} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50/60" data-testid={`primes-email-row-${c}`}>
                          <div className="sm:w-56 font-medium text-slate-800 flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" /> {c}
                          </div>
                          <Input
                            type="text"
                            placeholder="email1@wasabi.fr, email2@wasabi.fr"
                            value={primesEmailsMap[c] || ''}
                            onChange={e => setPrimesEmailsMap(prev => ({ ...prev, [c]: e.target.value }))}
                            className="flex-1"
                            data-testid={`primes-email-input-${c}`}
                          />
                        </div>
                      ))}
                      <p className="text-xs text-slate-400 mt-2">💡 Ces adresses sont utilisées par le bouton « Envoyer Excel » dans la fenêtre « Primes du mois ».</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}


          {/* Corner Access Control Tab */}
          {isSuperAdmin && (
            <TabsContent value="corner-access">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Send className="w-5 h-5 text-orange-500" />
                        Accès Commande Corner — Prestataires
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Sélectionnez les prestataires qui peuvent accéder au module Commande Corner. Si aucun n'est coché, tous les prestataires y ont accès.
                      </CardDescription>
                    </div>
                    <Button onClick={handleSaveCornerAccess} disabled={savingCornerAccess} data-testid="save-corner-access-btn">
                      {savingCornerAccess ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>) : (<><Save className="w-4 h-4 mr-2" /> Enregistrer</>)}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingCornerAccess ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <Send className="w-5 h-5 text-orange-600" />
                        <span className="text-sm text-orange-800">
                          <strong>{cornerAllowedIds.length}</strong> prestataire{cornerAllowedIds.length !== 1 ? 's' : ''} autorisé{cornerAllowedIds.length !== 1 ? 's' : ''}
                          {cornerAllowedIds.length === 0 && ' (tous les prestataires ont accès par défaut)'}
                        </span>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input placeholder="Rechercher un prestataire..." value={cornerAccessSearch} onChange={e => setCornerAccessSearch(e.target.value)} className="pl-10" data-testid="corner-access-search" />
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="w-12">Autorisé</TableHead>
                              <TableHead>Nom</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Organisation</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {users
                              .filter(u => u.role === 'prestataire')
                              .filter(u => {
                                if (!cornerAccessSearch) return true;
                                const q = cornerAccessSearch.toLowerCase();
                                return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                              })
                              .map(u => {
                                const isAllowed = cornerAllowedIds.includes(u.id);
                                return (
                                  <TableRow key={u.id} className={isAllowed ? 'bg-green-50/50' : ''} data-testid={`corner-access-row-${u.id}`}>
                                    <TableCell>
                                      <Switch checked={isAllowed} onCheckedChange={() => handleToggleCornerAccess(u.id)} data-testid={`corner-toggle-${u.id}`} />
                                    </TableCell>
                                    <TableCell className="font-medium">{u.name || '—'}</TableCell>
                                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                                    <TableCell>
                                      <Badge variant={isAllowed ? 'default' : 'secondary'} className="text-xs">
                                        {u.organisation_name || '—'}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {users.filter(u => u.role === 'prestataire').length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-sm text-slate-400 py-8">Aucun prestataire</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Prerequis Access Control Tab */}
          {isSuperAdmin && (
            <TabsContent value="prerequis-access">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-500" />
                        Accès au module Prérequis Techniques
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Bloquez l'accès au module Prérequis Techniques pour certains utilisateurs. Les utilisateurs bloqués ne pourront ni créer, ni consulter les fiches prérequis.
                      </CardDescription>
                    </div>
                    <Button
                      onClick={handleSavePrerequisAccess}
                      disabled={savingPrerequisAccess}
                      data-testid="save-prerequis-access-btn"
                    >
                      {savingPrerequisAccess ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Enregistrer</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingPrerequisAccess ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                  ) : (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="flex items-center gap-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <Lock className="w-5 h-5 text-orange-600" />
                        <span className="text-sm text-orange-800">
                          <strong>{blockedUserIds.length}</strong> utilisateur{blockedUserIds.length !== 1 ? 's' : ''} bloqué{blockedUserIds.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Rechercher un utilisateur..."
                          value={prerequisAccessSearch}
                          onChange={(e) => setPrerequisAccessSearch(e.target.value)}
                          className="pl-10"
                          data-testid="prerequis-access-search"
                        />
                      </div>

                      {/* Users table */}
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="w-12">Bloqué</TableHead>
                              <TableHead>Nom</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Rôle</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {users
                              .filter(u => {
                                if (!prerequisAccessSearch) return true;
                                const q = prerequisAccessSearch.toLowerCase();
                                return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
                              })
                              .filter(u => u.role !== 'super_admin')
                              .map(u => {
                                const isBlocked = blockedUserIds.includes(u.id);
                                return (
                                  <TableRow key={u.id} className={isBlocked ? 'bg-red-50/50' : ''} data-testid={`prerequis-access-row-${u.id}`}>
                                    <TableCell>
                                      <Switch
                                        checked={isBlocked}
                                        onCheckedChange={() => handleToggleBlockUser(u.id)}
                                        data-testid={`block-toggle-${u.id}`}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium">{u.name || '—'}</TableCell>
                                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                                    <TableCell>
                                      <Badge variant={isBlocked ? 'destructive' : 'secondary'} className="text-xs">
                                        {isBlocked && <Lock className="w-3 h-3 mr-1" />}
                                        {u.role}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {users.filter(u => u.role !== 'super_admin').length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-sm text-slate-400 py-8">
                                  Aucun utilisateur
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Capex Sales Access Tab (blocage d'exceptions) */}
          {isTabVisible('capex-sales-access') && (
            <TabsContent value="capex-sales-access">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-500" />
                        Accès au module Capex Sales
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Autorisation explicite : seuls les utilisateurs cochés ci-dessous ont accès à CAPEX SALES. Les administrateurs et super-administrateurs ont toujours accès.
                      </CardDescription>
                    </div>
                    <Button
                      onClick={handleSaveCsAccess}
                      disabled={savingCsAccess}
                      data-testid="save-capex-sales-access-btn"
                    >
                      {savingCsAccess ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
                      ) : (
                        <><Save className="w-4 h-4 mr-2" /> Enregistrer</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingCsAccess ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <Shield className="w-5 h-5 text-orange-600" />
                        <span className="text-sm text-orange-800">
                          <strong>{csAllowedUserIds.length}</strong> utilisateur{csAllowedUserIds.length !== 1 ? 's' : ''} autorisé{csAllowedUserIds.length !== 1 ? 's' : ''} à Capex Sales
                        </span>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Rechercher un utilisateur..."
                          value={csAccessSearch}
                          onChange={(e) => setCsAccessSearch(e.target.value)}
                          className="pl-10"
                          data-testid="capex-sales-access-search"
                        />
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="w-12">Autorisé</TableHead>
                              <TableHead>Nom</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Rôle</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {users
                              .filter(u => {
                                if (!csAccessSearch) return true;
                                const q = csAccessSearch.toLowerCase();
                                return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
                              })
                              .filter(u => u.role !== 'super_admin')
                              .map(u => {
                                const isAllowed = csAllowedUserIds.includes(u.id);
                                const locked = csAlwaysAllowedIds.includes(u.id);
                                return (
                                  <TableRow key={u.id} className={isAllowed ? 'bg-green-50/50' : ''} data-testid={`capex-sales-access-row-${u.id}`}>
                                    <TableCell>
                                      <Switch
                                        checked={isAllowed}
                                        disabled={locked}
                                        onCheckedChange={() => handleToggleCsAllowUser(u.id)}
                                        data-testid={`cs-allow-toggle-${u.id}`}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium">{u.name || '—'}</TableCell>
                                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                                    <TableCell>
                                      <Badge variant={isAllowed ? 'default' : 'secondary'} className="text-xs">
                                        {locked && <Lock className="w-3 h-3 mr-1" />}
                                        {u.role}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {users.filter(u => u.role !== 'super_admin').length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-sm text-slate-400 py-8">
                                  Aucun utilisateur
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Capex Sales — Import Opportunités (back-office) */}
          {isTabVisible('capex-sales-import') && (
            <TabsContent value="capex-sales-import">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-orange-500" />
                    Import des opportunités / contrats Capex Sales
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Importez l'export « BUSINESS CAPEX SALES » (.xlsx). Les opportunités sont rattachées aux comptes par le « Nom du compte » (enseigne + ville). Réimport idempotent : le précédent import de ce type est remplacé. Alimente l'écran Data Direction (signatures, renouvellements, opportunités créées).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <input type="file" accept=".xlsx" onChange={(e) => { setCsOppFile(e.target.files?.[0] || null); setCsOppResult(null); }} data-testid="capex-opp-file" className="text-sm" />
                      <Button onClick={() => handleImportCsOpps(true)} disabled={csOppUploading || !csOppFile} variant="outline" data-testid="capex-opp-preview-btn">
                        {csOppUploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>) : (<>Aperçu</>)}
                      </Button>
                      {csOppResult?.preview && (
                        <Button onClick={() => handleImportCsOpps(false)} disabled={csOppUploading} data-testid="capex-opp-import-btn">
                          <Upload className="w-4 h-4 mr-2" /> Confirmer l'import ({csOppResult.count})
                        </Button>
                      )}
                    </div>
                    {csOppResult?.preview && (
                      <div className="p-3 bg-slate-50 border rounded-lg space-y-2" data-testid="capex-opp-preview">
                        <div className="text-sm font-semibold text-slate-800">Aperçu : {csOppResult.count} ligne(s) · non rattachés {csOppResult.unmatched_count} · ignorés {csOppResult.skipped}</div>
                        <div className="text-xs text-slate-500">Par nature : {Object.entries(csOppResult.by_nature || {}).map(([k, v]) => `${k} : ${v}`).join(' · ') || '—'}</div>
                        <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1 pr-2">Compte</th><th className="pr-2">Opportunité</th><th className="pr-2">Nature</th><th className="pr-2">CA</th><th>Date</th></tr></thead><tbody>
                          {(csOppResult.sample || []).map((s, i) => (<tr key={i} className="border-t"><td className="py-1 pr-2">{s.account_name}</td><td className="pr-2">{s.name}</td><td className="pr-2">{s.nature}</td><td className="pr-2">{s.ca}</td><td>{s.date}</td></tr>))}
                        </tbody></table></div>
                      </div>
                    )}
                    {csOppResult && !csOppResult.preview && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2" data-testid="capex-opp-result">
                        <div className="text-sm font-semibold text-green-800">{csOppResult.imported} opportunité(s) importée(s) · total en base : {csOppResult.total_opportunities}</div>
                        <div className="text-sm text-slate-600">Par nature : {Object.entries(csOppResult.by_nature || {}).map(([k, v]) => `${k} : ${v}`).join(' · ') || '—'}</div>
                        <div className="text-sm text-slate-600">Non rattachés : <strong>{csOppResult.unmatched_count}</strong> · Lignes ignorées : {csOppResult.skipped}</div>
                      </div>
                    )}
                    <div className="text-xs text-slate-400">
                      Colonnes attendues : Propriétaire de l'opportunité, Nature du Projet, Nom du compte, Nom de l'opportunité, Date Signature Contrat, Date Prévisionnelle Ouverture Corner, CA Annuel Prévisionnel Corner, Étape…
                    </div>

                    <div className="pt-4 mt-2 border-t">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">Import des comptes Clients / Prospects</div>
                          <div className="text-xs text-slate-500">Assistant complet (mapping enregistré, détection de doublons, aperçu) selon vos trames.</div>
                        </div>
                        <a href="/capex-sales?screen=import" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600" data-testid="capex-open-account-import">
                          <Upload className="w-4 h-4" /> Ouvrir l'import des comptes
                        </a>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">Capex Sales → Paramètres → Importer des comptes.</div>
                    </div>

                    <div className="pt-4 mt-2 border-t">
                      <div className="text-sm font-semibold text-slate-800">Import des contacts</div>
                      <div className="text-xs text-slate-500 mb-2">Export « Rapport Contact Capex Sales » (.xlsx). Nettoyage auto (Mr.→M., VIP booléen, lignes Sample écartées), rattachement par « Nom du compte » exact (homonyme → priorité au client). Réimport idempotent.</div>
                      <div className="flex flex-wrap items-center gap-3">
                        <input type="file" accept=".xlsx" onChange={(e) => { setCsContactFile(e.target.files?.[0] || null); setCsContactResult(null); }} data-testid="capex-contact-file" className="text-sm" />
                        <Button onClick={() => handleImportCsContacts(true)} disabled={csContactUploading || !csContactFile} variant="outline" data-testid="capex-contact-preview-btn">
                          {csContactUploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>) : (<>Aperçu</>)}
                        </Button>
                        {csContactResult?.preview && (
                          <Button onClick={() => handleImportCsContacts(false)} disabled={csContactUploading} data-testid="capex-contact-import-btn">
                            <Upload className="w-4 h-4 mr-2" /> Confirmer l'import ({csContactResult.count})
                          </Button>
                        )}
                      </div>
                      {csContactResult?.preview && (
                        <div className="mt-2 p-3 bg-slate-50 border rounded-lg" data-testid="capex-contact-preview">
                          <div className="text-sm font-semibold text-slate-800 mb-1">Aperçu : {csContactResult.count} contact(s) · VIP {csContactResult.vip} · non rattachés {csContactResult.unmatched_count} · ignorés {csContactResult.skipped}</div>
                          <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1 pr-2">Civ.</th><th className="pr-2">Prénom</th><th className="pr-2">Nom</th><th className="pr-2">Poste</th><th className="pr-2">Email</th><th>VIP</th></tr></thead><tbody>
                            {(csContactResult.sample || []).map((s, i) => (<tr key={i} className="border-t"><td className="py-1 pr-2">{s.civilite || ''}</td><td className="pr-2">{s.prenom || ''}</td><td className="pr-2">{s.nom || ''}</td><td className="pr-2">{s.poste || ''}</td><td className="pr-2">{s.email || ''}</td><td>{s.vip ? 'Oui' : ''}</td></tr>))}
                          </tbody></table></div>
                        </div>
                      )}
                      {csContactResult && !csContactResult.preview && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-slate-700" data-testid="capex-contact-result">
                          <strong className="text-green-800">{csContactResult.imported}</strong> contact(s) importé(s) · total : {csContactResult.total_contacts} · VIP : {csContactResult.vip} · non rattachés : {csContactResult.unmatched_count} · ignorés : {csContactResult.skipped}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 mt-2 border-t">
                      <div className="text-sm font-semibold text-slate-800">Import des comptes rendus</div>
                      <div className="text-xs text-slate-500 mb-2">Export « CR ALL CAPEX SALES » (.xlsx). Colonnes : « Compte Rendu : Nom de Compte Rendu » (= Enseigne + Ville), Origine, Objet, Résultat, Description. Rattachement au compte par Nom exact (homonyme → priorité au client). Réimport idempotent.</div>
                      <div className="flex flex-wrap items-center gap-3">
                        <input type="file" accept=".xlsx" onChange={(e) => { setCsCrFile(e.target.files?.[0] || null); setCsCrResult(null); }} data-testid="capex-cr-file" className="text-sm" />
                        <Button onClick={() => handleImportCsCr(true)} disabled={csCrUploading || !csCrFile} variant="outline" data-testid="capex-cr-preview-btn">
                          {csCrUploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>) : (<>Aperçu</>)}
                        </Button>
                        {csCrResult?.preview && (
                          <Button onClick={() => handleImportCsCr(false)} disabled={csCrUploading} data-testid="capex-cr-import-btn">
                            <Upload className="w-4 h-4 mr-2" /> Confirmer l'import ({csCrResult.count})
                          </Button>
                        )}
                      </div>
                      {csCrResult?.preview && (
                        <div className="mt-2 p-3 bg-slate-50 border rounded-lg" data-testid="capex-cr-preview">
                          <div className="text-sm font-semibold text-slate-800 mb-1">Aperçu : {csCrResult.count} compte(s) rendu(s) · non rattachés {csCrResult.unmatched_count} · ignorés {csCrResult.skipped}</div>
                          <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1 pr-2">Compte</th><th className="pr-2">Objet</th><th className="pr-2">Résultat</th><th className="pr-2">Origine</th><th>Description</th></tr></thead><tbody>
                            {(csCrResult.sample || []).map((s, i) => (<tr key={i} className="border-t"><td className="py-1 pr-2">{s.account_name || ''}</td><td className="pr-2">{s.objet || ''}</td><td className="pr-2">{s.resultat || ''}</td><td className="pr-2">{s.origine || ''}</td><td className="text-slate-500">{s.description || ''}</td></tr>))}
                          </tbody></table></div>
                          {csCrResult.unmatched_count > 0 && (
                            <div className="text-[11px] text-amber-600 mt-2">Non rattachés (compte absent) : {(csCrResult.unmatched_samples || []).slice(0, 10).join(', ')}{csCrResult.unmatched_count > 10 ? '…' : ''}</div>
                          )}
                        </div>
                      )}
                      {csCrResult && !csCrResult.preview && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-slate-700" data-testid="capex-cr-result">
                          <strong className="text-green-800">{csCrResult.imported}</strong> compte(s) rendu(s) importé(s) · total : {csCrResult.total_comptes_rendus} · non rattachés : {csCrResult.unmatched_count} · ignorés : {csCrResult.skipped}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 mt-2 border-t">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-800">Dédoublonnage des comptes</div>
                          <div className="text-xs text-slate-500">Fusionne les prospects homonymes en une fiche (clients conservés). Sauvegarde automatique avant suppression.</div>
                        </div>
                        <Button variant="outline" onClick={handleDedupAccounts} disabled={csDedupRunning} data-testid="capex-dedup-btn">
                          {csDedupRunning ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Traitement...</>) : (<><Users className="w-4 h-4 mr-2" /> Lancer le dédoublonnage</>)}
                        </Button>
                      </div>
                      {csDedupResult && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-slate-700" data-testid="capex-dedup-result">
                          {csDedupResult.message ? csDedupResult.message : (<><strong>{csDedupResult.deleted}</strong> doublon(s) supprimé(s) sur {csDedupResult.groups} groupe(s) · homonymes restants : {csDedupResult.remaining_homonyms} · comptes : {csDedupResult.total_accounts}</>)}
                        </div>
                      )}
                    </div>

                    <div className="pt-4 mt-2 border-t">
                      <div className="text-sm font-semibold text-slate-800 mb-2">Historique des imports</div>
                      {csImportLog.length === 0 ? (
                        <div className="text-xs text-slate-400">Aucun import enregistré pour le moment.</div>
                      ) : (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Fichier</TableHead>
                                <TableHead>Par</TableHead>
                                <TableHead className="text-right">Créés</TableHead>
                                <TableHead className="text-right">MàJ</TableHead>
                                <TableHead className="text-right">Ignorés</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {csImportLog.map((l) => (
                                <TableRow key={l.id} data-testid={`capex-import-log-${l.id}`}>
                                  <TableCell className="text-xs">{l.at ? new Date(l.at).toLocaleString('fr-FR') : '—'}</TableCell>
                                  <TableCell><Badge variant="secondary" className="text-xs">{l.mode === 'opportunites' ? 'Opportunités' : l.mode === 'contacts' ? 'Contacts' : l.mode === 'comptes_rendus' ? 'Comptes Rendus' : l.mode === 'dedoublonnage' ? 'Dédoublonnage' : 'Comptes'}</Badge></TableCell>
                                  <TableCell className="text-xs truncate max-w-[180px]">{l.filename || '—'}</TableCell>
                                  <TableCell className="text-xs">{l.user_name || '—'}</TableCell>
                                  <TableCell className="text-right text-xs">{l.created ?? 0}</TableCell>
                                  <TableCell className="text-right text-xs">{l.updated ?? 0}</TableCell>
                                  <TableCell className="text-right text-xs">{l.skipped ?? 0}</TableCell>
                                  <TableCell className="text-right text-xs font-semibold">{l.total ?? 0}</TableCell>
                                  <TableCell className="text-right">
                                    {l.rolled_back ? (
                                      <Badge variant="outline" className="text-[10px]">Annulé</Badge>
                                    ) : (['opportunites', 'contacts', 'comptes_rendus', 'dedoublonnage'].includes(l.mode) ? (
                                      <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => handleRollbackImport(l)} data-testid={`capex-rollback-${l.id}`}>Annuler</Button>
                                    ) : <span className="text-[10px] text-slate-300">—</span>)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Planning Chantiers Tab */}
          <TabsContent value="planning-chantiers">
            <PlanningChantiersAdminTab token={token} language={language} />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
